/**
 * Frameworks API Service
 * Base: /api/v1/
 *
 * All functions return typed promises. Replace BASE_URL with your actual
 * API origin in production (e.g. import.meta.env.VITE_API_BASE_URL).
 */

import { useAuthStore } from "../stores/authStore";
import axios from "axios";
import { API_URL } from "../constants/index";
import { useToastStore } from "../stores/toastStore";
import { extractApiErrors } from "./errorMap";

declare global {
  interface Window {
    BE_URL?: string;
  }
}

// Session-expired de-bounce - prevent multiple alerts
let _sessionExpiredAlertShown = false;
let _sessionExpirationPromise: Promise<void> | null = null;

/** Handle session expiration with debounce to show only one alert */
function handleSessionExpiration() {
  if (_sessionExpiredAlertShown) return;

  // Prevent race condition: set flag immediately
  _sessionExpiredAlertShown = true;

  // Use existing promise if one is in progress
  if (_sessionExpirationPromise) return;

  _sessionExpirationPromise = Promise.resolve().then(() => {
    sessionStorage.setItem("_sessionExpiredFired", "1");
    sessionStorage.setItem("logoutReason", "Your session has expired. Please login again.");

    useToastStore.getState().showError(
      "Session Ended",
      "Your session has expired. Please login again."
    );

    useAuthStore.getState().logout();
    window.dispatchEvent(new CustomEvent("user-logout", { detail: { reason: "Session expired" } }));
  });
}

window.addEventListener("user-logout", () => {
  _sessionExpiredAlertShown = false;
  _sessionExpirationPromise = null;
  sessionStorage.removeItem("_sessionExpiredFired");
});

const BASE_URL = (window.BE_URL || "") + "/api/v1";

/**
 * Turn a raw, non-JSON HTTP error body into a short, user-friendly message.
 * Strips bulky HTML error pages (e.g. nginx's "413 Request Entity Too Large")
 * and maps common status codes to brief, relevant text.
 */
export function briefHttpError(
  status: number,
  body?: string,
  statusText?: string,
): string {
  const raw = (body ?? "").trim();
  const isHtml = /<html|<!doctype|<head|<body|<center/i.test(raw);
  switch (status) {
    case 413:
      return "The file is too large to upload. Please choose a smaller file.";
    case 408:
      return "The request timed out. Please try again.";
    case 429:
      return "Too many requests. Please wait a moment and try again.";
    case 500:
      return "Something went wrong on the server. Please try again.";
    case 502:
    case 503:
    case 504:
      return "The server is temporarily unavailable. Please try again.";
  }
  // Non-HTML, reasonably short bodies are usually meaningful — show as-is.
  if (raw && !isHtml && raw.length <= 300) return raw;
  return `Request failed (${status}${statusText ? ` ${statusText}` : ""}).`;
}

/** Build auth headers from the current token */
export function getAuthHeaders(): Record<string, string> {
  const token = useAuthStore.getState().getToken();
  return token ? { Authorization: `Bearer ${token}` } : {};
}

/** Generic fetch wrapper with JSON parsing and error throwing */
async function apiFetch<T>(path: string, options?: RequestInit): Promise<T> {
  const { headers: optHeaders, ...rest } = options ?? {};

  // Merge headers: default Content-Type → auth → caller overrides
  // Callers can pass `headers: {}` to drop Content-Type (e.g. FormData uploads)
  const defaultHeaders: Record<string, string> =
    optHeaders && Object.keys(optHeaders).length === 0
      ? {} // caller explicitly cleared headers (FormData — browser sets Content-Type)
      : { "Content-Type": "application/json" };

  const mergedHeaders: Record<string, string> = {
    ...defaultHeaders,
    ...getAuthHeaders(),
    ...(optHeaders as Record<string, string>),
  };

  const res = await fetch(`${BASE_URL}${path}`, {
    headers: mergedHeaders,
    ...rest,
  });

  // Handle 401 Unauthorized
  if (res.status === 401) {
    handleSessionExpiration();
    const err = new Error("Session expired. Please login again.");
    err.name = "Session Expired";
    throw err;
  }

  if (!res.ok) {
    const body = await res.text().catch(() => "");
    let title = "Request Failed";
    // Default to a brief, sanitized message (handles HTML pages like nginx 413).
    let detail = briefHttpError(res.status, body, res.statusText);
    let errorCode: string | undefined;
    try {
      const parsed = JSON.parse(body);
      const firstError = parsed?.errors?.[0];
      if (firstError) {
        title = firstError.title || title;
        errorCode = firstError.code;
        const raw = firstError.detail || firstError.message || detail;
        // Keep only the first sentence for a clean, short message
        detail = raw.split(/[.(]/)[0].trim();
      }
    } catch {
      // body is not JSON — keep the brief message from briefHttpError above
    }
    const err = new Error(detail);
    err.name = title;
    (err as any).errorCode = errorCode;
    throw err;
  }
  // 204 No Content — nothing to parse
  if (res.status === 204 || res.headers.get("content-length") === "0") {
    return undefined as T;
  }
  return res.json() as Promise<T>;
}

/* ─────────────────────────────────────────────────────────
   Types
───────────────────────────────────────────────────────── */

/** Envelope returned by the backend for every response */
export interface ApiEnvelope<T> {
  status: string;
  apiVersion: string;
  requestId: string;
  timestamp: string;
  data: T;
  pagination: { page: number; pageSize: number; total: number } | null;
  errors: { code: string; message: string }[] | null;
}

export interface FrameworkCatalog {
  id: string;
  name: string;
  version: string;
  source: string;
  controlCount: number;
}

/* ── Executive / application insights (dashboard) ── */
export interface InsightDetail {
  id: string;
  label: string;
  metric: string;
  previousMetric?: string;
  status?: string;
}
export interface InsightItem {
  title: string;
  subtitle: string;
  metric: string;
  previousMetric?: string;
  priority?: string;
  category?: string;
  businessImpact?: string;
  recommendedAction?: string;
  details: InsightDetail[];
}
export interface InsightSection {
  type: "worse" | "improved" | "stable" | "worsePoams" | "inactiveUsers";
  label: string;
  count: number;
  items: InsightItem[];
}
export interface TopPoamItem {
  poamItemId: string;
  title: string;
  severity?: string;
  status?: string;
  progressPercent?: number;
  scheduledCompletionDate?: string;
  remediationPlanTitle?: string;
  assignees?: Array<string | { id?: string; name?: string; displayName?: string }>;
  applicationName?: string;
  /* Application/source name from the API (used for the Application column). */
  sspSourceName?: string;
  /* Name of the System Security Plan this POA&M item belongs to. */
  sspName?: string;
}
export interface ApplicationInsightsData {
  generatedBy?: string;
  worse: InsightSection;
  improved: InsightSection;
  stable: InsightSection;
  topPoamItems?: TopPoamItem[];
  /* POAMs whose posture regressed this period (InsightSection — its items are
     the "sections", each with a title/metric and detail rows). */
  worsePoams?: InsightSection;
  /* Users with no recent activity (InsightSection — detail rows are the users:
     label = name, metric = activity, e.g. "Never logged in" / "65 days"). */
  inactiveUsers?: InsightSection;
  /* Headline metrics surfaced in "Trends At A Glance". complianceScore may be
     a percent (92) or a fraction (0.92); criticalPoamCount is a whole number. */
  complianceScore?: number;
  criticalPoamCount?: number;
}

/** GET /dashboard/insights/applications — executive "what changed" insights. */
/** Org/group filter for the insights endpoints. Group and org are mutually
 *  exclusive; each selected id becomes a repeated query param
 *  (e.g. ?orgId=a&orgId=b or ?groupId=a&groupId=b). */
export interface InsightsFilter {
  groupIds?: string[];
  orgIds?: string[];
}
function insightsFilterQuery(filter?: InsightsFilter): string {
  const params = new URLSearchParams();
  (filter?.groupIds ?? []).forEach((id) => id && params.append("groupId", id));
  (filter?.orgIds ?? []).forEach((id) => id && params.append("orgId", id));
  const qs = params.toString();
  return qs ? `?${qs}` : "";
}

export function fetchApplicationInsights(filter?: InsightsFilter): Promise<ApiEnvelope<ApplicationInsightsData>> {
  return apiFetch<ApiEnvelope<ApplicationInsightsData>>(
    `/dashboard/insights/applications${insightsFilterQuery(filter)}`,
  );
}

/** Nested counts backing the grouped "Key Metrics Overview" sections. Every
 *  group and field is optional so partial/placeholder responses render cleanly
 *  (only the fields actually present are shown as cards). */
/** A single metric field within a group — value plus its own copy text. */
export interface InsightsCountField {
  value: number;
  description?: string;
  tooltip?: string;
}
/** A group holds a title/description plus any number of metric fields. */
export interface InsightsCountGroup {
  title?: string;
  description?: string;
  /** Metric fields keyed by their raw name (anything other than title/description). */
  [field: string]: InsightsCountField | string | undefined;
}
/** Groups keyed by their raw name (compliancePosture, riskAndRemediation, …). */
export type InsightsCountsResponse = Record<string, InsightsCountGroup | undefined>;

/** GET /dashboard/insights/counts — grouped aggregate metric counts. */
export function fetchInsightsCounts(filter?: InsightsFilter): Promise<ApiEnvelope<InsightsCountsResponse>> {
  return apiFetch<ApiEnvelope<InsightsCountsResponse>>(
    `/dashboard/insights/counts${insightsFilterQuery(filter)}`,
  );
}

/* ── Catalog CRUD types (matching backend API) ── */

export interface CatalogResponse {
  id: string;
  documentId: string;
  title?: string;
  owningOrgUnitId?: string | null;
  catalogType?: CatalogType;
  countryName?: string | null;
  countryCode?: string | null;
  status?: "DRAFT" | "ACTIVE" | "DEPRECATED";
  lifecycleStatus?: string;
  version?: string;
  oscalVersion?: string;
  versionNumber?: number;
  createdAt: string;
  updatedAt: string;
  groups?: GroupResponse[];
  controls?: ControlResponse[];
  groupCount?: number;
  controlCount?: number;
}

export interface GroupResponse {
  id: string;
  groupId: string;
  title: string;
  clazz: string;
  sortOrder: number;
  groups: GroupResponse[];
  controls: ControlResponse[];
}

export interface PartPayload {
  partId: string;
  name: string;
  ns?: string;
  clazz?: string;
  title?: string | null;
  prose: string;
  sortOrder: number;
  parts: PartPayload[];
}

export interface PartResponse extends PartPayload {
  id: string;
}

export interface ConstraintTest {
  expression: string | null;
  remarks: string | null;
}

export interface ParameterConstraint {
  description: string | null;
  tests: ConstraintTest[] | null;
}

export interface ParameterGuideline {
  prose: string;
}

export interface ParameterSelection {
  "how-many": "one" | "one-or-more";
  choice: string[];
}

export interface ParamPayload {
  paramId: string;
  label: string;
  usage?: string;
  clazz?: string | null;
  constraints?: ParameterConstraint[];
  guidelines?: ParameterGuideline[];
  values?: string[];
  selectConfig?: ParameterSelection | null;
  sortOrder: number;
}

export interface ParamResponse extends ParamPayload {
  id: string;
}

export interface ControlResponse {
  id: string;
  controlId: string;
  title: string;
  clazz: string;
  sortOrder: number;
  parts: PartResponse[];
  params: ParamResponse[];
  enhancements: ControlResponse[];
}

export type CatalogType = "REGULATORY" | "TECHNOLOGY" | "INTEGRATION";

export interface CreateCatalogPayload {
  title: string;
  owningOrgUnitId?: string | null;
  catalogType?: CatalogType;
  countryName?: string | null;
  countryCode?: string | null;
}

export interface CreateGroupPayload {
  groupId: string;
  title: string;
  clazz?: string;
  sortOrder: number;
}

export interface CreateControlPayload {
  controlId: string;
  title: string;
  clazz?: string | null;
  groupId?: string | null;
  parentControlId?: string | null;
  sortOrder: number;
  parts?: (PartPayload & { id?: string })[] | null;
  params?: (ParamPayload & { id?: string })[] | null;
}

export interface MappingPair {
  sourceControlId: string;
  targetControlId: string;
  relationship: "equivalent" | "subset" | "superset" | "related" | "none";
  rationale?: string;
}

export interface ControlMapping {
  id: string;
  title: string;
  sourceCatalogId: string;
  sourceCatalogName: string;
  targetCatalogId: string;
  targetCatalogName: string;
  pairCount: number;
  status: "active" | "draft" | "deprecated";
  createdAt: string;
  updatedAt: string;
  pairs?: MappingPair[];
}

export interface CreateMappingPayload {
  title: string;
  sourceCatalogId: string;
  targetCatalogId: string;
}

export interface ImportMappingPayload {
  oscalJson: string; // raw OSCAL JSON string
  format: "json" | "xml";
  overwrite?: boolean;
}

export interface ImportResult {
  mappingId: string;
  pairsImported: number;
  validationWarnings: string[];
}

/* ── License Management ── */

export interface LicenseInfo {
  userName: string;
  startDate: string;
  expiryDate: string;
  sspLimit: number;
}

export interface LicenseDetails {
  userName: string;
  licenseKey: string;
  startDate: string;
  expiryDate: string;
  sspLimit: number;
  currentSspCount: number;
  remainingSspQuota: number;
  status: string;
  activatedAt: string;
}

export interface LicenseValidationResponse {
  valid: boolean;
  expired: boolean;
  notYetActive: boolean;
  message: string;
  license: LicenseInfo;
  currentSspCount: number;
}

/* ─────────────────────────────────────────────────────────
   Catalog endpoints
───────────────────────────────────────────────────────── */

/** List all available framework catalogs */
export function listCatalogs(): Promise<ApiEnvelope<CatalogResponse[]>> {
  return apiFetch<ApiEnvelope<CatalogResponse[]>>("/catalogs");
}

/** Get a single catalog by ID (full tree: groups → controls → parts + params) */
export function getCatalog(id: string): Promise<ApiEnvelope<CatalogResponse>> {
  return apiFetch<ApiEnvelope<CatalogResponse>>(`/catalogs/${id}`);
}

/** Update a catalog's title and/or type — PUT /catalogs/{id} */
export function updateCatalog(
  id: string,
  payload: {
    title?: string;
    catalogType?: CatalogType;
    countryName?: string | null;
    countryCode?: string | null;
  },
): Promise<ApiEnvelope<CatalogResponse>> {
  return apiFetch<ApiEnvelope<CatalogResponse>>(`/catalogs/${id}`, {
    method: "PUT",
    body: JSON.stringify(payload),
  });
}

/** Create a new catalog shell */
export function createCatalog(
  payload: CreateCatalogPayload,
): Promise<ApiEnvelope<CatalogResponse>> {
  return apiFetch<ApiEnvelope<CatalogResponse>>("/catalogs", {
    method: "POST",
    body: JSON.stringify(payload),
  });
}

/** Delete a catalog */
export function deleteCatalog(id: string, reason?: string): Promise<void> {
  const qs = reason ? `?reason=${encodeURIComponent(reason)}` : "";
  return apiFetch<void>(`/catalogs/${id}${qs}`, { method: "DELETE" });
}

/* ── Catalog Export ── */

export interface ExportJobResponse {
  jobId: string;
  status: "pending" | "completed" | "failed";
  documentId: string;
  format: string;
  downloadUrl: string | null;
  startedAt: string;
  completedAt: string | null;
}

/** Start a catalog export job (uses documentId, not catalogId) */
export function exportCatalogDoc(
  documentId: string,
  format: "json" | "xml" | "yaml" = "json",
): Promise<ApiEnvelope<ExportJobResponse>> {
  return apiFetch<ApiEnvelope<ExportJobResponse>>(`/export/${documentId}`, {
    method: "POST",
    body: JSON.stringify({ format }),
  });
}

/** Poll an export job for status */
export function getExportJob(
  jobId: string,
): Promise<ApiEnvelope<ExportJobResponse>> {
  return apiFetch<ApiEnvelope<ExportJobResponse>>(`/export/${jobId}/download`);
}

/** Build full download URL from a jobId */
export function getExportDownloadUrl(jobId: string): string {
  return `${BASE_URL}/export/${jobId}/download/file`;
}

/* ── Group endpoints ── */

/** List groups in a catalog */
export function listGroups(
  catalogId: string,
): Promise<ApiEnvelope<GroupResponse[]>> {
  return apiFetch<ApiEnvelope<GroupResponse[]>>(
    `/catalogs/${catalogId}/groups`,
  );
}

/** Create a group in a catalog */
export function createGroup(
  catalogId: string,
  payload: CreateGroupPayload,
): Promise<ApiEnvelope<GroupResponse>> {
  return apiFetch<ApiEnvelope<GroupResponse>>(`/catalogs/${catalogId}/groups`, {
    method: "POST",
    body: JSON.stringify(payload),
  });
}

/** Delete a group from a catalog */
export function deleteGroup(catalogId: string, groupId: string): Promise<void> {
  return apiFetch<void>(`/catalogs/${catalogId}/groups/${groupId}`, {
    method: "DELETE",
  });
}

/* ── Control endpoints ── */

/** List controls in a catalog (flat or filtered by group) */
export function listControls(
  catalogId: string,
  params?: { flat?: boolean; group_id?: string },
): Promise<ApiEnvelope<ControlResponse[]>> {
  const qs = new URLSearchParams();
  if (params?.flat) qs.set("flat", "true");
  if (params?.group_id) qs.set("group_id", params.group_id);
  const query = qs.toString();
  return apiFetch<ApiEnvelope<ControlResponse[]>>(
    `/catalogs/${catalogId}/controls${query ? `?${query}` : ""}`,
  );
}

/** Get a specific control by controlId (e.g. "ac-1") */
export function getControl(
  catalogId: string,
  controlId: string,
): Promise<ApiEnvelope<ControlResponse>> {
  return apiFetch<ApiEnvelope<ControlResponse>>(
    `/catalogs/${catalogId}/controls/${controlId}`,
  );
}

/** Create a control in a catalog */
export function createControl(
  catalogId: string,
  payload: CreateControlPayload,
): Promise<ApiEnvelope<ControlResponse>> {
  return apiFetch<ApiEnvelope<ControlResponse>>(
    `/catalogs/${catalogId}/controls`,
    {
      method: "POST",
      body: JSON.stringify(payload),
    },
  );
}

/** Delete a control from a catalog */
export function deleteControlApi(
  catalogId: string,
  controlId: string,
): Promise<void> {
  return apiFetch<void>(`/catalogs/${catalogId}/controls/${controlId}`, {
    method: "DELETE",
  });
}

/** Response from POST /import */
export interface ImportJobResponse {
  id: string;
  jobStatus: string;
  importMode: string;
  documentType: string;
  sourceFilename?: string;
  groupCount: string;
  startedAt: string;
  createdAt: string;
}

/** Validate an OSCAL file before importing — POST /import/validate */
export interface ValidateResponse {
  documentType: string;
  oscalVersion: string;
  valid?: boolean;
  message?: string;
  findings: {
    code?: string;
    severity: string;
    message: string;
    title?: string;
    detail?: string;
  }[];
}

export function validateImport(
  file: File,
  catalogType?: CatalogType,
): Promise<ApiEnvelope<ValidateResponse>> {
  const formData = new FormData();
  formData.append("file", file);
  if (catalogType) formData.append("catalogType", catalogType);
  return fetch(`${BASE_URL}/import/validate`, {
    method: "POST",
    headers: { Accept: "*/*", ...getAuthHeaders() },
    body: formData,
  }).then(async (res) => {
    const json = await res.json();
    if (!res.ok || json.status === "error") {
      const errMsg =
        json.errors?.[0]?.detail ??
        json.errors?.[0]?.title ??
        "Validation failed";
      throw new Error(errMsg);
    }
    return json as ApiEnvelope<ValidateResponse>;
  });
}

/** Validate an OSCAL document from a URL — POST /import/validate */
export function validateImportUrl(
  url: string,
): Promise<ApiEnvelope<ValidateResponse>> {
  const formData = new FormData();
  formData.append("url", url);
  return fetch(`${BASE_URL}/import/validate`, {
    method: "POST",
    headers: { Accept: "*/*", ...getAuthHeaders() },
    body: formData,
  }).then(async (res) => {
    const json = await res.json();
    if (!res.ok || json.status === "error") {
      const errMsg =
        json.errors?.[0]?.detail ??
        json.errors?.[0]?.title ??
        "Validation failed";
      throw new Error(errMsg);
    }
    return json as ApiEnvelope<ValidateResponse>;
  });
}

/** Import an OSCAL catalog JSON file — POST /import?mode={mode}&auto_migrate=true */
export function importCatalog(
  file: File,
  mode: "full" | "delta" = "full",
  owningOrgUnitId?: string,
): Promise<ApiEnvelope<ImportJobResponse>> {
  const formData = new FormData();
  formData.append("file", file);
  let url = `${BASE_URL}/import?resource=catalog&mode=${mode}&auto_migrate=true`;
  if (owningOrgUnitId) {
    url += `&owning_org_unit_id=${owningOrgUnitId}`;
  }
  return fetch(url, {
    method: "POST",
    headers: { Accept: "*/*", ...getAuthHeaders() },
    body: formData,
  }).then(async (res) => {
    if (!res.ok) {
      const body = await res.text().catch(() => "");
      let title = "Import Failed";
      let detail = res.statusText;
      try {
        const parsed = JSON.parse(body);
        const src = parsed?.errors?.[0] ?? parsed;
        if (src?.title) title = src.title;
        if (src?.detail || src?.message) detail = src.detail || src.message;
      } catch {
        if (body) detail = body;
      }
      const err = new Error(detail);
      err.name = title;
      throw err;
    }
    return res.json() as Promise<ApiEnvelope<ImportJobResponse>>;
  });
}

/** Session detail from GET /import/sessions/{id} */
export interface ImportSessionResponse {
  id: string;
  jobStatus: string;
  importMode: string;
  documentType: string;
  sourceFilename: string;
  sourceContentHash?: string;
  sourceOscalVersion?: string;
  initiatedBy?: string;
  startedAt: string;
  findings: {
    id: string;
    severity: string;
    code: string;
    title: string;
    detail: string;
    autoRepairable: boolean;
    repaired: boolean;
    sortOrder: number;
    createdAt: string;
  }[];
  references: unknown[];
  createdAt: string;
  updatedAt: string;
}

/** Get import session details — GET /import/sessions/{id} */
export function getImportSession(
  sessionId: string,
): Promise<ApiEnvelope<ImportSessionResponse>> {
  return apiFetch<ApiEnvelope<ImportSessionResponse>>(
    `/import/sessions/${sessionId}`,
  );
}

/** Abort an import session — POST /import/sessions/{id}/abort */
export function abortImportSession(
  sessionId: string,
): Promise<ApiEnvelope<ImportSessionResponse>> {
  return apiFetch<ApiEnvelope<ImportSessionResponse>>(
    `/import/sessions/${sessionId}/abort`,
    {
      method: "POST",
    },
  );
}

/** Commit an import session — POST /import/sessions/{id}/commit */
export function commitImportSession(
  sessionId: string,
  owningOrgUnitId?: string | null,
  catalogType?: CatalogType,
): Promise<ApiEnvelope<ImportJobResponse>> {
  const payload: { owningOrgUnitId?: string; catalogType?: CatalogType } = {};
  if (owningOrgUnitId) payload.owningOrgUnitId = owningOrgUnitId;
  if (catalogType) payload.catalogType = catalogType;
  return apiFetch<ApiEnvelope<ImportJobResponse>>(
    `/import/sessions/${sessionId}/commit`,
    {
      method: "POST",
      body: Object.keys(payload).length
        ? JSON.stringify(payload)
        : undefined,
    },
  );
}

/* ── Control Mapping Import ──
   Flow: validate the file (POST /import/validate) — findings tell you which
   catalog side(s) the file couldn't resolve. Pick catalogs only for the
   missing side(s) and re-validate. When clean, stage (POST /import,
   resource=control_mapping) which returns an async session; poll it until
   jobStatus=validated, then commit to actually create the mapping. */

/** Validate a mapping file. Pass catalog id(s) only for the side(s) the file
 *  couldn't resolve on a prior validate. POST /import/validate[?..._catalog_id]. */
export function validateMappingImport(
  file: File,
  opts?: { sourceCatalogId?: string; targetCatalogId?: string },
): Promise<ApiEnvelope<ValidateResponse>> {
  const formData = new FormData();
  formData.append("file", file);
  const params = new URLSearchParams();
  if (opts?.sourceCatalogId)
    params.set("source_catalog_id", opts.sourceCatalogId);
  if (opts?.targetCatalogId)
    params.set("target_catalog_id", opts.targetCatalogId);
  const qs = params.toString();
  return fetch(`${BASE_URL}/import/validate${qs ? `?${qs}` : ""}`, {
    method: "POST",
    headers: { Accept: "*/*", ...getAuthHeaders() },
    body: formData,
  }).then(async (res) => {
    const json = await res.json();
    if (!res.ok || json.status === "error") {
      const errMsg =
        json.error?.detail ??
        json.errors?.[0]?.detail ??
        json.errors?.[0]?.title ??
        "Validation failed";
      throw new Error(errMsg);
    }
    return json as ApiEnvelope<ValidateResponse>;
  });
}

/** Session summary returned by staging (202, jobStatus becomes validated async). */
export interface MappingImportStageResponse {
  id: string;
  jobStatus: string;
  documentType: string;
  sourceFilename: string;
}

/** Stage a control-mapping import (creates the session). POST /import.
 *  Send catalog id(s) only for the side(s) picked during validation. */
export function stageMappingImport(
  file: File,
  opts: {
    owningOrgUnitId?: string;
    mode?: "full" | "delta";
    sourceCatalogId?: string;
    targetCatalogId?: string;
  },
): Promise<ApiEnvelope<MappingImportStageResponse>> {
  const formData = new FormData();
  formData.append("file", file);
  formData.append("resource", "control_mapping");
  formData.append("mode", opts.mode ?? "full");
  if (opts.owningOrgUnitId)
    formData.append("owning_org_unit_id", opts.owningOrgUnitId);
  if (opts.sourceCatalogId)
    formData.append("source_catalog_id", opts.sourceCatalogId);
  if (opts.targetCatalogId)
    formData.append("target_catalog_id", opts.targetCatalogId);
  return fetch(`${BASE_URL}/import`, {
    method: "POST",
    headers: { Accept: "*/*", ...getAuthHeaders() },
    body: formData,
  }).then(async (res) => {
    if (!res.ok) {
      const body = await res.text().catch(() => "");
      let title = "Import Failed";
      let detail = res.statusText;
      try {
        const parsed = JSON.parse(body);
        const src = parsed?.error ?? parsed?.errors?.[0] ?? parsed;
        if (src?.title) title = src.title;
        if (src?.detail || src?.message) detail = src.detail || src.message;
      } catch {
        if (body) detail = body;
      }
      const err = new Error(detail);
      err.name = title;
      throw err;
    }
    return res.json() as Promise<ApiEnvelope<MappingImportStageResponse>>;
  });
}

/** Commit a staged mapping import session — creates the mapping.
 *  POST /import/sessions/{id}/commit. Include catalog id(s) only if you picked
 *  them (i.e. the file couldn't resolve that side). */
export interface MappingCommitResponse {
  id: string;
  jobStatus: string;
  targetDocumentId?: string;
  documentType: string;
}

export function commitMappingImportSession(
  sessionId: string,
  payload: {
    owningOrgUnitId?: string | null;
    sourceCatalogId?: string;
    targetCatalogId?: string;
  },
): Promise<ApiEnvelope<MappingCommitResponse>> {
  const body: Record<string, string> = {};
  if (payload.owningOrgUnitId) body.owningOrgUnitId = payload.owningOrgUnitId;
  if (payload.sourceCatalogId) body.sourceCatalogId = payload.sourceCatalogId;
  if (payload.targetCatalogId) body.targetCatalogId = payload.targetCatalogId;
  return apiFetch<ApiEnvelope<MappingCommitResponse>>(
    `/import/sessions/${sessionId}/commit`,
    { method: "POST", body: JSON.stringify(body) },
  );
}

/** Get a document by ID — GET /documents/{documentId} */
export interface DocumentResponse {
  id: string;
  title: string;
  documentType: string;
  lifecycleStatus: string;
  createdAt: string;
  updatedAt: string;
}

export function getDocument(
  documentId: string,
): Promise<ApiEnvelope<DocumentResponse>> {
  return apiFetch<ApiEnvelope<DocumentResponse>>(`/documents/${documentId}`);
}

/** Rename/update a document — PATCH /documents/{documentId} */
export function renameDocument(
  documentId: string,
  title: string,
  extra?: { catalogType?: CatalogType },
): Promise<ApiEnvelope<DocumentResponse>> {
  return apiFetch<ApiEnvelope<DocumentResponse>>(`/documents/${documentId}`, {
    method: "PATCH",
    body: JSON.stringify({ title, ...extra }),
  });
}

/** Transition a document's lifecycle status — POST /documents/{documentId}/transitions */
export interface TransitionPayload {
  targetStatus: string;
  comments?: string;
}

export interface TransitionResponse {
  id: string;
  documentId: string;
  lifecycleStatus: string;
  previousStatus: string;
  comments: string | null;
  createdAt: string;
}

/** Get transition history for a document — GET /documents/{documentId}/transitions */
export function getDocumentTransitions(
  documentId: string,
): Promise<ApiEnvelope<TransitionResponse[]>> {
  return apiFetch<ApiEnvelope<TransitionResponse[]>>(
    `/documents/${documentId}/transitions`,
  );
}

export function transitionDocument(
  documentId: string,
  payload: TransitionPayload,
): Promise<ApiEnvelope<TransitionResponse>> {
  return apiFetch<ApiEnvelope<TransitionResponse>>(
    `/documents/${documentId}/transitions`,
    { method: "POST", body: JSON.stringify(payload) },
  );
}

/** Import an OSCAL catalog from a URL */
export function importCatalogFromUrl(
  url: string,
  owningOrgUnitId?: string,
): Promise<ApiEnvelope<ImportJobResponse>> {
  let importUrl = "/import?resource=catalog";
  if (owningOrgUnitId) {
    importUrl += `&owning_org_unit_id=${owningOrgUnitId}`;
  }
  return apiFetch<ApiEnvelope<ImportJobResponse>>(importUrl, {
    method: "POST",
    body: JSON.stringify({ url }),
  });
}

/** Import an OSCAL profile */
export function importProfile(
  file: File,
  mode: "full" | "delta" = "full",
  owningOrgUnitId?: string,
): Promise<ApiEnvelope<ImportJobResponse>> {
  const formData = new FormData();
  formData.append("file", file);
  let url = `${BASE_URL}/import?resource=profile&mode=${mode}&auto_migrate=true`;
  if (owningOrgUnitId) {
    url += `&owning_org_unit_id=${owningOrgUnitId}`;
  }
  return fetch(url, {
    method: "POST",
    headers: { Accept: "*/*", ...getAuthHeaders() },
    body: formData,
  }).then(async (res) => {
    if (!res.ok) {
      const body = await res.text().catch(() => "");
      let title = "Import Failed";
      let detail = res.statusText;
      try {
        const parsed = JSON.parse(body);
        // Support both { errors: [{ title, detail }] } and { title, detail } shapes
        const src = parsed?.errors?.[0] ?? parsed;
        if (src?.title) title = src.title;
        if (src?.detail || src?.message) detail = src.detail || src.message;
      } catch {
        if (body) detail = body;
      }
      const err = new Error(detail);
      err.name = title;
      throw err;
    }
    return res.json() as Promise<ApiEnvelope<ImportJobResponse>>;
  });
}

/** Import an OSCAL profile from a URL */
export function importProfileFromUrl(
  url: string,
  owningOrgUnitId?: string,
): Promise<ApiEnvelope<ImportJobResponse>> {
  let importUrl = "/import?resource=profile";
  if (owningOrgUnitId) {
    importUrl += `&owning_org_unit_id=${owningOrgUnitId}`;
  }
  return apiFetch<ApiEnvelope<ImportJobResponse>>(importUrl, {
    method: "POST",
    body: JSON.stringify({ url }),
  });
}

/** Import component definitions */
export function importComponent(
  file: File,
  mode: "full" | "delta" = "full",
  owningOrgUnitId?: string,
): Promise<ApiEnvelope<ImportJobResponse>> {
  const formData = new FormData();
  formData.append("file", file);
  let url = `${BASE_URL}/import?resource=component&mode=${mode}&auto_migrate=true`;
  if (owningOrgUnitId) {
    url += `&owning_org_unit_id=${owningOrgUnitId}`;
  }
  return fetch(url, {
    method: "POST",
    headers: { Accept: "*/*", ...getAuthHeaders() },
    body: formData,
  }).then(async (res) => {
    if (!res.ok) {
      const body = await res.text().catch(() => "");
      let title = "Import Failed";
      let detail = res.statusText;
      try {
        const parsed = JSON.parse(body);
        const firstError = parsed?.errors?.[0];
        if (firstError) {
          title = firstError.title || title;
          const raw = firstError.detail || firstError.message || detail;
          detail = raw.split(/[.(]/)[0].trim();
        }
      } catch {
        if (body) detail = body;
      }
      const err = new Error(detail);
      err.name = title;
      throw err;
    }
    return res.json() as Promise<ApiEnvelope<ImportJobResponse>>;
  });
}

/** Import SSP */
export function importSSP(
  file: File,
  mode: "full" | "delta" = "full",
  owningOrgUnitId?: string,
): Promise<ApiEnvelope<ImportJobResponse>> {
  const formData = new FormData();
  formData.append("file", file);
  let url = `${BASE_URL}/import?resource=ssp&mode=${mode}&auto_migrate=true`;
  if (owningOrgUnitId) {
    url += `&owning_org_unit_id=${owningOrgUnitId}`;
  }
  return fetch(url, {
    method: "POST",
    headers: { Accept: "*/*", ...getAuthHeaders() },
    body: formData,
  }).then(async (res) => {
    if (!res.ok) {
      const body = await res.text().catch(() => "");
      let title = "Import Failed";
      let detail = res.statusText;
      try {
        const parsed = JSON.parse(body);
        const firstError = parsed?.errors?.[0];
        if (firstError) {
          title = firstError.title || title;
          const raw = firstError.detail || firstError.message || detail;
          detail = raw.split(/[.(]/)[0].trim();
        }
      } catch {
        if (body) detail = body;
      }
      const err = new Error(detail);
      err.name = title;
      throw err;
    }
    return res.json() as Promise<ApiEnvelope<ImportJobResponse>>;
  });
}

/* ─────────────────────────────────────────────────────────
   Profile endpoints
───────────────────────────────────────────────────────── */

export interface ProfileResponse {
  id: string;
  documentId: string;
  title: string;
  oscalVersion: string;
  lifecycleStatus: string;
  resolved: boolean;
  versionNumber: number;
  importCount: number;
  groupCount?: number;
  controlCount?: number;
  createdAt: string;
  updatedAt: string;
}

/** Profile import entry (inside a full profile detail) */
export interface ProfileImport {
  id: string;
  href: string;
  sourceType?: "CATALOG" | "PROFILE";
  sourceId?: string;
  sourceTitle?: string;
  includeControls: { "with-ids"?: string[]; "with-child-controls"?: string }[];
  excludeControls: { "with-ids"?: string[]; "with-child-controls"?: string }[];
  sortOrder: number;
  controlDetails?: ProfileControlDetail[];
  catalogId?: string;
}

/** Control detail entry inside a profile response */
export interface ProfileControlDetail {
  controlId: string;
  controlName: string;
  groupId: string;
  groupName: string;
  hasParameters?: boolean;
}

/** Full profile detail — GET /profiles/{id} */
export interface ProfileDetailResponse {
  id: string;
  documentId: string;
  title: string;
  oscalVersion: string;
  lifecycleStatus: string;
  versionNumber: number;
  imports: ProfileImport[];
  merge: {
    id: string;
    combineMethod: string;
    structuringDirective: string;
    custom: unknown | null;
  } | null;
  modify: unknown | null;
  createdAt: string;
  updatedAt: string;
}

/** Create a new profile — POST /profiles */
export interface CreateProfilePayload {
  title: string;
  owningOrgUnitId?: string | null;
  imports: {
    href: string;
    sourceType?: "CATALOG" | "PROFILE";
    includeControls: { "with-ids": string[] }[];
    excludeControls: { "with-ids": string[] }[];
    sortOrder: number;
  }[];
}

export function createProfile(
  payload: CreateProfilePayload,
): Promise<ApiEnvelope<ProfileDetailResponse>> {
  return apiFetch<ApiEnvelope<ProfileDetailResponse>>("/profiles", {
    method: "POST",
    body: JSON.stringify(payload),
  });
}

/** List all profiles — GET /profiles */
export function listProfiles(): Promise<ApiEnvelope<ProfileResponse[]>> {
  return apiFetch<ApiEnvelope<ProfileResponse[]>>("/profiles");
}
export function listCompProfiles(): Promise<ApiEnvelope<ProfileResponse[]>> {
  return apiFetch<ApiEnvelope<ProfileResponse[]>>(
    `/component-definitions/profiles`,
  );
}

/** Get a single profile by ID — GET /profiles/{id} */
export function getProfile(
  id: string,
): Promise<ApiEnvelope<ProfileDetailResponse>> {
  return apiFetch<ApiEnvelope<ProfileDetailResponse>>(`/profiles/${id}`);
}

/** Get a profile by document ID — GET /profiles/by-document/{documentId} */
export function getProfileByDocumentId(
  documentId: string,
): Promise<ApiEnvelope<ProfileDetailResponse>> {
  return apiFetch<ApiEnvelope<ProfileDetailResponse>>(
    `/profiles/by-document/${documentId}`,
  );
}

/** Update profile (merge, modify, etc.) — PATCH /profiles/{id} */
/** A single set-parameter entry for profile modify payloads */
export interface SetParameter {
  "param-id": string;
  label?: string;
  usage?: string;
  class?: string;
  values: string[];
  constraints?: {
    description: string;
    tests?: { expression: string; remarks?: string }[];
  }[];
  guidelines?: { prose: string }[];
  select?: {
    "how-many": string;
    choice: string[];
  } | null;
}

export interface UpdateProfilePayload {
  title?: string;
  imports?: {
    href: string;
    sourceType?: "CATALOG" | "PROFILE";
    includeControls: { "with-ids": string[] }[];
    excludeControls: { "with-ids": string[] }[];
    sortOrder: number;
  }[];
  merge?: {
    combineMethod: "use-first" | "keep";
    structuringDirective: "flat" | "as-is" | "custom";
    custom?: {
      "insert-controls"?: {
        order?: "keep" | "ascending" | "descending";
        "include-all"?: Record<string, unknown>;
        "include-controls"?: { "with-id": string }[];
        "exclude-controls"?: { "with-id": string }[];
      }[];
    } | null;
  };
  modify?: {
    setParameters?: SetParameter[];
    alters?: {
      "control-id": string;
      adds?: {
        position?: string;
        "by-id"?: string;
        name: string;
        title?: string;
        prose: string;
        ns?: string;
        class?: string;
      }[];
      removes?: {
        "by-id"?: string;
        name?: string;
        ns?: string;
        class?: string;
      }[];
    }[];
  };
}

export function updateProfile(
  id: string,
  payload: UpdateProfilePayload,
): Promise<ApiEnvelope<ProfileDetailResponse>> {
  return apiFetch<ApiEnvelope<ProfileDetailResponse>>(`/profiles/${id}`, {
    method: "PATCH",
    body: JSON.stringify(payload),
  });
}

/* ── Merge Analysis ── */

/** Source entry for an overlapping control */
export interface OverlappingControlSource {
  importId: string;
  sourceType: "CATALOG" | "PROFILE";
  sourceTitle: string;
}

/** A single overlapping control across multiple imports */
export interface OverlappingControl {
  controlId: string;
  controlTitle: string;
  sourceCount: number;
  sources: OverlappingControlSource[];
}

/** Per-import summary in merge analysis */
export interface ImportSummary {
  importId: string;
  sourceTitle: string;
  totalControls: number;
  uniqueControls: number;
  overlappingControls: number;
  overlappingControlIds: string[];
}

/** Current merge configuration */
export interface CurrentMergeConfig {
  id: string;
  combineMethod: string;
  structuringDirective: string;
  custom: unknown | null;
}

/** Full merge analysis response */
export interface MergeAnalysisResponse {
  profileId: string;
  totalImports: number;
  totalUniqueControls: number;
  totalOverlappingControls: number;
  currentMergeConfig: CurrentMergeConfig | null;
  overlappingControls: OverlappingControl[];
  importSummaries: ImportSummary[];
}

/** Get merge analysis for a profile — GET /profiles/{id}/merge-analysis */
export function getProfileMergeAnalysis(
  id: string,
): Promise<ApiEnvelope<MergeAnalysisResponse>> {
  return apiFetch<ApiEnvelope<MergeAnalysisResponse>>(
    `/profiles/${id}/merge-analysis`,
  );
}

/** Available parameter entry — GET /profiles/{id}/available-parameters */
export interface AvailableParameter {
  paramId: string;
  controlId: string;
  controlTitle: string;
  controlStatement?: string;
  controlGuidance?: string;
  label: string;
  usage?: string;
  clazz?: string | null;
  values?: string[];
  constraints?: ParameterConstraint[];
  guidelines?: { prose: string }[];
  selectConfig?: ParameterSelection | null;
}

/** Get available parameters for a profile — GET /profiles/{id}/available-parameters */
export function getProfileAvailableParameters(
  id: string,
): Promise<ApiEnvelope<AvailableParameter[]>> {
  return apiFetch<ApiEnvelope<AvailableParameter[]>>(
    `/profiles/${id}/available-parameters`,
  );
}

/** Available control entry — GET /profiles/{id}/available-controls */
export interface AvailableControlPart {
  partId: string;
  name: string;
  title: string | null;
  hasProse: boolean;
}

export interface AvailableControl {
  controlId: string;
  controlTitle: string;
  groupId?: string;
  groupTitle?: string;
  parts: AvailableControlPart[];
  hasAlter: boolean;
}

/** Get available controls for a profile — GET /profiles/{id}/available-controls */
export function getProfileAvailableControls(
  id: string,
): Promise<ApiEnvelope<AvailableControl[]>> {
  return apiFetch<ApiEnvelope<AvailableControl[]>>(
    `/profiles/${id}/available-controls`,
  );
}

/** Delete a profile */
export function deleteProfile(id: string, reason?: string): Promise<void> {
  const qs = reason ? `?reason=${encodeURIComponent(reason)}` : "";
  return apiFetch<void>(`/profiles/${id}${qs}`, { method: "DELETE" });
}

/** Resolve a published profile — POST /profiles/{id}/resolve */
export interface ResolvedControlPart {
  ns?: string;
  name: string;
  prose: string;
  part_id: string;
  resolved_prose: string;
}

export interface ResolvedControlParam {
  class?: string;
  label?: string;
  values?: string[];
  param_id: string;
  guidelines?: { prose: string }[];
}

export interface ResolvedControl {
  title: string;
  control_id: string;
  group_id?: string;
  group_title?: string;
  source_href: string;
  source_catalog_id: string;
  parts?: ResolvedControlPart[];
  params?: ResolvedControlParam[];
}

export interface ResolveProfileResponse {
  profileId: string;
  resolvedAt: string;
  profileVersion: number;
  /** Structuring directive applied when resolving: "as-is" | "flat" | "custom" */
  method?: string;
  sourceCount?: number;
  controls: ResolvedControl[];
  sourceCatalogVersions: Record<string, unknown>;
}

export function resolveProfile(
  id: string,
): Promise<ApiEnvelope<ResolveProfileResponse>> {
  return apiFetch<ApiEnvelope<ResolveProfileResponse>>(
    `/profiles/${id}/resolve`,
    { method: "POST" },
  );
}

/** Get resolved catalog for a profile — GET /profiles/{id}/resolved-catalog */
export function getResolvedCatalog(
  id: string,
): Promise<ApiEnvelope<ResolveProfileResponse>> {
  return apiFetch<ApiEnvelope<ResolveProfileResponse>>(
    `/profiles/${id}/resolved-catalog`,
  );
}

/** List resolved profiles — GET /profiles/resolved */
export function listResolvedProfiles(): Promise<
  ApiEnvelope<ProfileResponse[]>
> {
  return apiFetch<ApiEnvelope<ProfileResponse[]>>(`/profiles/resolved`);
}

/* ─────────────────────────────────────────────────────────
   Mapping endpoints
───────────────────────────────────────────────────────── */

/** Get a single mapping with its full control pairs */
export function getMapping(id: string): Promise<ControlMapping> {
  return apiFetch<ControlMapping>(`/mappings/${id}`);
}

/** Create a new (empty) mapping */
export function createMapping(
  payload: CreateMappingPayload,
  owningOrgUnitId?: string,
): Promise<ControlMapping> {
  let url = "/mappings";
  if (owningOrgUnitId) {
    url += `?owning_org_unit_id=${owningOrgUnitId}`;
  }
  return apiFetch<ControlMapping>(url, {
    method: "POST",
    body: JSON.stringify(payload),
  });
}

/** Update mapping metadata */
export function updateMapping(
  id: string,
  payload: Partial<CreateMappingPayload>,
): Promise<ControlMapping> {
  return apiFetch<ControlMapping>(`/mappings/${id}`, {
    method: "PATCH",
    body: JSON.stringify(payload),
  });
}

/** Delete a mapping */
export function deleteMapping(id: string): Promise<void> {
  return apiFetch<void>(`/mappings/${id}`, { method: "DELETE" });
}

/** Import an OSCAL mapping document (JSON or XML) */
export function importMapping(
  payload: ImportMappingPayload,
  owningOrgUnitId?: string,
): Promise<ImportResult> {
  let url = "/mappings/import";
  if (owningOrgUnitId) {
    url += `?owning_org_unit_id=${owningOrgUnitId}`;
  }
  return apiFetch<ImportResult>(url, {
    method: "POST",
    body: JSON.stringify(payload),
  });
}

/** Export a mapping as an OSCAL document */
export function exportMapping(
  id: string,
  format: "json" | "xml" = "json",
): Promise<Blob> {
  return fetch(`${BASE_URL}/mappings/${id}/export?format=${format}`, {
    headers: {
      Accept: format === "json" ? "application/json" : "application/xml",
      ...getAuthHeaders(),
    },
  }).then((r) => {
    if (!r.ok) throw new Error(`Export failed: ${r.status}`);
    return r.blob();
  });
}

/** Add or update individual mapping pairs in bulk */
export function upsertMappingPairs(
  mappingId: string,
  pairs: MappingPair[],
): Promise<{ updated: number }> {
  return apiFetch<{ updated: number }>(`/mappings/${mappingId}/pairs`, {
    method: "PUT",
    body: JSON.stringify({ pairs }),
  });
}

/* ─────────────────────────────────────────────────────────
   Mapping Collection endpoints (OSCAL mapping-collection)
───────────────────────────────────────────────────────── */

export interface MappingCollectionSummary {
  id: string;
  documentId: string;
  title?: string;
  oscalVersion?: string;
  lifecycleStatus?: string;
  versionNumber?: number;
  mappingCount?: number;
  entryCount?: number;
  /** True when this collection was auto-derived from a chain of two mappings */
  derived?: boolean;
  derivedFromCollectionAId?: string | null;
  derivedFromCollectionBId?: string | null;
  derivedFromCollectionATitle?: string | null;
  derivedFromCollectionBTitle?: string | null;
  createdAt: string;
  updatedAt: string;
}

export interface MappingEntryResponse {
  id: string;
  entryUuid: string;
  sourceControlId: string;
  sourceControlTitle: string | null;
  targetControlId: string;
  targetControlTitle: string | null;
  relationship: MappingRelationship;
  relatedSourceMappings?: RelatedSourceMappingResponse[];
  /** True when this entry was auto-generated by the chain-mapping feature (read-only) */
  derived?: boolean;
  sortOrder: number;
  createdAt: string;
  updatedAt: string;
}

export interface RelatedSourceMappingResponse {
  mappingId: string;
  entryId: string;
  sourceCatalogId: string;
  sourceCatalogTitle?: string;
  sourceResourceHref?: string;
  sourceControlId: string;
  sourceControlTitle?: string;
  relationship: MappingRelationship;
}

export type MappingRelationship =
  | "equivalent-to"
  | "equal-to"
  | "subset-of"
  | "superset-of"
  | "intersects-with"
  | "no-relationship";

export interface MappingResponse {
  id: string;
  mappingUuid: string;
  sourceResourceHref: string;
  targetResourceHref: string;
  /** Human-readable catalog titles resolved by the backend */
  sourceCatalogTitle?: string | null;
  targetCatalogTitle?: string | null;
  sortOrder: number;
  entries: MappingEntryResponse[];
  /** Auto-derived mapping collections created as a side-effect of adding this mapping */
  autoCreated?: AutoCreatedCollection[];
  createdAt: string;
  updatedAt: string;
}

export interface MappingCollectionDetail {
  id: string;
  documentId: string;
  title?: string;
  mappings: MappingResponse[];
  /** Set when this collection was auto-derived from two chained mappings */
  derived?: boolean;
  derivedFromCollectionAId?: string | null;
  derivedFromCollectionBId?: string | null;
  derivedFromCollectionATitle?: string | null;
  derivedFromCollectionBTitle?: string | null;
  createdAt: string;
  updatedAt: string;
}

/** A mapping collection auto-generated by the chain-mapping feature */
export interface AutoCreatedCollection {
  derivedCollectionId: string;
  derivedDocumentId: string;
  derivedCollectionTitle: string;
  derivedEntryCount: number;
  skippedEntryCount: number;
  skippedSourceControlIds: string[];
}

/** A derived mapping collection actually created by the apply-suggestions endpoint */
export interface AppliedAutoMapping {
  collectionId: string;
  documentId: string;
  title: string;
  derivedEntryCount: number;
  skippedEntryCount: number;
  skippedSourceControlIds: string[];
}

/** A pending chain-mapping suggestion: collection A connects to collection B via a common catalog */
export interface AutoMappingSuggestion {
  collectionAId: string;
  collectionATitle: string;
  collectionBId: string;
  collectionBTitle: string;
  intermediaryCatalogHref: string;
  derivedSourceHref: string;
  derivedTargetHref: string;
  derivedSourceCatalogTitle?: string;
  derivedTargetCatalogTitle?: string;
  /** Full human-readable title for the derived mapping that would be created */
  derivedMappingTitle?: string;
}

export interface CoverageData {
  mappingCollectionId: string;
  totalSourceControls: number;
  mappedSourceControls: number;
  totalTargetControls: number;
  coveredTargetControls: number;
  sourceCoveragePercent: number;
  targetCoveragePercent: number;
  unmappedSourceControlIds: string[];
  uncoveredTargetControlIds: string[];
  gaps: { controlId: string; framework: string; gapType: string }[];
}

/** List mapping collections */
export function listMappingCollections(): Promise<
  ApiEnvelope<MappingCollectionSummary[]>
> {
  return apiFetch<ApiEnvelope<MappingCollectionSummary[]>>(
    `/mapping-collections`,
  );
}

/** Get a single mapping collection with nested mappings & entries */
export function getMappingCollection(
  id: string,
): Promise<ApiEnvelope<MappingCollectionDetail>> {
  return apiFetch<ApiEnvelope<MappingCollectionDetail>>(
    `/mapping-collections/${id}`,
  );
}

/** Create a new mapping collection */
export function createMappingCollection(payload: {
  title: string;
  owningOrgUnitId?: string;
}): Promise<ApiEnvelope<MappingCollectionSummary>> {
  return apiFetch<ApiEnvelope<MappingCollectionSummary>>(
    "/mapping-collections",
    {
      method: "POST",
      body: JSON.stringify(payload),
    },
  );
}

/** Delete a mapping collection */
export function deleteMappingCollection(
  id: string,
  reason?: string,
): Promise<void> {
  const qs = reason ? `?reason=${encodeURIComponent(reason)}` : "";
  return apiFetch<void>(`/mapping-collections/${id}${qs}`, {
    method: "DELETE",
  });
}

/** Add a mapping (link two catalogs) in a collection */
export function addMappingToCollection(
  collectionId: string,
  payload: {
    sourceResourceHref: string;
    targetResourceHref: string;
    sortOrder?: number;
  },
): Promise<ApiEnvelope<MappingResponse>> {
  return apiFetch<ApiEnvelope<MappingResponse>>(
    `/mapping-collections/${collectionId}/mappings`,
    { method: "POST", body: JSON.stringify(payload) },
  );
}

/**
 * Get pending chain-mapping suggestions for a collection.
 * Returns suggestions where this collection connects to another via a common
 * catalog but the derived mapping has not yet been auto-created.
 */
export function getAutoMappingSuggestions(
  collectionId: string,
): Promise<ApiEnvelope<AutoMappingSuggestion[]>> {
  return apiFetch<ApiEnvelope<AutoMappingSuggestion[]>>(
    `/mapping-collections/${collectionId}/auto-mapping-suggestions`,
  );
}

/**
 * Apply (create) ALL pending chain-mapping suggestions for a collection at once.
 * Already-existing derivations are silently skipped, so this is safe to call
 * multiple times. Returns the list of derived collections actually created.
 */
export function applyAutoMappingSuggestions(
  collectionId: string,
): Promise<ApiEnvelope<AppliedAutoMapping[]>> {
  return apiFetch<ApiEnvelope<AppliedAutoMapping[]>>(
    `/mapping-collections/${collectionId}/auto-mapping-suggestions/apply`,
    { method: "POST" },
  );
}

/** Get a mapping within a collection */
export function getCollectionMapping(
  collectionId: string,
  mappingId: string,
): Promise<ApiEnvelope<MappingResponse>> {
  return apiFetch<ApiEnvelope<MappingResponse>>(
    `/mapping-collections/${collectionId}/mappings/${mappingId}`,
  );
}

/** Create a mapping entry (control-to-control relationship) */
export function createMappingEntry(
  collectionId: string,
  mappingId: string,
  payload: {
    sourceControlId: string;
    targetControlId: string;
    relationship: MappingRelationship;
    sortOrder?: number;
  },
): Promise<ApiEnvelope<MappingEntryResponse>> {
  return apiFetch<ApiEnvelope<MappingEntryResponse>>(
    `/mapping-collections/${collectionId}/mappings/${mappingId}/entries`,
    { method: "POST", body: JSON.stringify(payload) },
  );
}

/** Update a mapping entry */
export function updateMappingEntry(
  collectionId: string,
  mappingId: string,
  entryId: string,
  payload: {
    sourceControlId: string;
    targetControlId: string;
    relationship: MappingRelationship;
    sortOrder: number;
    remarks?: string | null;
  },
): Promise<ApiEnvelope<MappingEntryResponse>> {
  return apiFetch<ApiEnvelope<MappingEntryResponse>>(
    `/mapping-collections/${collectionId}/mappings/${mappingId}/entries/${entryId}`,
    { method: "PUT", body: JSON.stringify(payload) },
  );
}

/** Delete a mapping entry */
export function deleteMappingEntry(
  collectionId: string,
  mappingId: string,
  entryId: string,
): Promise<void> {
  return apiFetch<void>(
    `/mapping-collections/${collectionId}/mappings/${mappingId}/entries/${entryId}`,
    { method: "DELETE" },
  );
}

/** Get coverage / gap analysis for a mapping collection */
export function getMappingCoverage(
  collectionId: string,
  profileId?: string,
): Promise<ApiEnvelope<CoverageData>> {
  const qs = profileId ? `?profile_id=${encodeURIComponent(profileId)}` : "";
  return apiFetch<ApiEnvelope<CoverageData>>(
    `/mapping-collections/${collectionId}/coverage${qs}`,
  );
}

/* ─────────────────────────────────────────────────────────
   Component Definition types
───────────────────────────────────────────────────────── */

export interface ComponentDefinitionSummaryResponse {
  id: string;
  documentId: string;
  title?: string;
  lifecycleStatus?: string;
  componentCount?: number;
  controlCount?: number;
  createdAt: string;
  updatedAt: string;
}

export interface ComponentResponse {
  id: string;
  componentUuid: string;
  componentType: ComponentType;
  title: string;
  description: string;
  purpose: string;
  props?: { name: string; value: string }[];
  sortOrder: number;
  controlImplementations: ControlImplementationResponse[];
  createdAt: string;
  updatedAt: string;
}

export interface CapabilityResponse {
  id: string;
  capabilityUuid: string;
  name: string;
  description: string;
  sortOrder: number;
  remarks?: string | null;
  createdAt: string;
  updatedAt: string;
}

export interface ComponentDefinitionResponse {
  id: string;
  documentId: string;
  components: ComponentResponse[];
  capabilities: CapabilityResponse[];
  createdAt: string;
  updatedAt: string;
}

export interface SetParameterResponse {
  paramId: string;
  values: string[];
  remarks?: string;
}

export interface ControlImplementationResponse {
  id: string;
  ciUuid: string;
  source: string;
  description: string;
  setParameters: SetParameterResponse[];
  sortOrder: number;
  implementedRequirements: ImplementedRequirementResponse[];
  createdAt: string;
  updatedAt: string;
}

export interface ImplementedRequirementResponse {
  id: string;
  irUuid: string;
  controlId: string;
  description: string;
  setParameters: SetParameterResponse[];
  sortOrder: number;
  statements: StatementResponse[];
  createdAt: string;
  updatedAt: string;
}

export interface StatementResponse {
  id: string;
  statementUuid: string;
  statementId: string;
  description: string;
  sortOrder: number;
  createdAt: string;
  updatedAt: string;
}

export type ComponentType =
  | "software"
  | "hardware"
  | "service"
  | "policy"
  | "process"
  | "procedure"
  | "plan"
  | "guidance"
  | "standard"
  | "validation"
  | "operating-system";

export interface CreateComponentDefinitionPayload {
  title: string;
  owningOrgUnitId?: string | null;
}

export interface AddComponentPayload {
  componentType: ComponentType;
  title: string;
  description?: string;
  purpose?: string;
  props?: { name: string; value: string }[];
  sortOrder: number;
}

export interface AddCapabilityPayload {
  name: string;
  description?: string;
  sortOrder: number;
}

/* ── Responsible Role types (on CDEF components) ── */

export interface ComponentResponsibleRoleResponse {
  id: string;
  roleId: string;
  title: string;
  partyUuids: string[];
  createdAt: string;
  updatedAt: string;
}

export interface AddComponentResponsibleRolePayload {
  roleId: string;
  title: string;
  partyUuids?: string[];
}

/* ── Component Definition Export types ── */

export type CdefExportFormat = "json" | "xml" | "yaml";

export interface CdefExportPayload {
  format: CdefExportFormat;
}

export interface CdefExportResponse {
  format: CdefExportFormat;
  filename: string;
  content: string;
}

/* ── SSP Usage (which SSPs reference a component) ── */

export interface ComponentSspUsageResponse {
  sspId: string;
  sspTitle: string;
  sspDocumentId: string;
  componentTitle: string;
  importedAt: string;
}

/* ── Compliance Status types ── */

export interface ComponentComplianceStatusResponse {
  componentId: string;
  totalControls: number;
  implementedControls: number;
  partialControls: number;
  plannedControls: number;
  notApplicableControls: number;
  compliancePercentage: number;
  lastAssessedAt: string | null;
  nextAssessmentDue: string | null;
}

export interface SetParameterPayload {
  paramId: string;
  values: string[];
  remarks?: string;
}

export interface StatementPayload {
  statementId: string;
  description?: string;
  sortOrder: number;
}

export interface ImplementedRequirementPayload {
  controlId: string;
  description?: string;
  setParameters?: SetParameterPayload[];
  statements?: StatementPayload[];
}

export interface AddControlImplementationPayload {
  source: string;
  description?: string;
  setParameters?: SetParameterPayload[];
  implementedRequirements?: ImplementedRequirementPayload[];
}

/* ─────────────────────────────────────────────────────────
   Component Definition endpoints
───────────────────────────────────────────────────────── */

/** List all component definitions */
export function listComponentDefinitions(): Promise<
  ApiEnvelope<ComponentDefinitionSummaryResponse[]>
> {
  return apiFetch<ApiEnvelope<ComponentDefinitionSummaryResponse[]>>(
    `/component-definitions`,
  );
}

/** Get a single component definition with full detail */
export function getComponentDefinition(
  id: string,
): Promise<ApiEnvelope<ComponentDefinitionResponse>> {
  return apiFetch<ApiEnvelope<ComponentDefinitionResponse>>(
    `/component-definitions/${id}`,
  );
}

/** Create a new component definition */
export function createComponentDefinition(
  payload: CreateComponentDefinitionPayload,
): Promise<ApiEnvelope<ComponentDefinitionSummaryResponse>> {
  return apiFetch<ApiEnvelope<ComponentDefinitionSummaryResponse>>(
    "/component-definitions",
    { method: "POST", body: JSON.stringify(payload) },
  );
}

/** Delete a component definition (soft delete) */
export function deleteComponentDefinition(
  id: string,
  reason?: string,
): Promise<ApiEnvelope<null>> {
  const qs = reason ? `?reason=${encodeURIComponent(reason)}` : "";
  return apiFetch<ApiEnvelope<null>>(`/component-definitions/${id}${qs}`, {
    method: "DELETE",
  });
}

/* ── Component endpoints (within a definition) ── */

/** List components in a definition */
export function listComponents(
  definitionId: string,
  type?: ComponentType,
): Promise<ApiEnvelope<ComponentResponse[]>> {
  const qs = type ? `?type=${type}` : "";
  return apiFetch<ApiEnvelope<ComponentResponse[]>>(
    `/component-definitions/${definitionId}/components${qs}`,
  );
}

/** Delete a component from a definition */
export function deleteComponent(
  definitionId: string,
  componentId: string,
): Promise<ApiEnvelope<null>> {
  return apiFetch<ApiEnvelope<null>>(
    `/component-definitions/${definitionId}/components/${componentId}`,
    { method: "DELETE" },
  );
}

/** Add a component to a definition */
export function addComponent(
  definitionId: string,
  payload: AddComponentPayload,
): Promise<ApiEnvelope<ComponentResponse>> {
  return apiFetch<ApiEnvelope<ComponentResponse>>(
    `/component-definitions/${definitionId}/components`,
    { method: "POST", body: JSON.stringify(payload) },
  );
}

/* ── Capability endpoints ── */

/** List capabilities in a definition */
export function listCapabilities(
  definitionId: string,
): Promise<ApiEnvelope<CapabilityResponse[]>> {
  return apiFetch<ApiEnvelope<CapabilityResponse[]>>(
    `/component-definitions/${definitionId}/capabilities`,
  );
}

/** Add a capability to a definition */
export function addCapability(
  definitionId: string,
  payload: AddCapabilityPayload,
): Promise<ApiEnvelope<CapabilityResponse>> {
  return apiFetch<ApiEnvelope<CapabilityResponse>>(
    `/component-definitions/${definitionId}/capabilities`,
    { method: "POST", body: JSON.stringify(payload) },
  );
}

/* ── Control Implementation endpoints ── */

/** List control implementations for a component */
export function listControlImplementations(
  definitionId: string,
  componentId: string,
): Promise<ApiEnvelope<ControlImplementationResponse[]>> {
  return apiFetch<ApiEnvelope<ControlImplementationResponse[]>>(
    `/component-definitions/${definitionId}/components/${componentId}/control-implementations`,
  );
}

/** Add a control implementation to a component */
export function addControlImplementation(
  definitionId: string,
  componentId: string,
  payload: AddControlImplementationPayload,
): Promise<ApiEnvelope<ControlImplementationResponse>> {
  return apiFetch<ApiEnvelope<ControlImplementationResponse>>(
    `/component-definitions/${definitionId}/components/${componentId}/control-implementations`,
    { method: "POST", body: JSON.stringify(payload) },
  );
}

/** Update (full replace) a control implementation on a component */
export function updateControlImplementation(
  definitionId: string,
  componentId: string,
  ciId: string,
  payload: AddControlImplementationPayload,
): Promise<ApiEnvelope<ControlImplementationResponse>> {
  return apiFetch<ApiEnvelope<ControlImplementationResponse>>(
    `/component-definitions/${definitionId}/components/${componentId}/control-implementations/${ciId}`,
    { method: "PUT", body: JSON.stringify(payload) },
  );
}

/** Delete one or many implemented requirements (controls) from a component */
export function deleteImplementedRequirements(
  definitionId: string,
  componentId: string,
  implementedRequirementIds: string[],
  reason?: string,
): Promise<ApiEnvelope<{ requested: number; deleted: number }>> {
  return apiFetch(
    `/component-definitions/${definitionId}/components/${componentId}/implemented-requirements/delete`,
    {
      method: "POST",
      body: JSON.stringify({ implementedRequirementIds, reason }),
    },
  );
}

/* ── Responsible Role endpoints (on CDEF components) ── */

/** List responsible roles for a component */
export function listComponentResponsibleRoles(
  definitionId: string,
  componentId: string,
): Promise<ApiEnvelope<ComponentResponsibleRoleResponse[]>> {
  return apiFetch<ApiEnvelope<ComponentResponsibleRoleResponse[]>>(
    `/component-definitions/${definitionId}/components/${componentId}/responsible-roles`,
  );
}

/** Add a responsible role to a component */
export function addComponentResponsibleRole(
  definitionId: string,
  componentId: string,
  payload: AddComponentResponsibleRolePayload,
): Promise<ApiEnvelope<ComponentResponsibleRoleResponse>> {
  return apiFetch<ApiEnvelope<ComponentResponsibleRoleResponse>>(
    `/component-definitions/${definitionId}/components/${componentId}/responsible-roles`,
    { method: "POST", body: JSON.stringify(payload) },
  );
}

/** Delete a responsible role from a component */
export function deleteComponentResponsibleRole(
  definitionId: string,
  componentId: string,
  roleId: string,
): Promise<ApiEnvelope<null>> {
  return apiFetch<ApiEnvelope<null>>(
    `/component-definitions/${definitionId}/components/${componentId}/responsible-roles/${roleId}`,
    { method: "DELETE" },
  );
}

/* ── Generic Document Export ── */

/** Export any OSCAL document by documentId as a JSON file download */
export function exportDocumentJson(documentId: string): Promise<Blob> {
  return fetch(`${BASE_URL}/export/${documentId}/json`, {
    headers: { Accept: "application/json", ...getAuthHeaders() },
  }).then((r) => {
    if (!r.ok) throw new Error(`Export failed: ${r.status}`);
    return r.blob();
  });
}

/* ── Component Definition Export ── */

/** Export a component definition in OSCAL format */
export function exportComponentDefinition(
  definitionId: string,
  format: CdefExportFormat = "json",
): Promise<ApiEnvelope<CdefExportResponse>> {
  return apiFetch<ApiEnvelope<CdefExportResponse>>(
    `/component-definitions/${definitionId}/export?format=${format}`,
  );
}

/* ── SSP Usage endpoints ── */

/** List SSPs that reference a given component */
export function listComponentSspUsage(
  definitionId: string,
  componentId: string,
): Promise<ApiEnvelope<ComponentSspUsageResponse[]>> {
  return apiFetch<ApiEnvelope<ComponentSspUsageResponse[]>>(
    `/component-definitions/${definitionId}/components/${componentId}/ssp-usage`,
  );
}

/* ── Compliance Status endpoints ── */

/** Get compliance status summary for a component */
export function getComponentComplianceStatus(
  definitionId: string,
  componentId: string,
): Promise<ApiEnvelope<ComponentComplianceStatusResponse>> {
  return apiFetch<ApiEnvelope<ComponentComplianceStatusResponse>>(
    `/component-definitions/${definitionId}/components/${componentId}/compliance-status`,
  );
}

/* ── Search endpoints ── */

/** Search components across all definitions */
export function searchComponents(params?: {
  q?: string;
  type?: ComponentType;
  control_id?: string;
}): Promise<ApiEnvelope<ComponentResponse[]>> {
  const qs = new URLSearchParams();
  if (params?.q) qs.set("q", params.q);
  if (params?.type) qs.set("type", params.type);
  if (params?.control_id) qs.set("control_id", params.control_id);
  const query = qs.toString();
  return apiFetch<ApiEnvelope<ComponentResponse[]>>(
    `/components/search${query ? `?${query}` : ""}`,
  );
}

/** Get all implementations for a specific control */
export function getControlImplementationsForControl(
  controlId: string,
): Promise<ApiEnvelope<ImplementedRequirementResponse[]>> {
  return apiFetch<ApiEnvelope<ImplementedRequirementResponse[]>>(
    `/controls/${controlId}/implementations`,
  );
}

/* ─────────────────────────────────────────────────────────
   SSP (System Security Plan) Types
───────────────────────────────────────────────────────── */

export interface SspSummaryResponse {
  id: string;
  documentId: string;
  title?: string;
  profileName?: string;
  lifecycleStatus?: string;
  importProfileHref: string;
  owningOrgUnitId?: string;
  sourceType?: string;
  sourceId?: string;
  sourceName?: string;
  createdAt: string;
  updatedAt: string;
}

export interface SspResponse {
  id: string;
  title: string;
  documentId: string;
  importProfileHref: string;
  systemCharacteristics: SystemCharacteristicsResponse | null;
  systemImplementation: {
    id: string;
    description: string | null;
    components: SspComponentResponse[];
    inventoryItems: InventoryItemResponse[];
  } | null;
  controlImplementations: SspControlImplementationResponse[];
  leveragedAuthorizations: LeveragedAuthorizationResponse[];
  createdAt: string;
  updatedAt: string;
}

export interface CreateSspPayload {
  title: string;
  importProfileHref: string;
  owningOrgUnitId?: string | null;
}

/** Payload for POST /ssps/from-application — creates an SSP that inherits
 *  components from a chosen application. Field names are snake_case to
 *  match the BE contract verbatim. */
export interface CreateSspFromApplicationPayload {
  title: string;
  import_profile_href: string;
  owning_org_unit_id: string;
  application_id: string;
  component_ids: string[];
  /** Provider SSP IDs to auto-wire as Leveraged Authorizations at creation time. */
  provider_ssp_ids?: string[];
}

/** Payload for POST /ssps/from-service — creates a service-level SSP that
 *  inherits the chosen components from a single service. */
export interface CreateSspFromServicePayload {
  title: string;
  import_profile_href: string;
  owning_org_unit_id: string;
  service_id: string;
  component_ids: string[];
}

export interface UpdateSspPayload {
  importProfileHref?: string;
}

export interface SystemCharacteristicsResponse {
  id: string;
  systemName: string;
  systemNameShort: string | null;
  description: string | null;
  securitySensitivityLevel: string | null;
  systemIds: { "identifier-type": string; id: string }[];
  authorizationBoundary: { description: string } | null;
  networkArchitecture: { description: string } | null;
  dataFlow: { description: string } | null;
  securityImpactLevel: {
    "security-objective-confidentiality": string;
    "security-objective-integrity": string;
    "security-objective-availability": string;
  } | null;
  status: string | null;
  statusRemarks: string | null;
  dateAuthorized: string | null;
  informationTypes: InformationTypeResponse[];
  createdAt: string;
  updatedAt: string;
}

export interface SetSystemCharacteristicsPayload {
  systemName: string;
  systemNameShort?: string;
  description?: string;
  securitySensitivityLevel?: string;
  systemType?: string;
  serviceModel?: string;
  deploymentModel?: string;
  systemIds?: { "identifier-type": string; id: string }[];
  authorizationBoundary?: { description: string };
  networkArchitecture?: { description: string };
  dataFlow?: { description: string };
  securityImpactLevel?: {
    "security-objective-confidentiality": string;
    "security-objective-integrity": string;
    "security-objective-availability": string;
  };
  status?: string;
  statusRemarks?: string;
  dateAuthorized?: string;
}

export interface InformationTypeResponse {
  id: string;
  infoTypeUuid: string;
  title: string;
  description: string | null;
  confidentialityImpact: { base: string } | null;
  integrityImpact: { base: string } | null;
  availabilityImpact: { base: string } | null;
  sortOrder: number;
  createdAt: string;
  updatedAt: string;
}

export interface AddInformationTypePayload {
  title: string;
  description?: string;
  confidentialityImpact?: { base: string; selected?: string };
  integrityImpact?: { base: string; selected?: string };
  availabilityImpact?: { base: string; selected?: string };
  sortOrder: number;
}

export interface SspComponentResponse {
  id: string;
  componentUuid: string;
  componentType: string;
  title: string;
  description: string | null;
  purpose: string | null;
  status: string | null;
  statusRemarks: string | null;
  sourceComponentId: string | null;
  props?: { name: string; value: string }[];
  sortOrder: number;
  createdAt: string;
  updatedAt: string;
  assetCount?: number | null;
  implementedControlCount?: number | null;
  fullyImplementedControlCount?: number | null;
  sspLayerSlug?: string | null;
  sspLayerName?: string | null;
  displayStatus?: string | null;
  displayStatusLabel?: string | null;
}

export interface ComponentProtocolPayload {
  name: string;
  title?: string;
  portRanges?: { start: number; end: number; transport: string }[];
  sortOrder: number;
}

export interface ComponentResponsibleRolePayload {
  role_id: string;
  party_uuids?: string[];
}

export interface AddSspComponentPayload {
  sourceComponentId?: string | null;
  componentType: string;
  title: string;
  description?: string;
  purpose?: string;
  status?: string;
  responsibleRoles?: ComponentResponsibleRolePayload[];
  protocols?: ComponentProtocolPayload[];
  sortOrder: number;
}

export interface AssetIdentifierResponse {
  id: string;
  identifierType: string;
  identifierValue: string;
}

export interface InventoryItemResponse {
  id: string;
  inventoryUuid: string;
  componentId: string | null;
  componentUuid: string | null;
  componentName: string | null;
  description: string;
  implementedComponents: Record<string, unknown>[];
  assetIdentifiers: AssetIdentifierResponse[];
  sortOrder: number;
  createdAt: string;
  updatedAt: string;
}

export interface AddInventoryItemPayload {
  description: string;
  component_id?: string;
  implementedComponents?: { "component-uuid": string }[];
  assetIdentifiers?: { identifierType: string; identifierValue: string }[];
  sortOrder: number;
}

export interface PatchInformationTypePayload {
  title?: string;
  description?: string;
  confidentialityImpact?: { base: string; selected?: string } | null;
  integrityImpact?: { base: string; selected?: string } | null;
  availabilityImpact?: { base: string; selected?: string } | null;
}

export interface PatchInventoryItemPayload {
  description?: string;
  component_id?: string | null;
  implementedComponents?: { "component-uuid": string }[];
  assetIdentifiers?: { identifierType: string; identifierValue: string }[];
}

export interface PatchSspComponentPayload {
  title?: string;
  componentType?: string;
  description?: string | null;
  purpose?: string | null;
  status?: string | null;
  statusRemarks?: string | null;
  responsibleRoles?: ComponentResponsibleRolePayload[];
}

export interface ComponentControlRow {
  implementedRequirementId: string;
  controlId: string;
  /** Human-readable control title from the response (field name varies). */
  controlTitle?: string | null;
  title?: string | null;
  implementationStatus: string | null;
  description: string | null;
}

export interface PatchByComponentLinkPayload {
  description?: string | null;
  implementationStatus?: string | null;
  responsibleRoles?: { roleId: string }[];
}

export interface SspImplementedRequirementResponse {
  id: string;
  irUuid: string;
  controlId: string;
  implementationStatus: string;
  origination: string;
  description?: string | null;
  setParameters: { values: string[]; "param-id": string }[];
  byComponents: Record<string, unknown>[];
  statements: {
    id: string;
    statementUuid: string;
    statementId: string;
    sortOrder: number;
    createdAt: string;
    updatedAt: string;
  }[];
  sortOrder: number;
  createdAt: string;
  updatedAt: string;
}

/** Raw API response for a control implementation (contains nested implementedRequirements) */
export interface SspControlImplementationRawResponse {
  id: string;
  ciUuid: string;
  componentId: string | null;
  source: string;
  description: string | null;
  implementedRequirements: SspImplementedRequirementResponse[];
  sortOrder: number;
  createdAt: string;
  updatedAt: string;
}

/** Flattened control implementation used by the UI (one entry per implemented requirement) */
export interface SspControlImplementationResponse {
  id: string;
  irUuid: string;
  controlId: string;
  componentId: string | null;
  source: string;
  description: string | null;
  implementationStatus: string;
  controlOrigination: string;
  setParameters: { values: string[]; "param-id": string }[];
  responsibleRoles?: { "role-id": string; "party-uuids"?: string[] }[];
  byComponents: Record<string, unknown>[];
  statements: {
    id: string;
    statementUuid: string;
    statementId: string;
    description?: string;
    sortOrder: number;
    createdAt: string;
    updatedAt: string;
  }[];
  sortOrder: number;
  createdAt: string;
  updatedAt: string;
  /** Parent control implementation ID */
  ciId: string;
}

export type LeveragedAuthorizationStatus = "active" | "revoked" | "expired";

export interface LeveragedAuthorizationResponse {
  id: string;
  leveragedUuid: string;
  title: string;
  partyUuid: string | null;
  dateAuthorized: string | null;
  providerSspId: string | null;
  status: LeveragedAuthorizationStatus | null;
  controlScope: string[] | null;
  sourceSspVersion: string | null;
  sortOrder: number;
  createdAt: string;
  updatedAt: string;
}

export interface AddLeveragedAuthorizationPayload {
  leveragedUuid?: string | null;
  title: string;
  partyUuid?: string;
  dateAuthorized?: string;
  sortOrder: number;
}

export interface UpdateLeveragedAuthorizationPayload {
  title?: string;
  partyUuid?: string;
  dateAuthorized?: string;
  status?: LeveragedAuthorizationStatus;
  controlScope?: string[] | null;
  remarks?: string;
}

export interface AssetResolutionResponse {
  inventoryItem: InventoryItemResponse;
  components: SspComponentResponse[];
  controlImplementations: SspControlImplementationResponse[];
}

/* ── SSP Metadata: Roles, Parties, Locations, Responsible Parties ── */

export interface SspRoleResponse {
  id: string;
  documentId: string;
  roleId: string;
  title: string;
  shortName: string | null;
  description: string | null;
  sortOrder: number;
  createdAt: string;
}

export interface SspRolePayload {
  roleId: string;
  title: string;
  shortName?: string | null;
  description?: string | null;
  sortOrder: number;
}

export interface SspPartyResponse {
  id: string;
  documentId: string;
  partyUuid: string;
  partyType: "person" | "organization";
  name: string;
  shortName: string | null;
  emailAddresses: string[];
  telephoneNumbers: string[];
  memberOfOrganizations: string[];
  externalIds: string[] | null;
  sortOrder: number;
  createdAt: string;
}

export interface SspPartyPayload {
  partyUuid?: string | null;
  partyType: "person" | "organization";
  name: string;
  shortName?: string | null;
  emailAddresses?: string[];
  telephoneNumbers?: string[];
  memberOfOrganizations?: string[];
  externalIds?: string[] | null;
  sortOrder: number;
}

export interface SspLocationResponse {
  id: string;
  documentId: string;
  locationUuid: string;
  title: string;
  addressLines: string[];
  city: string | null;
  state: string | null;
  postalCode: string | null;
  country: string | null;
  latitude: number | null;
  longitude: number | null;
  sortOrder: number;
  createdAt: string;
}

export interface SspLocationPayload {
  locationUuid?: string | null;
  title: string;
  addressLines?: string[];
  city?: string | null;
  state?: string | null;
  postalCode?: string | null;
  country?: string | null;
  latitude?: number | null;
  longitude?: number | null;
  sortOrder: number;
}

export interface SspResponsiblePartyResponse {
  id: string;
  documentId: string;
  roleId: string;
  partyUuids: string[];
  ownerType: string | null;
  ownerUuid: string | null;
  createdAt: string;
}

export interface SspResponsiblePartyPayload {
  roleId: string;
  partyUuids: string[];
}

/* ── SSP Control Implementation CRUD types ── */

export type ImplementationStatus =
  | "implemented"
  | "partial"
  | "planned"
  | "alternative"
  | "not-implemented"
  | "not applicable";

export type ControlOrigination =
  | "system-specific"
  | "inherited"
  | "customer-configured"
  | "customer"
  | "customer-responsibility"
  | "hybrid"
  | "shared"
  | "provider-specific"
  | "provider";

export interface AddSspControlImplementationPayload {
  source: string;
  controlId?: string;
  component_id?: string;
  description?: string;
  implementationStatus: ImplementationStatus;
  controlOrigination?: ControlOrigination;
  responsibleRoleIds?: string[];
  setParameters?: { "param-id": string; values: string[]; remarks?: string }[];
  statements?: {
    statementId: string;
    description?: string;
    sortOrder?: number;
  }[];
}

export interface UpdateSspControlImplementationPayload {
  description?: string;
  implementationStatus?: ImplementationStatus;
  controlOrigination?: ControlOrigination;
  responsibleRoleIds?: string[];
  setParameters?: Record<string, unknown>[];
  statements?: { statementId: string; description: string }[];
}

export interface UpdateImplementedRequirementPayload {
  controlId: string;
  implementationStatus: ImplementationStatus;
  origination: ControlOrigination;
  description?: string;
  setParameters?: { "param-id": string; values: string[] }[] | null;
  byComponents?:
    | {
        componentUuid: string;
        description?: string;
        implementationStatus?: ImplementationStatus;
        responsibleRoles?: { "role-id": string }[];
        setParameters?: null;
        inherited?: null;
        satisfied?: null;
        sortOrder?: number;
      }[]
    | null;
  statements?:
    | {
        statementId: string;
        description?: string;
        sortOrder?: number;
      }[]
    | null;
  sortOrder?: number;
}

/* ── SSP Lifecycle ── */

export interface SspLifecycleResponse {
  sspId: string;
  currentStatus: string;
  availableTransitions: string[];
  lastTransitionAt: string | null;
  lastTransitionBy: string | null;
}

export interface TransitionSspPayload {
  targetStatus: string;
  comment?: string;
}

/* ── SSP Full (single-call detail) ── */

export interface SspFullResponse {
  ssp: SspResponse & {
    systemCharacteristics: SystemCharacteristicsResponse | null;
    systemImplementation: {
      users: SspSystemUserResponse[];
      components: SspComponentResponse[];
      inventoryItems: InventoryItemResponse[];
      interconnections: SspInterconnectionResponse[];
    };
    controlImplementations: SspControlImplementationRawResponse[];
    leveragedAuthorizations: LeveragedAuthorizationResponse[];
  };
  informationTypes: InformationTypeResponse[];
  roles: SspRoleResponse[];
  parties: SspPartyResponse[];
  locations: SspLocationResponse[];
  responsibleParties: SspResponsiblePartyResponse[];
  baselineControls: BaselineControlResponse[] | { controls: BaselineControlResponse[] };
}

/** Get full SSP detail in one call — GET /ssps/{id}/full */
export function getSspFull(id: string): Promise<ApiEnvelope<SspFullResponse>> {
  return apiFetch(`/ssps/${id}/full`);
}

/* ── SSP History / Change Tracking ── */

export interface SspHistoryEntry {
  id: string;
  action: string;
  entityType: string;
  entityId: string;
  changedBy: string | null;
  changedAt: string;
  summary: string;
  details: Record<string, unknown> | null;
}

/* ── SSP Export ── */

export interface SspExportPayload {
  format: "json" | "xml" | "yaml";
  profile: string;
  backMatterMode: string;
}

export interface SspExportResponse {
  downloadUrl: string;
  format: string;
  oscalVersion: string;
  generatedAt: string;
}

/* ─────────────────────────────────────────────────────────
   SSP endpoints
───────────────────────────────────────────────────────── */

/** List all SSPs */
export function listSSPs(): Promise<ApiEnvelope<SspSummaryResponse[]>> {
  return apiFetch(`/ssps`);
}

/** Get a single SSP with full detail */
export function getSSP(id: string): Promise<ApiEnvelope<SspResponse>> {
  return apiFetch(`/ssps/${id}`);
}

/** Get the profile linked to an SSP — GET /ssps/{id}/profile */
export function getSspProfile(
  sspId: string,
): Promise<ApiEnvelope<ProfileDetailResponse>> {
  return apiFetch<ApiEnvelope<ProfileDetailResponse>>(`/ssps/${sspId}/profile`);
}

/** Get profile-matched components for an SSP — GET /ssps/{id}/profile-matched-components */
export function getSspProfileMatchedComponents(
  sspId: string,
): Promise<
  ApiEnvelope<
    (ComponentResponse & { cdefTitle?: string; sourceComponentId?: string })[]
  >
> {
  return apiFetch<
    ApiEnvelope<
      (ComponentResponse & { cdefTitle?: string; sourceComponentId?: string })[]
    >
  >(`/ssps/${sspId}/profile-matched-components`);
}

/** Create a new SSP */
export function createSSP(
  payload: CreateSspPayload,
): Promise<ApiEnvelope<SspSummaryResponse>> {
  return apiFetch(`/ssps`, { method: "POST", body: JSON.stringify(payload) });
}

/** Create a new SSP that inherits components from an application. */
export function createSSPFromApplication(
  payload: CreateSspFromApplicationPayload,
): Promise<ApiEnvelope<SspSummaryResponse>> {
  return apiFetch(`/ssps/from-application`, {
    method: "POST",
    body: JSON.stringify(payload),
  });
}

/** Create a new service-level SSP that inherits the chosen components
 *  from a single service. */
export function createSSPFromService(
  payload: CreateSspFromServicePayload,
): Promise<ApiEnvelope<SspSummaryResponse>> {
  return apiFetch(`/ssps/from-service`, {
    method: "POST",
    body: JSON.stringify(payload),
  });
}

/** Update an SSP */
export function updateSSP(
  id: string,
  payload: UpdateSspPayload,
): Promise<ApiEnvelope<SspResponse>> {
  return apiFetch(`/ssps/${id}`, {
    method: "PATCH",
    body: JSON.stringify(payload),
  });
}

/** Delete an SSP (soft delete) */
export function deleteSSP(
  id: string,
  reason?: string,
): Promise<ApiEnvelope<null>> {
  const qs = reason ? `?reason=${encodeURIComponent(reason)}` : "";
  return apiFetch(`/ssps/${id}${qs}`, { method: "DELETE" });
}

/* ── System Characteristics ── */

/** Get system characteristics for an SSP */
export function getSystemCharacteristics(
  sspId: string,
): Promise<ApiEnvelope<SystemCharacteristicsResponse>> {
  return apiFetch(`/ssps/${sspId}/system-characteristics`);
}

/** Set system characteristics for an SSP */
export function setSystemCharacteristics(
  sspId: string,
  payload: SetSystemCharacteristicsPayload,
): Promise<ApiEnvelope<SystemCharacteristicsResponse>> {
  return apiFetch(`/ssps/${sspId}/system-characteristics`, {
    method: "PUT",
    body: JSON.stringify(payload),
  });
}

/* ── Information Types ── */

/** List information types for an SSP */
export function listInformationTypes(
  sspId: string,
): Promise<ApiEnvelope<InformationTypeResponse[]>> {
  return apiFetch(`/ssps/${sspId}/information-types`);
}

/** Add an information type to an SSP */
export function addInformationType(
  sspId: string,
  payload: AddInformationTypePayload,
): Promise<ApiEnvelope<InformationTypeResponse>> {
  return apiFetch(`/ssps/${sspId}/information-types`, {
    method: "POST",
    body: JSON.stringify(payload),
  });
}

/** Edit an information type */
export function patchInformationType(
  sspId: string,
  infoTypeId: string,
  payload: PatchInformationTypePayload,
): Promise<ApiEnvelope<InformationTypeResponse>> {
  return apiFetch(`/ssps/${sspId}/information-types/${infoTypeId}`, {
    method: "PATCH",
    body: JSON.stringify(payload),
  });
}

/** Delete an information type */
export function deleteInformationType(
  sspId: string,
  infoTypeId: string,
): Promise<ApiEnvelope<null>> {
  return apiFetch(`/ssps/${sspId}/information-types/${infoTypeId}`, {
    method: "DELETE",
  });
}

/* ── SSP Components ── */

/** List components for an SSP */
export function listSspComponents(
  sspId: string,
): Promise<ApiEnvelope<SspComponentResponse[]>> {
  return apiFetch(`/ssps/${sspId}/components`);
}

/** Add a component to an SSP */
export function addSspComponent(
  sspId: string,
  payload: AddSspComponentPayload,
): Promise<ApiEnvelope<SspComponentResponse>> {
  return apiFetch(`/ssps/${sspId}/components`, {
    method: "POST",
    body: JSON.stringify(payload),
  });
}

/** Get a single SSP component */
export function getSspComponent(
  sspId: string,
  componentUuid: string,
): Promise<ApiEnvelope<SspComponentResponse>> {
  return apiFetch(`/ssps/${sspId}/components/${componentUuid}`);
}

/** Delete a component from an SSP (soft-delete; shared controls are not removed) */
export function deleteSspComponent(
  sspId: string,
  componentUuid: string,
): Promise<ApiEnvelope<null>> {
  return apiFetch(`/ssps/${sspId}/components/${componentUuid}`, {
    method: "DELETE",
  });
}

/** Edit a component in an SSP */
export function patchSspComponent(
  sspId: string,
  componentId: string,
  payload: PatchSspComponentPayload,
): Promise<ApiEnvelope<SspComponentResponse>> {
  return apiFetch(`/ssps/${sspId}/components/${componentId}`, {
    method: "PATCH",
    body: JSON.stringify(payload),
  });
}

/** List inventory items linked to a specific component (full list; paginated client-side) */
export function listComponentInventoryItems(
  sspId: string,
  componentUuid: string,
): Promise<ApiEnvelope<InventoryItemResponse[]>> {
  return apiFetch(
    `/ssps/${sspId}/components/${componentUuid}/inventory-items`,
  );
}

/** List control implementations linked to a specific component */
export function listComponentControlImplementations(
  sspId: string,
  componentUuid: string,
): Promise<ApiEnvelope<SspControlImplementationRawResponse[]>> {
  return apiFetch(
    `/ssps/${sspId}/components/${componentUuid}/control-implementations`,
  );
}

/** List controls implemented by a specific component (lightweight summary) */
export function listComponentControls(
  sspId: string,
  componentId: string,
): Promise<ApiEnvelope<ComponentControlRow[]>> {
  return apiFetch(`/ssps/${sspId}/components/${componentId}/controls`);
}

/* ── Inventory Items ── */

/** List inventory items for an SSP */
export function listInventoryItems(
  sspId: string,
): Promise<ApiEnvelope<InventoryItemResponse[]>> {
  return apiFetch(`/ssps/${sspId}/inventory-items`);
}

/** Add an inventory item to an SSP */
export function addInventoryItem(
  sspId: string,
  payload: AddInventoryItemPayload,
): Promise<ApiEnvelope<InventoryItemResponse>> {
  return apiFetch(`/ssps/${sspId}/inventory-items`, {
    method: "POST",
    body: JSON.stringify(payload),
  });
}

/** Edit an inventory item */
export function patchInventoryItem(
  sspId: string,
  inventoryItemId: string,
  payload: PatchInventoryItemPayload,
): Promise<ApiEnvelope<InventoryItemResponse>> {
  return apiFetch(`/ssps/${sspId}/inventory-items/${inventoryItemId}`, {
    method: "PATCH",
    body: JSON.stringify(payload),
  });
}

/** Delete an inventory item */
export function deleteInventoryItem(
  sspId: string,
  inventoryItemId: string,
): Promise<ApiEnvelope<null>> {
  return apiFetch(`/ssps/${sspId}/inventory-items/${inventoryItemId}`, {
    method: "DELETE",
  });
}

/* ── SSP Control Implementations ── */

/** List raw control implementations for an SSP (un-flattened, grouped by CI) */
export function listSspControlImplementationsRaw(
  sspId: string,
): Promise<ApiEnvelope<SspControlImplementationRawResponse[]>> {
  return apiFetch(`/ssps/${sspId}/control-implementations`);
}

/** List control implementations for an SSP (flattens implementedRequirements) */
export async function listSspControlImplementations(
  sspId: string,
  controlId?: string,
): Promise<ApiEnvelope<SspControlImplementationResponse[]>> {
  const qs = controlId ? `?control_id=${encodeURIComponent(controlId)}` : "";
  const raw: ApiEnvelope<SspControlImplementationRawResponse[]> =
    await apiFetch(`/ssps/${sspId}/control-implementations${qs}`);
  const flattened: SspControlImplementationResponse[] = [];
  for (const ci of raw.data) {
    for (const ir of ci.implementedRequirements ?? []) {
      flattened.push({
        id: ir.id,
        irUuid: ir.irUuid,
        controlId: ir.controlId,
        componentId: ci.componentId ?? null,
        source: ci.source,
        description: ir.description ?? ci.description,
        implementationStatus: ir.implementationStatus,
        controlOrigination: ir.origination,
        setParameters: ir.setParameters ?? [],
        byComponents: ir.byComponents ?? [],
        statements: ir.statements ?? [],
        sortOrder: ir.sortOrder,
        createdAt: ir.createdAt,
        updatedAt: ir.updatedAt,
        ciId: ci.id,
      });
    }
  }
  return { ...raw, data: flattened };
}

/* ── SSP Implemented Requirements ── */

/** List implemented requirements for a control implementation within an SSP */
export function listSspImplementedRequirements(
  sspId: string,
  ciId: string,
): Promise<ApiEnvelope<ImplementedRequirementResponse[]>> {
  return apiFetch(
    `/ssps/${sspId}/control-implementations/${ciId}/implemented-requirements`,
  );
}

/* ── Asset Resolution ── */

/** Resolve an asset identifier to inventory item, components, and control implementations */
export function resolveAsset(
  sspId: string,
  identifier: string,
): Promise<ApiEnvelope<AssetResolutionResponse>> {
  return apiFetch(
    `/ssps/${sspId}/resolve-asset?identifier=${encodeURIComponent(identifier)}`,
  );
}

/* ── Leveraged Authorizations ── */

/** List leveraged authorizations for an SSP */
export function listLeveragedAuthorizations(
  sspId: string,
): Promise<ApiEnvelope<LeveragedAuthorizationResponse[]>> {
  return apiFetch(`/ssps/${sspId}/leveraged-authorizations`);
}

/** Add a leveraged authorization to an SSP */
export function addLeveragedAuthorization(
  sspId: string,
  payload: AddLeveragedAuthorizationPayload,
): Promise<ApiEnvelope<LeveragedAuthorizationResponse>> {
  return apiFetch(`/ssps/${sspId}/leveraged-authorizations`, {
    method: "POST",
    body: JSON.stringify(payload),
  });
}

/** Update a leveraged authorization */
export function updateLeveragedAuthorization(
  sspId: string,
  authId: string,
  payload: UpdateLeveragedAuthorizationPayload,
): Promise<ApiEnvelope<LeveragedAuthorizationResponse>> {
  return apiFetch(`/ssps/${sspId}/leveraged-authorizations/${authId}`, {
    method: "PATCH",
    body: JSON.stringify(payload),
  });
}

/** Delete a leveraged authorization */
export function deleteLeveragedAuthorization(
  sspId: string,
  authId: string,
): Promise<ApiEnvelope<null>> {
  return apiFetch(`/ssps/${sspId}/leveraged-authorizations/${authId}`, {
    method: "DELETE",
  });
}

export interface LeveragedAuthorizationItem {
  id: string;
  sspId: string;
  providerSspId: string | null;
  leveragedUuid: string;
  title: string;
  status: string;
  sourceSspVersion: string | null;
  sortOrder: number;
  createdAt: string;
  updatedAt: string;
}

export interface CmdbLaResolveResponse {
  consumerSspId: string;
  created: number;
  alreadyExisting: number;
  leveragedAuthorizations: LeveragedAuthorizationItem[];
}

/** Resolve leveraged authorizations automatically from CMDB topology */
export async function resolveLeveragedAuthorizationsFromCmdb(
  sspId: string,
): Promise<CmdbLaResolveResponse> {
  const response = await apiFetch<ApiEnvelope<CmdbLaResolveResponse>>(
    `/ssps/${sspId}/leveraged-authorizations/resolve-from-cmdb`,
    { method: "POST" },
  );
  return response.data;
}

/* ── Roles ── */

/** List roles for an SSP */
export function listSspRoles(
  sspId: string,
): Promise<ApiEnvelope<SspRoleResponse[]>> {
  return apiFetch(`/ssps/${sspId}/roles`);
}

/** Replace all roles for an SSP */
export function replaceSspRoles(
  sspId: string,
  payload: SspRolePayload[],
): Promise<ApiEnvelope<SspRoleResponse[]>> {
  return apiFetch(`/ssps/${sspId}/roles`, {
    method: "PUT",
    body: JSON.stringify(payload),
  });
}

/* ── Parties ── */

/** List parties for an SSP */
export function listSspParties(
  sspId: string,
): Promise<ApiEnvelope<SspPartyResponse[]>> {
  return apiFetch(`/ssps/${sspId}/parties`);
}

/** Replace all parties for an SSP */
export function replaceSspParties(
  sspId: string,
  payload: SspPartyPayload[],
): Promise<ApiEnvelope<SspPartyResponse[]>> {
  return apiFetch(`/ssps/${sspId}/parties`, {
    method: "PUT",
    body: JSON.stringify(payload),
  });
}

/* ── Locations ── */

/** List locations for an SSP */
export function listSspLocations(
  sspId: string,
): Promise<ApiEnvelope<SspLocationResponse[]>> {
  return apiFetch(`/ssps/${sspId}/locations`);
}

/** Replace all locations for an SSP */
export function replaceSspLocations(
  sspId: string,
  payload: SspLocationPayload[],
): Promise<ApiEnvelope<SspLocationResponse[]>> {
  return apiFetch(`/ssps/${sspId}/locations`, {
    method: "PUT",
    body: JSON.stringify(payload),
  });
}

/* ── Responsible Parties ── */

/** List responsible party assignments for an SSP */
export function listSspResponsibleParties(
  sspId: string,
): Promise<ApiEnvelope<SspResponsiblePartyResponse[]>> {
  return apiFetch(`/ssps/${sspId}/responsible-parties`);
}

/** Replace all responsible party assignments for an SSP */
export function replaceSspResponsibleParties(
  sspId: string,
  payload: SspResponsiblePartyPayload[],
): Promise<ApiEnvelope<SspResponsiblePartyResponse[]>> {
  return apiFetch(`/ssps/${sspId}/responsible-parties`, {
    method: "PUT",
    body: JSON.stringify(payload),
  });
}

/* ── Control Implementation CRUD ── */

/** Add a control implementation to an SSP */
export function addSspControlImplementation(
  sspId: string,
  payload: AddSspControlImplementationPayload,
): Promise<ApiEnvelope<SspControlImplementationResponse>> {
  return apiFetch(`/ssps/${sspId}/control-implementations`, {
    method: "POST",
    body: JSON.stringify(payload),
  });
}

/** Update a control implementation */
export function updateSspControlImplementation(
  sspId: string,
  ciId: string,
  payload: UpdateSspControlImplementationPayload,
): Promise<ApiEnvelope<SspControlImplementationResponse>> {
  return apiFetch(`/ssps/${sspId}/control-implementations/${ciId}`, {
    method: "PATCH",
    body: JSON.stringify(payload),
  });
}

/** Update an implemented requirement within a control implementation */
export function updateImplementedRequirement(
  sspId: string,
  ciId: string,
  payload: UpdateImplementedRequirementPayload,
): Promise<ApiEnvelope<SspControlImplementationResponse>> {
  return apiFetch(
    `/ssps/${sspId}/control-implementations/${ciId}/implemented-requirements`,
    { method: "PUT", body: JSON.stringify(payload) },
  );
}

/** Bulk update implemented requirements — PUT /ssps/{id}/control-implementations/{ciId}/implemented-requirements/bulk */
export interface BulkImplementedRequirementPayload {
  controlId: string;
  implementationStatus: ImplementationStatus;
  origination?: ControlOrigination;
  description?: string;
  setParameters?: { "param-id": string; values: string[] }[];
  responsibleRoles?: { "role-id": string; "party-uuids"?: string[] }[];
  byComponents?: {
    componentUuid: string;
    description?: string;
    implementationStatus?: string;
    /** Provider-responsibility export method */
    exportMethod?: "fully_provided" | "shared" | "not_applicable";
    /** Provider "provided" block — used when exportMethod = fully_provided or shared */
    provided?: {
      description?: string;
      responsibleRoles?: { "role-id": string; "party-uuids"?: string[] }[];
    };
    sortOrder: number;
  }[];
  statements?: {
    statementId: string;
    description?: string;
    sortOrder: number;
  }[];
  sortOrder?: number;
}

export function bulkUpdateImplementedRequirements(
  sspId: string,
  ciId: string,
  payload: BulkImplementedRequirementPayload[],
): Promise<ApiEnvelope<SspControlImplementationResponse[]>> {
  return apiFetch(
    `/ssps/${sspId}/control-implementations/${ciId}/implemented-requirements/bulk`,
    {
      method: "PUT",
      body: JSON.stringify(payload),
    },
  );
}

/** Payload for POST /ssps/{sspId}/control-implementations/{ciId}/validation-document/save */
export interface SaveValidationDocumentPayload {
  componentId: string;
  validationDocumentId: string;
  validationDocumentName: string;
}

export interface SspCiValidationDocumentResponse {
  id: string;
  controlImplementationId: string;
  componentId: string;
  validationDocumentId: string;
  validationDocumentName: string;
  createdAt: string;
  updatedAt: string;
}

/** Upsert (add or update) the validation document linked to a (controlImplementation, component) pair */
export function saveValidationDocument(
  sspId: string,
  ciId: string,
  payload: SaveValidationDocumentPayload,
): Promise<ApiEnvelope<SspCiValidationDocumentResponse>> {
  return apiFetch(
    `/ssps/${sspId}/control-implementations/${ciId}/validation-document/save`,
    {
      method: "POST",
      body: JSON.stringify(payload),
    },
  );
}

/** Delete a control implementation */
export function deleteSspControlImplementation(
  sspId: string,
  ciId: string,
): Promise<ApiEnvelope<null>> {
  return apiFetch(`/ssps/${sspId}/control-implementations/${ciId}`, {
    method: "DELETE",
  });
}

/** Scoped set-parameters update for a single implemented-requirement (SSP-level override only) */
export function putSspIrSetParameters(
  sspId: string,
  irId: string,
  setParameters: { "param-id": string; values: string[] }[],
): Promise<ApiEnvelope<unknown>> {
  return apiFetch(`/ssps/${sspId}/implemented-requirements/${irId}/set-parameters`, {
    method: "PUT",
    body: JSON.stringify({ setParameters }),
  });
}

/* ── Asset-Control Matrix (Task 1 + 2) ── */

export type AssetControlImplementationStatus =
  | "implemented"
  | "partial"
  | "planned"
  | "alternative"
  | "not-applicable";

export interface AssetControlMatrixEntry {
  byComponentId: string;
  controlId: string;
  inventoryItemId: string;
  inventoryItemDisplayName: string;
  implementationStatus: AssetControlImplementationStatus;
}

export interface AssetControlImplementationRequest {
  controlId: string;
  inventoryItemId: string;
  implementationStatus: AssetControlImplementationStatus;
}

export function getAssetControlMatrix(
  sspId: string,
  componentId: string,
): Promise<ApiEnvelope<AssetControlMatrixEntry[]>> {
  return apiFetch(`/ssps/${sspId}/components/${componentId}/asset-control-matrix`);
}

export function saveAssetControlMatrix(
  sspId: string,
  componentId: string,
  items: AssetControlImplementationRequest[],
): Promise<ApiEnvelope<AssetControlMatrixEntry[]>> {
  return apiFetch(`/ssps/${sspId}/components/${componentId}/asset-control-matrix`, {
    method: "PUT",
    body: JSON.stringify(items),
  });
}

export function recomputeControlRollups(sspId: string): Promise<ApiEnvelope<number>> {
  return apiFetch(`/ssps/${sspId}/recompute-control-rollups`, { method: "POST" });
}

/* ── Provider Export Authoring (Task 3 + 4) ── */

export type ProviderResponsibility = "fully_implemented" | "shared" | "not_applicable";
export type InheritanceType = "fully_inherited" | "shared_inherited" | "not_applicable";

export interface ExportProvidedBlock {
  uuid?: string;
  description: string;
  responsibleRoles?: { roleId: string }[];
}

export interface ExportResponsibilityBlock {
  uuid?: string;
  description: string;
  providedUuid?: string;
  responsibleRoles?: { roleId: string }[];
}

export interface ComponentExportRow {
  byComponentId: string;
  controlId: string;
  providerResponsibility: ProviderResponsibility;
  inheritanceType: InheritanceType;
  provided: ExportProvidedBlock | null;
  responsibilities: ExportResponsibilityBlock[];
}

export interface ProviderExportSavePayload {
  providerResponsibility: ProviderResponsibility;
  provided: ExportProvidedBlock;
  responsibilities: ExportResponsibilityBlock[];
}

export function getComponentExport(
  sspId: string,
  componentId: string,
): Promise<ApiEnvelope<ComponentExportRow[]>> {
  return apiFetch(`/ssps/${sspId}/components/${componentId}/export`);
}

export type ComponentExportStatus = "fully_implemented" | "shared" | "not_applicable" | "not_authored";

export interface ComponentExportStatusRow {
  controlId: string;
  controlTitle: string;
  exportStatus: ComponentExportStatus;
  byComponentId: string | null;
  providerResponsibility: ProviderResponsibility | null;
  hasProvided: boolean;
  hasResponsibilities: boolean;
}

export function getComponentExportStatus(
  sspId: string,
  componentId: string,
): Promise<ApiEnvelope<ComponentExportStatusRow[]>> {
  return apiFetch(`/ssps/${sspId}/components/${componentId}/export/status`);
}

export function saveControlExport(
  sspId: string,
  componentId: string,
  controls: { controlId: string; export: ProviderExportSavePayload }[],
): Promise<ApiEnvelope<ComponentExportRow[]>> {
  return apiFetch(
    `/ssps/${sspId}/components/${componentId}/export`,
    { method: "PUT", body: JSON.stringify({ controls }) },
  );
}

/* ── SSP Inheritance Discovery (Tasks 5–7) ── */

export interface MatchedAsset {
  inventoryItemId: string;
  displayName: string;
  identifierType: string;
  identifierValue: string;
}

export interface MatchedProviderSsp {
  providerSspId: string;
  title: string;
  lifecycleStatus: "draft" | "in_review" | "published" | "archived";
  isAuthorized: boolean;
  matchedAssetCount: number;
  matchedAssets: MatchedAsset[];
}

export interface EligibleControl {
  controlId: string;
  byComponentId: string;
  componentUuid: string;
  componentTitle: string;
  providerResponsibility: ProviderResponsibility;
  inheritanceType: InheritanceType;
  provided: ExportProvidedBlock | null;
}

export interface InheritableComponent {
  id: string;
  componentUuid: string;
  componentType: string;
  title: string;
  description: string | null;
  selected: boolean;
}

export interface InheritFromSspPayload {
  providerSspId: string;
  componentIds?: string[] | null;
  controlIds?: string[] | null;
}

export interface InheritFromSspResult {
  consumerSspId: string;
  providerSspId: string;
  providerSspTitle: string;
  leveragedAuthorizationId: string;
  componentsCreated: number;
  componentsSkipped: number;
  inventoryItemsCreated: number;
  components: { id: string; title: string; status: string }[];
}

export function listMatchedProviderSsps(
  consumerSspId: string,
): Promise<ApiEnvelope<MatchedProviderSsp[]>> {
  return apiFetch(`/ssps/${consumerSspId}/matched-provider-ssps`);
}

export function listEligibleControls(
  providerSspId: string,
): Promise<ApiEnvelope<EligibleControl[]>> {
  return apiFetch(`/ssps/${providerSspId}/eligible-controls`);
}

export function listInheritableComponents(
  providerSspId: string,
): Promise<ApiEnvelope<InheritableComponent[]>> {
  return apiFetch(`/ssps/${providerSspId}/inheritable-components`);
}

export function inheritFromSsp(
  consumerSspId: string,
  payload: InheritFromSspPayload,
): Promise<ApiEnvelope<InheritFromSspResult>> {
  return apiFetch(`/ssps/${consumerSspId}/inherit-from-ssp`, {
    method: "POST",
    body: JSON.stringify(payload),
  });
}

export function inheritFromApplication(
  sspId: string,
  applicationId: string,
): Promise<ApiEnvelope<{ componentsCreated: number }>> {
  return apiFetch(`/ssps/${sspId}/inherit-from-application`, {
    method: "POST",
    body: JSON.stringify({ applicationId }),
  });
}

/* ── SSP Role Summary ── */

export interface SspProviderSummary {
  exportedControlCount: number;
  consumerCount: number;
  hasUnauthoredExports: boolean;
}

export interface SspConsumerSummary {
  leveragedAuthorizationCount: number;
  pendingSatisfiedCount: number;
}

export interface SspRoleSummaryResponse {
  sspId: string;
  isProvider: boolean;
  isConsumer: boolean;
  isStandalone: boolean;
  providerSummary: SspProviderSummary | null;
  consumerSummary: SspConsumerSummary | null;
}

export function getSspRoleSummary(
  sspId: string,
): Promise<ApiEnvelope<SspRoleSummaryResponse>> {
  return apiFetch(`/ssps/${sspId}/role-summary`);
}

/* ── SSP Satisfied Statement ── */

export interface PatchSatisfiedPayload {
  description: string;
  implementationStatus: string;
  responsibleRoles?: { roleId: string }[];
}

export function patchSatisfiedStatement(
  sspId: string,
  byComponentId: string,
  payload: PatchSatisfiedPayload,
): Promise<ApiEnvelope<ByComponentResponse>> {
  return apiFetch(`/ssps/${sspId}/by-components/${byComponentId}/satisfied`, {
    method: "PATCH",
    body: JSON.stringify(payload),
  });
}

/** Edit the link between a component and a control (description / status / roles only) */
export function patchByComponent(
  sspId: string,
  byComponentId: string,
  payload: PatchByComponentLinkPayload,
): Promise<ApiEnvelope<ByComponentResponse>> {
  return apiFetch(`/ssps/${sspId}/by-components/${byComponentId}`, {
    method: "PATCH",
    body: JSON.stringify(payload),
  });
}

/** Detach a component from a control (the control remains in the SSP) */
export function deleteByComponent(
  sspId: string,
  byComponentId: string,
): Promise<ApiEnvelope<null>> {
  return apiFetch(`/ssps/${sspId}/by-components/${byComponentId}`, {
    method: "DELETE",
  });
}

/* ── SSP Lifecycle ── */

/** Get lifecycle status for an SSP */
export function getSspLifecycle(
  sspId: string,
): Promise<ApiEnvelope<SspLifecycleResponse>> {
  return apiFetch(`/ssps/${sspId}/lifecycle`);
}

/** Transition SSP lifecycle status */
export function transitionSspLifecycle(
  sspId: string,
  payload: TransitionSspPayload,
): Promise<ApiEnvelope<SspLifecycleResponse>> {
  return apiFetch(`/ssps/${sspId}/lifecycle/transition`, {
    method: "POST",
    body: JSON.stringify(payload),
  });
}

/* ── SSP History ── */

/** Get change history for an SSP */
export function getSspHistory(
  sspId: string,
): Promise<ApiEnvelope<SspHistoryEntry[]>> {
  return apiFetch(`/ssps/${sspId}/history`);
}

/* ── SSP Export ── */

/** Export an SSP as OSCAL package */
export function exportSsp(
  sspId: string,
  payload: SspExportPayload,
): Promise<ApiEnvelope<SspExportResponse>> {
  return apiFetch(`/ssps/${sspId}/export`, {
    method: "POST",
    body: JSON.stringify(payload),
  });
}

/* ── Component Library Import ── */

export interface SourceControlImplementationResponse {
  id: string;
  source: string;
  implementedRequirements: {
    id: string;
    controlId: string;
    description: string;
  }[];
}

export interface ImportedControlImplementationResponse {
  id: string;
  ciUuid: string;
  componentId: string;
  source: string;
  implementedRequirements: ImplementedRequirementDetailResponse[];
  sortOrder: number;
  createdAt: string;
  updatedAt: string;
}

export interface ImportComponentResponse extends SspComponentResponse {
  sourceControlImplementations: SourceControlImplementationResponse[];
  controlImplementations: ImportedControlImplementationResponse[];
}

export type ImportComponentsResponse = ImportComponentResponse[];

/** Import one or more components from the component library into an SSP */
export function importComponentToSsp(
  sspId: string,
  componentIds: string[],
): Promise<ApiEnvelope<ImportComponentsResponse>> {
  return apiFetch(`/ssps/${sspId}/import-components`, {
    method: "POST",
    body: JSON.stringify({ component_ids: componentIds }),
  });
}

/* ── SSP System Users ── */

export interface SspSystemUserResponse {
  id: string;
  userUuid: string;
  title: string;
  shortName: string | null;
  description: string | null;
  roleIds: string[];
  authorizedPrivileges: {
    title: string;
    description: string;
    functions_performed: string[];
  }[];
  sortOrder: number;
  createdAt: string;
  updatedAt: string;
}

export interface AddSspSystemUserPayload {
  title: string;
  shortName?: string;
  description?: string;
  roleIds?: string[];
  authorizedPrivileges?: {
    title: string;
    description?: string;
    functions_performed?: string[];
  }[];
  sortOrder: number;
}

export interface UpdateSspSystemUserPayload {
  title?: string;
  shortName?: string;
  description?: string;
  roleIds?: string[];
  authorizedPrivileges?: {
    title: string;
    description?: string;
    functions_performed?: string[];
  }[];
}

/** List system users for an SSP */
export function listSspSystemUsers(
  sspId: string,
): Promise<ApiEnvelope<SspSystemUserResponse[]>> {
  return apiFetch(`/ssps/${sspId}/system-users`);
}

/** Add a system user to an SSP */
export function addSspSystemUser(
  sspId: string,
  payload: AddSspSystemUserPayload,
): Promise<ApiEnvelope<SspSystemUserResponse>> {
  return apiFetch(`/ssps/${sspId}/system-users`, {
    method: "POST",
    body: JSON.stringify(payload),
  });
}

/** Update a system user */
export function updateSspSystemUser(
  sspId: string,
  userId: string,
  payload: UpdateSspSystemUserPayload,
): Promise<ApiEnvelope<SspSystemUserResponse>> {
  return apiFetch(`/ssps/${sspId}/system-users/${userId}`, {
    method: "PATCH",
    body: JSON.stringify(payload),
  });
}

/** Delete a system user */
export function deleteSspSystemUser(
  sspId: string,
  userId: string,
): Promise<ApiEnvelope<null>> {
  return apiFetch(`/ssps/${sspId}/system-users/${userId}`, {
    method: "DELETE",
  });
}

/* ── SSP Interconnections ── */

export interface SspInterconnectionResponse {
  id: string;
  interconnectionUuid: string;
  title: string;
  description: string | null;
  direction: "incoming" | "outgoing" | "incoming-outgoing";
  remoteSystemName: string | null;
  responsibleRoles: { role_id: string; party_uuids: string[] }[];
  protocols: {
    name: string;
    title?: string;
    port_ranges: { start: number; end: number; transport: string }[];
  }[];
  sortOrder: number;
  createdAt: string;
  updatedAt: string;
}

export interface AddSspInterconnectionPayload {
  title: string;
  description?: string;
  direction: "incoming" | "outgoing" | "incoming-outgoing";
  remoteSystemName?: string;
  responsibleRoles?: { role_id: string; party_uuids?: string[] }[];
  protocols?: {
    name: string;
    title?: string;
    port_ranges?: { start: number; end: number; transport: string }[];
  }[];
  sortOrder: number;
}

export interface UpdateSspInterconnectionPayload {
  title?: string;
  description?: string;
  direction?: "incoming" | "outgoing" | "incoming-outgoing";
  remoteSystemName?: string;
  responsibleRoles?: { role_id: string; party_uuids?: string[] }[];
  protocols?: {
    name: string;
    title?: string;
    port_ranges?: { start: number; end: number; transport: string }[];
  }[];
}

/** List interconnections for an SSP */
export function listSspInterconnections(
  sspId: string,
): Promise<ApiEnvelope<SspInterconnectionResponse[]>> {
  return apiFetch(`/ssps/${sspId}/interconnections`);
}

/** Add an interconnection to an SSP */
export function addSspInterconnection(
  sspId: string,
  payload: AddSspInterconnectionPayload,
): Promise<ApiEnvelope<SspInterconnectionResponse>> {
  return apiFetch(`/ssps/${sspId}/interconnections`, {
    method: "POST",
    body: JSON.stringify(payload),
  });
}

/** Update an interconnection */
export function updateSspInterconnection(
  sspId: string,
  connId: string,
  payload: UpdateSspInterconnectionPayload,
): Promise<ApiEnvelope<SspInterconnectionResponse>> {
  return apiFetch(`/ssps/${sspId}/interconnections/${connId}`, {
    method: "PATCH",
    body: JSON.stringify(payload),
  });
}

/** Delete an interconnection */
export function deleteSspInterconnection(
  sspId: string,
  connId: string,
): Promise<ApiEnvelope<null>> {
  return apiFetch(`/ssps/${sspId}/interconnections/${connId}`, {
    method: "DELETE",
  });
}

/* ── SSP Baseline Controls (read-only from Catalog → Profile chain) ── */

export interface BaselineControlParameter {
  paramId: string;
  label: string;
  catalogValues: string[];
  profileValues: string[];
  constraints: string[];
}

export interface BaselineControlParameterSource {
  label?: string;
  usage?: string;
  class?: string;
  clazz?: string;
  constraints?: unknown[];
  guidelines?: { prose?: string }[];
  values?: string[];
}

export interface BaselineControlResolvedParameter {
  param_id?: string;
  paramId?: string;
  is_overridden?: boolean;
  isOverridden?: boolean;
  catalog?: BaselineControlParameterSource | null;
  profile?: BaselineControlParameterSource | null;
  component_definition?: BaselineControlParameterSource | null;
  componentDefinition?: BaselineControlParameterSource | null;
}

export interface BaselineControlPart {
  id?: string;
  partId: string;
  name: string;
  ns?: string;
  prose?: string;
  resolved_prose?: string;
  sortOrder?: number;
  parts?: BaselineControlPart[];
}

export interface BaselineControlDetails {
  title?: string;
  control_id?: string;
  source_href?: string;
  source_catalog_id?: string;
  parts?: BaselineControlPart[];
  params?: {
    id?: string;
    paramId: string;
    label?: string;
    clazz?: string;
    guidelines?: { prose: string }[];
    values?: string[];
    sortOrder?: number;
  }[];
}

export interface BaselineControlResponse {
  controlId: string;
  control_id?: string;
  controlTitle?: string;
  title?: string;
  family?: string;
  parameters?: BaselineControlParameter[];
  params?: BaselineControlResolvedParameter[];
  controlDetails?: BaselineControlDetails | null;
  implementationStatus?: string;
  implementedRequirements?: ImplementedRequirementDetailResponse[];
  enhancements?: unknown[];
}

/** Get baseline controls for an SSP (resolved from Profile) */
export function listBaselineControls(
  sspId: string,
  params?: { startDate?: string; endDate?: string },
): Promise<ApiEnvelope<BaselineControlResponse[]>> {
  const qs = params
    ? "?" + new URLSearchParams(
        Object.fromEntries(
          Object.entries(params).filter(([, v]) => !!v) as [string, string][]
        )
      ).toString()
    : "";
  return apiFetch(`/ssps/${sspId}/baseline-controls${qs}`);
}

/* ── SSP Implemented Requirements (nested under control-implementations) ── */

export interface ByComponentPayload {
  componentUuid: string;
  description?: string;
  implementationStatus?: ImplementationStatus;
  responsibleRoles?: { role_id: string; party_uuids?: string[] }[];
  inherited?: {
    provided_uuid: string;
    description?: string;
  } | null;
  satisfied?: {
    responsibility_uuid: string;
    description?: string;
  } | null;
  sortOrder: number;
}

export interface ByComponentResponse {
  id: string | null;
  componentUuid: string;
  componentName?: string;
  description: string | null;
  implementationStatus: string | null;
  responsibleRoles: { role_id: string; party_uuids: string[] }[];
  inherited: { uuid?: string; provided_uuid?: string; "provided-uuid"?: string; "leveraged-authorization-uuid"?: string; description: string | null } | null;
  satisfied: { uuid?: string; responsibility_uuid?: string; "responsibility-uuid"?: string; description: string | null; "responsible-roles"?: { roleId: string }[]; implementationStatus?: string } | null;
  provided: { uuid?: string; description: string | null; "responsible-roles"?: { roleId: string }[] } | null;
  providerResponsibility: ProviderResponsibility | null;
  responsibilities: ExportResponsibilityBlock[] | null;
  inheritanceStatus: string | null;
  pendingSatisfied?: boolean;
  /** Linked validation document — populated once one is saved via saveValidationDocument() */
  validationDocumentId?: string | null;
  validationDocumentName?: string | null;
  /** Document name as returned by the baseline-controls endpoint */
  linkedDocument?: string | null;
  sortOrder: number;
  createdAt: string;
  updatedAt: string;
}

export interface AddImplementedRequirementPayload {
  controlId: string;
  implementationStatus: ImplementationStatus;
  origination?: string;
  description?: string;
  setParameters?: { "param-id": string; values: string[] }[];
  byComponents?: ByComponentPayload[];
  statements?: {
    statementId: string;
    description: string;
    sortOrder: number;
  }[];
  sortOrder: number;
}

export interface PatchImplementedRequirementPayload {
  implementationStatus?: ImplementationStatus;
  origination?: string;
  description?: string;
  setParameters?: { "param-id": string; values: string[] }[];
  byComponents?: ByComponentPayload[];
  statements?: {
    statementId: string;
    description: string;
    sortOrder: number;
  }[];
}

export interface ImplementedRequirementDetailResponse {
  id: string;
  irUuid: string;
  controlId: string;
  implementationStatus: string;
  origination: string | null;
  description: string | null;
  setParameters: { "param-id": string; values: string[] }[];
  byComponents: ByComponentResponse[];
  statements: {
    id: string;
    statementId: string;
    description: string;
    sortOrder: number;
  }[];
  sortOrder: number;
  createdAt: string;
  updatedAt: string;
}

/** List implemented requirements for a control implementation */
export function listImplementedRequirements(
  sspId: string,
  ciId: string,
): Promise<ApiEnvelope<ImplementedRequirementDetailResponse[]>> {
  return apiFetch(
    `/ssps/${sspId}/control-implementations/${ciId}/implemented-requirements`,
  );
}

/** Add an implemented requirement */
export function addImplementedRequirement(
  sspId: string,
  ciId: string,
  payload: AddImplementedRequirementPayload,
): Promise<ApiEnvelope<ImplementedRequirementDetailResponse>> {
  return apiFetch(
    `/ssps/${sspId}/control-implementations/${ciId}/implemented-requirements`,
    {
      method: "POST",
      body: JSON.stringify(payload),
    },
  );
}

/** Update an implemented requirement */
export function updateImplementedRequirement_ssp(
  sspId: string,
  ciId: string,
  irId: string,
  payload: PatchImplementedRequirementPayload,
): Promise<ApiEnvelope<ImplementedRequirementDetailResponse>> {
  return apiFetch(
    `/ssps/${sspId}/control-implementations/${ciId}/implemented-requirements/${irId}`,
    {
      method: "PATCH",
      body: JSON.stringify(payload),
    },
  );
}

/** Delete an implemented requirement */
export function deleteImplementedRequirement(
  sspId: string,
  ciId: string,
  irId: string,
): Promise<ApiEnvelope<null>> {
  return apiFetch(
    `/ssps/${sspId}/control-implementations/${ciId}/implemented-requirements/${irId}`,
    {
      method: "DELETE",
    },
  );
}

/* ─────────────────────────────────────────────────────────
   SSP Supporting Evidence
───────────────────────────────────────────────────────── */

export interface SspSupportingEvidence {
  id: string;
  evidenceId: string;
  title: string;
  description?: string;
  tags?: string[];
  hasFile?: boolean;
  fileName?: string;
  fileSize?: number;
  contentType?: string;
  createdAt: string;
  updatedAt: string;
}

/** List all supporting evidence for an SSP */
export function listSspSupportingEvidence(
  sspId: string,
): Promise<ApiEnvelope<SspSupportingEvidence[]>> {
  return apiFetch(`/ssps/${sspId}/supporting-evidence`);
}

/** Create supporting evidence and link to SSP */
export function createSspSupportingEvidence(
  sspId: string,
  payload: { title: string; description?: string; tags?: string[] },
): Promise<ApiEnvelope<SspSupportingEvidence>> {
  return apiFetch(`/ssps/${sspId}/supporting-evidence`, {
    method: "POST",
    body: JSON.stringify(payload),
  });
}

/** Upload a file to linked supporting evidence */
export function uploadSspSupportingEvidenceFile(
  sspId: string,
  evidenceId: string,
  file: File,
): Promise<ApiEnvelope<SspSupportingEvidence>> {
  const formData = new FormData();
  formData.append("file", file);
  return apiFetch(`/ssps/${sspId}/supporting-evidence/${evidenceId}/upload`, {
    method: "POST",
    headers: {},
    body: formData,
  });
}

/** Delete supporting evidence link */
export function deleteSspSupportingEvidence(
  sspId: string,
  evidenceId: string,
): Promise<ApiEnvelope<null>> {
  return apiFetch(`/ssps/${sspId}/supporting-evidence/${evidenceId}`, {
    method: "DELETE",
  });
}

/* ─────────────────────────────────────────────────────────
   POA&M Types
───────────────────────────────────────────────────────── */

export interface ApiPoam {
  id: string;
  title?: string;
  documentId: string;
  importSspHref: string;
  sspName?: string;
  itemCount?: number;
  items?: ApiPoamItem[];
  createdAt: string;
  updatedAt: string;
}

/** CVSS-style POA&M item severity. */
export type PoamSeverity = "critical" | "high" | "medium" | "low";

/** Automated finding — IM8 control + its catalog statement (snake_case JSON blob). */
export interface PoamFrameworkControl {
  control_id: string;
  statement?: string | null;
}

/** Automated finding — per-asset frozen OPA evidence (snake_case JSON blob). */
export interface PoamAffectedAsset {
  asset_uuid: string;
  hostname?: string | null;
  component_name?: string | null;
  status?: string | null;
  command?: string | null;
  evidence_data?: string | null;
  scanned_at?: string | null;
}

export interface ApiPoamItem {
  id: string;
  poamItemUuid: string;
  assessmentPlanId?: string;
  title: string;
  description: string;
  controlId?: string;
  controlTitle?: string;
  statementId?: string | null;
  statementDescription?: string | null;
  status?: string;
  /** CVSS-style severity, user-assigned. null until set. */
  severity?: PoamSeverity | null;
  relatedObservations: string[] | null;
  relatedRisks: string[] | null;
  relatedFindings: string[] | null;
  /** Automated findings only — the failing tech control's catalog statement. */
  techControlStatement?: string | null;
  /** Automated findings only — IM8 controls this tech control maps to. */
  affectedFrameworkControls?: string[] | null;
  /** Automated findings only — IM8 controls with their statements. */
  frameworkControls?: PoamFrameworkControl[] | null;
  /** Automated findings only — CMDB asset UUIDs the tech control failed on. */
  affectedAssetUuids?: string[] | null;
  /** Automated findings only — per-asset frozen evidence (command, output, scan time). */
  affectedAssets?: PoamAffectedAsset[] | null;
  sortOrder: number;
  createdAt: string;
  updatedAt: string;
}

export interface PoamPlan {
  planId: string;
  planTitle: string;
  itemCount: number;
  createdAt: string;
}

export interface ApiRemediation {
  id: string;
  poamItemId: string;
  planTitle: string;
  planDescription: string;
  milestones: string | null;
  scheduledCompletionDate: string | null;
  actualCompletionDate: string | null;
  status: RemediationStatusApi;
  createdAt: string;
  updatedAt: string;
}

export type RemediationStatusApi =
  | "open"
  | "in_progress"
  | "completed"
  | "delayed"
  | "cancelled";

export interface ApiMilestone {
  id?: string;
  name: string;
  due?: string;
  completed?: boolean;
}

export interface ApiRiskAdjustment {
  id: string;
  poamItemId: string;
  adjustmentType:
    | "false_positive"
    | "risk_acceptance"
    | "operational_requirement";
  justification: string;
  adjustedRiskLevel: string | null;
  approvalWorkflowId: string | null;
  approvedBy: string;
  approvedAt: string;
  effectiveFrom: string;
  effectiveUntil: string | null;
  createdAt: string;
  updatedAt: string;
}

export interface ApiMonthlySubmissionResult {
  poamId: string;
  snapshotId: string;
  totalItems: number;
  openItems: number;
  completedItems: number;
  submittedAt: string;
}

/* ─────────────────────────────────────────────────────────
   POA&M Milestone helpers
───────────────────────────────────────────────────────── */

export function parseMilestones(raw: string | null): ApiMilestone[] {
  if (!raw) return [];
  try {
    const parsed = JSON.parse(raw);
    return Array.isArray(parsed) ? parsed : [];
  } catch {
    return [];
  }
}

export function serializeMilestones(milestones: ApiMilestone[]): string {
  return JSON.stringify(milestones);
}

/* ─────────────────────────────────────────────────────────
   POA&M endpoints
───────────────────────────────────────────────────────── */

/** List POA&Ms */
export function listPoams(): Promise<{ status: string; data: ApiPoam[] }> {
  return apiFetch<{ status: string; data: ApiPoam[] }>(`/poams`);
}

/** Create POA&M */
export function createPoam(
  title: string,
  importSspHref?: string,
): Promise<{ status: string; data: ApiPoam }> {
  return apiFetch<{ status: string; data: ApiPoam }>("/poams", {
    method: "POST",
    body: JSON.stringify({
      title,
      ...(importSspHref ? { importSspHref } : {}),
    }),
  });
}

/** Response shape for POA&M generation */
export interface GeneratePoamResponse {
  poam_id: string;
  created: number;
  updated: number;
  closed: number;
  items: ApiPoamItem[];
}

/** Generate / refresh POA&M items from a plan's rejected findings.
 *  SSP-scoped + idempotent — safe to call repeatedly. Empty body. */
export function generatePoam(
  planId: string,
): Promise<{ status: string; data: GeneratePoamResponse }> {
  return apiFetch<{ status: string; data: GeneratePoamResponse }>(
    `/poams/generate/${planId}`,
    { method: "POST" },
  );
}

/** Get POA&M Detail (includes items array) */
export function getPoam(
  id: string,
): Promise<{ status: string; data: ApiPoam }> {
  return apiFetch<{ status: string; data: ApiPoam }>(`/poams/${id}`);
}

/** List POA&M Items */
export function listPoamItems(
  poamId: string,
  opts: {
    planId?: string;
    status?: string;
    overdueOnly?: boolean;
  } = {},
): Promise<{ status: string; data: ApiPoamItem[] }> {
  const params = new URLSearchParams({
    ...(opts.planId ? { plan_id: opts.planId } : {}),
    ...(opts.status ? { status: opts.status } : {}),
    ...(opts.overdueOnly
      ? { overdue_only: "true" }
      : { overdue_only: "false" }),
  });
  return apiFetch<{ status: string; data: ApiPoamItem[] }>(
    `/poams/${poamId}/items?${params}`,
  );
}

/** Get POA&M items filtered by a specific assessment plan */
export function getPoamItemsByPlan(
  poamId: string,
  planId: string,
): Promise<{ status: string; data: ApiPoamItem[] }> {
  return apiFetch<{ status: string; data: ApiPoamItem[] }>(
    `/poams/${poamId}/items/by-plan/${planId}`,
  );
}

/** List assessment plans linked to a POA&M (for filter dropdown) */
export function listPoamPlans(
  poamId: string,
): Promise<{ status: string; data: PoamPlan[] }> {
  return apiFetch<{ status: string; data: PoamPlan[] }>(
    `/poams/${poamId}/plans`,
  );
}

/** Create POA&M Item */
export function createPoamItem(
  poamId: string,
  payload: {
    title: string;
    description?: string;
    relatedObservations?: string[];
    relatedRisks?: string[];
    relatedFindings?: string[];
  },
): Promise<{ status: string; data: ApiPoamItem }> {
  return apiFetch<{ status: string; data: ApiPoamItem }>(
    `/poams/${poamId}/items`,
    {
      method: "POST",
      body: JSON.stringify(payload),
    },
  );
}

/** localStorage key for caching remediation data */
function remediationCacheKey(poamId: string, itemId: string): string {
  return `poam_remediation_${poamId}_${itemId}`;
}

/** Get Item Remediation — tries GET first, falls back to localStorage cache */
export async function getRemediation(
  poamId: string,
  itemId: string,
): Promise<{ status: string; data: ApiRemediation } | null> {
  try {
    const result = await apiFetch<{ status: string; data: ApiRemediation }>(
      `/poams/${poamId}/items/${itemId}/remediation`,
    );
    // Cache successful response
    localStorage.setItem(
      remediationCacheKey(poamId, itemId),
      JSON.stringify(result),
    );
    return result;
  } catch {
    // GET not supported or failed — try localStorage cache
    try {
      const cached = localStorage.getItem(remediationCacheKey(poamId, itemId));
      if (cached) return JSON.parse(cached);
    } catch {
      /* ignore parse errors */
    }
    return null;
  }
}

/** Update Remediation (create or update) — caches response for offline reads */
export async function updateRemediation(
  poamId: string,
  itemId: string,
  payload: {
    planTitle: string;
    planDescription: string;
    milestones: string;
    scheduledCompletionDate: string | null;
    actualCompletionDate?: string | null;
    status: RemediationStatusApi;
    assigneeUserIds?: string[];
  },
): Promise<{ status: string; data: ApiRemediation }> {
  const result = await apiFetch<{ status: string; data: ApiRemediation }>(
    `/poams/${poamId}/items/${itemId}/remediation`,
    { method: "PUT", body: JSON.stringify(payload) },
  );
  // Cache the response so getRemediation can return it when GET is unavailable
  localStorage.setItem(
    remediationCacheKey(poamId, itemId),
    JSON.stringify(result),
  );
  return result;
}

/** Transition a POA&M item to a new stage */
export function transitionPoamItemStatus(
  poamId: string,
  itemId: string,
  payload: { targetStatus: string; reason?: string },
): Promise<{ status: string; data: ApiPoamItem }> {
  return apiFetch<{ status: string; data: ApiPoamItem }>(
    `/poams/${poamId}/items/${itemId}/status-transitions`,
    { method: "POST", body: JSON.stringify(payload) },
  );
}

/** Set / update a POA&M item's severity (critical | high | medium | low). */
export function updatePoamItemSeverity(
  poamId: string,
  itemId: string,
  severity: PoamSeverity,
): Promise<{ status: string; data: ApiPoamItem }> {
  return apiFetch<{ status: string; data: ApiPoamItem }>(
    `/poams/${poamId}/items/${itemId}/severity`,
    { method: "PUT", body: JSON.stringify({ severity }) },
  );
}

/** Submit Risk Adjustment */
export function submitRiskAdjustment(
  poamId: string,
  itemId: string,
  payload: {
    adjustmentType:
      | "false_positive"
      | "risk_acceptance"
      | "operational_requirement";
    justification: string;
    adjustedRiskLevel: string | null;
    effectiveFrom: string;
    effectiveUntil?: string | null;
  },
): Promise<{ status: string; data: ApiRiskAdjustment }> {
  return apiFetch<{ status: string; data: ApiRiskAdjustment }>(
    `/poams/${poamId}/items/${itemId}/risk-adjustments`,
    { method: "POST", body: JSON.stringify(payload) },
  );
}

/** Submit Monthly Report */
export function submitMonthly(
  poamId: string,
): Promise<{ status: string; data: ApiMonthlySubmissionResult }> {
  return apiFetch<{ status: string; data: ApiMonthlySubmissionResult }>(
    `/poams/${poamId}/submit-monthly`,
    { method: "POST", body: JSON.stringify({}) },
  );
}

/* ─────────────────────────────────────────────────────────
   Assessment Plan Types
───────────────────────────────────────────────────────── */

export type AssessmentPlanStatus =
  | "draft"
  | "in_review"
  | "approved"
  | "rejected";

export interface ApiReviewedControlStatement {
  id: string;
  statementId: string;
  statementDescription: string;
  assessmentMethods: string[];
  sortOrder: number;
  createdAt: string;
  updatedAt: string;
}

export interface ApiReviewedControl {
  id: string;
  controlId: string;
  controlDescription: string;
  implementationStatus?: string;
  componentName?: string;
  byComponents?: { componentUuid: string; componentName?: string }[];
  statements: ApiReviewedControlStatement[];
  sortOrder: number;
  createdAt: string;
  updatedAt: string;
}

export interface ApiAssessmentPlan {
  id: string;
  documentId: string;
  title?: string;
  importSspHref: string;
  sspTitle?: string;
  status?: AssessmentPlanStatus;
  lifecycleStatus?: string;
  assessmentMethods?: string[];
  assessors?: string[];
  reviewedControls?: ApiReviewedControl[];
  subjects?: ApiAssessmentSubject[];
  activities?: ApiAssessmentActivity[];
  assets?: ApiAssessmentAsset[];
  tasks?: ApiAssessmentTask[];
  task?: {
    id: string;
    startDate: string;
    endDate: string;
    assignees?: {
      id: string;
      userId: string;
      email: string;
      displayName: string;
      assignedAt: string;
    }[];
    createdAt: string;
    updatedAt: string;
  };
  controlCount?: number;
  startDate?: string;
  endDate?: string;
  owningOrgUnitId?: string | null;
  /**
   * Set by the backend only after "Generate POA&M" runs for the plan.
   * When present, the plan is considered finished and is hidden from the
   * Review and Execution lists.
   */
  reviewCompletedAt?: string | null;
  createdAt: string;
  updatedAt: string;
}

export interface ApiAssessmentSubject {
  id: string;
  subjectType: string;
  description: string;
  includeSubjects: string;
  excludeSubjects: string;
  sortOrder: number;
  createdAt: string;
  updatedAt: string;
}

export interface ApiAssessmentActivity {
  id: string;
  activityUuid: string;
  title: string;
  description: string;
  method: "interview" | "examine" | "test";
  relatedControls: string;
  sortOrder: number;
  createdAt: string;
  updatedAt: string;
}

export interface ApiAssessmentAsset {
  id: string;
  assetUuid: string;
  assetType: string;
  title: string;
  description: string;
  componentDetails: string;
  sortOrder: number;
  createdAt: string;
  updatedAt: string;
}

export interface ApiTaskAssignee {
  id: string;
  userId: string;
  email: string;
  displayName: string;
  assignedAt: string;
}

export interface ApiAssessmentTask {
  id: string;
  taskUuid?: string;
  taskType?: "action" | "milestone";
  title?: string;
  description?: string;
  startDate: string;
  endDate: string;
  timing?: string;
  assignees?: ApiTaskAssignee[];
  dependencies?: string[] | null;
  associatedActivities?: string[] | null;
  sortOrder?: number;
  createdAt: string;
  updatedAt: string;
}

/* ─────────────────────────────────────────────────────────
   Assessment Plan endpoints
───────────────────────────────────────────────────────── */

/** List Assessment Plans */
export function listAssessmentPlans(): Promise<{
  status: string;
  data: ApiAssessmentPlan[];
}> {
  return apiFetch<{ status: string; data: ApiAssessmentPlan[] }>(
    `/assessment-plans`,
  );
}

/** Create Assessment Plan */
export function createAssessmentPlan(
  title: string,
  importSspHref?: string,
  owningOrgUnitId?: string,
): Promise<{ status: string; data: ApiAssessmentPlan }> {
  return apiFetch<{ status: string; data: ApiAssessmentPlan }>(
    "/assessment-plans",
    {
      method: "POST",
      body: JSON.stringify({
        title,
        ...(importSspHref ? { importSspHref } : {}),
        ...(owningOrgUnitId ? { owningOrgUnitId } : {}),
      }),
    },
  );
}

/** Get Assessment Plan Detail (returns full plan with reviewedControls, activities, tasks, etc.) */
export function getAssessmentPlan(
  id: string,
): Promise<{ status: string; data: ApiAssessmentPlan }> {
  return apiFetch<{ status: string; data: ApiAssessmentPlan }>(
    `/assessment-plans/${id}/detail`,
  );
}

/** Add Subject to Assessment Plan */
export function addAssessmentSubject(
  planId: string,
  payload: {
    subjectType: string;
    description: string;
    includeSubjects: string;
    excludeSubjects: string;
  },
): Promise<{ status: string; data: ApiAssessmentSubject }> {
  return apiFetch<{ status: string; data: ApiAssessmentSubject }>(
    `/assessment-plans/${planId}/subjects`,
    { method: "POST", body: JSON.stringify(payload) },
  );
}

/** Add Activity to Assessment Plan */
export function addAssessmentActivity(
  planId: string,
  payload: {
    title: string;
    description: string;
    method: "interview" | "examine" | "test";
    relatedControls: string;
  },
): Promise<{ status: string; data: ApiAssessmentActivity }> {
  return apiFetch<{ status: string; data: ApiAssessmentActivity }>(
    `/assessment-plans/${planId}/activities`,
    { method: "POST", body: JSON.stringify(payload) },
  );
}

/** Add Asset to Assessment Plan */
export function addAssessmentAsset(
  planId: string,
  payload: {
    assetType: string;
    title: string;
    description: string;
    componentDetails?: string;
  },
): Promise<{ status: string; data: ApiAssessmentAsset }> {
  return apiFetch<{ status: string; data: ApiAssessmentAsset }>(
    `/assessment-plans/${planId}/assets`,
    { method: "POST", body: JSON.stringify(payload) },
  );
}

/** Schedule Task for Assessment Plan */
export function addAssessmentTask(
  planId: string,
  payload: {
    userIds: string[];
    startDate: string;
    endDate: string;
  },
): Promise<{ status: string; data: ApiAssessmentTask }> {
  return apiFetch<{ status: string; data: ApiAssessmentTask }>(
    `/assessment-plans/${planId}/tasks`,
    { method: "PUT", body: JSON.stringify(payload) },
  );
}

/** Save Schedule & Assignees for Assessment Plan */
export function saveAssessmentSchedule(
  planId: string,
  payload: {
    assigneeUserIds: string[];
    startDate: string;
    endDate: string;
  },
): Promise<{ status: string; data: ApiAssessmentPlan }> {
  return apiFetch<{ status: string; data: ApiAssessmentPlan }>(
    `/assessment-plans/${planId}/schedule`,
    { method: "PUT", body: JSON.stringify(payload) },
  );
}

/* ─────────────────────────────────────────────────────────
   Users
───────────────────────────────────────────────────────── */

export interface ApiUser {
  id: string;
  email: string;
  givenName: string;
  familyName: string;
  displayName: string;
  oidcSubject: string;
  isActive: boolean;
  platformRoles: string[];
  createdAt: string;
}

/** GET /api/v1/users — list all platform users */
export function listUsers(
  owningOrgUnitId?: string,
  role?: string,
): Promise<ApiEnvelope<ApiUser[]>> {
  const qs = new URLSearchParams();
  if (role) qs.set("role", role);
  const q = qs.toString();
  return apiFetch<ApiEnvelope<ApiUser[]>>(`/users${q ? `?${q}` : ""}`);
}

/* ─────────────────────────────────────────────────────────
   Assessment Plan – Method
───────────────────────────────────────────────────────── */

/** PUT /api/v1/assessment-plans/:planId/method — set assessment method */
export function setAssessmentMethod(
  planId: string,
  assessmentMethod: string,
): Promise<ApiEnvelope<unknown>> {
  return apiFetch<ApiEnvelope<unknown>>(`/assessment-plans/${planId}/method`, {
    method: "PUT",
    body: JSON.stringify({ assessmentMethod }),
  });
}

/** Get Reviewed Controls for an Assessment Plan */
export function getAssessmentPlanReviewedControls(
  planId: string,
): Promise<{ status: string; data: ApiReviewedControl[] }> {
  return apiFetch<{ status: string; data: ApiReviewedControl[] }>(
    `/assessment-plans/${planId}/reviewed-controls`,
  );
}

/** Save Reviewed Controls to Assessment Plan (PUT — replaces all) */
export function saveReviewedControls(
  planId: string,
  controls: {
    controlId: string;
    controlDescription: string;
    implementationStatus?: string;
    componentName?: string;
    statements: {
      statementId: string;
      statementDescription: string;
      assessmentMethods: string[];
    }[];
  }[],
): Promise<{ status: string; data: ApiReviewedControl[] }> {
  return apiFetch<{ status: string; data: ApiReviewedControl[] }>(
    `/assessment-plans/${planId}/reviewed-controls`,
    { method: "PUT", body: JSON.stringify({ controls }) },
  );
}

/** Create Assessment Plan (full payload — single POST) */
export function createAssessmentPlanFull(payload: {
  title: string;
  importSspHref?: string;
  controls: {
    controlId: string;
    controlDescription: string;
    statements: {
      statementId: string;
      statementDescription: string;
      assessmentMethods: string[];
    }[];
  }[];
  assigneeUserIds: string[];
  startDate: string;
  endDate: string;
}): Promise<{ status: string; data: ApiAssessmentPlan }> {
  return apiFetch<{ status: string; data: ApiAssessmentPlan }>(
    "/assessment-plans/full",
    { method: "POST", body: JSON.stringify(payload) },
  );
}

/** Submit Assessment Plan (POST /assessment-plans/{id}/submit — no payload) */
export function submitAssessmentPlan(
  planId: string,
  orgId?: string,
): Promise<{ status: string; data: ApiAssessmentPlan }> {
  const url = orgId
    ? `/assessment-plans/${planId}/submit?orgId=${orgId}`
    : `/assessment-plans/${planId}/submit`;
  return apiFetch<{ status: string; data: ApiAssessmentPlan }>(
    url,
    { method: "POST" },
  );
}

/** Update Assessment Plan Status */
export function updateAssessmentPlanStatus(
  planId: string,
  targetStatus: AssessmentPlanStatus,
  comments?: string,
): Promise<{ status: string; data: ApiAssessmentPlan }> {
  return apiFetch<{ status: string; data: ApiAssessmentPlan }>(
    `/assessment-plans/${planId}/status`,
    {
      method: "PUT",
      body: JSON.stringify({ targetStatus, comments: comments ?? "" }),
    },
  );
}

/* ── Trigger Evaluator types ── */

export interface TriggerEvalControl {
  control_id: string;
  control_status: string;
  evidence?: unknown[];
}

export interface TriggerEvalAssetResult {
  metadata: {
    catalog_name?: string;
    data_collected_date?: string;
    uuid?: string;
    props?: Array<{ name: string; value: string }>;
  };
  controls: TriggerEvalControl[];
}

/** Evaluator response: a single asset object, an array of them, or null/empty. */
export type TriggerEvalResponse =
  | TriggerEvalAssetResult
  | TriggerEvalAssetResult[]
  | null;

/** Trigger OPA evaluation for an assessment plan. Synchronous proxy — can take 30–60s. */
export function triggerAssessmentPlan(
  planId: string,
  opts?: { esIndex?: string; signal?: AbortSignal },
): Promise<{ status: string; data: TriggerEvalResponse }> {
  const idx = opts?.esIndex?.trim();
  const qs = idx ? `?es_index=${encodeURIComponent(idx)}` : "";
  return apiFetch<{ status: string; data: TriggerEvalResponse }>(
    `/assessment-plans/${planId}/trigger${qs}`,
    { method: "POST", signal: opts?.signal },
  );
}

/* ── Trigger payload preview types ── */

export interface TriggerPayloadAsset {
  asset_id: string;
  hostname?: string;
  ip_address?: string;
  es_index?: string | null;
  controls: Array<{ control_id: string; params: unknown }>;
}

export interface TriggerPayloadComponent {
  component_type: string;
  component_name: string;
  assets: TriggerPayloadAsset[];
}

export interface TriggerPayload {
  assessment_id: string;
  assessment_name: string;
  assessment_date: string;
  components: TriggerPayloadComponent[];
}

/** Preview the outbound payload that would be sent to the evaluator (no side effects). */
export function getAssessmentPlanTriggerPayload(
  planId: string,
  opts?: { esIndex?: string },
): Promise<{ status: string; data: TriggerPayload }> {
  const idx = opts?.esIndex?.trim();
  const qs = idx ? `?es_index=${encodeURIComponent(idx)}` : "";
  return apiFetch<{ status: string; data: TriggerPayload }>(
    `/assessment-plans/${planId}/trigger-payload${qs}`,
    { method: "GET" },
  );
}

/* ─────────────────────────────────────────────────────────
   Assessment Execution
───────────────────────────────────────────────────────── */

export interface PendingControlStatement {
  id: string;
  statementId: string;
  statementDescription: string;
  assessmentMethods: string[];
  sortOrder: number;
  createdAt: string;
  updatedAt: string;
  executionMethod?: "opa" | "declarative" | "declarative_evidence" | null;
  state?: string;
  verdict?: string;
  justification?: string | null;
  /** Assessor's follow-up question — from execution-view `note`/`reviewer_note`. */
  note?: string | null;
  /** Reviewer asked for evidence as part of this follow-up. */
  needsEvidence?: boolean;
  /** Execution-view `review_status`. Lag protection: backend may set this to
   *  "follow_up_resolved" before `state` flips off "FOLLOWUP". */
  reviewStatus?: string | null;
  /** Full follow-up timeline; oldest → newest. */
  iterations?: AssessmentExecutionViewIteration[];
  evidenceFiles?: AssessmentExecutionViewEvidenceFile[];
}

export interface PendingControl {
  id: string;
  controlId: string;
  controlDescription: string;
  statements: PendingControlStatement[];
}

/** Get pending controls for assessment execution */
export function getPendingControls(
  planId: string,
  source = "openstack",
): Promise<ApiEnvelope<PendingControl[]>> {
  return apiFetch<ApiEnvelope<PendingControl[]>>(
    `/assessment-execution/${planId}/pending-controls?source=${encodeURIComponent(source)}`,
  );
}

export interface MappedOpaControlResult {
  opaControlId: string;
  catalogName: string | null;
  controlStatus: string;
  evidence: Record<string, unknown>[];
  assetUuid: string | null;
  assetName: string | null;
  assetIdentifierType: string | null;
}

export interface AssessmentExecutionViewTechControl {
  tech_control_id: string;
  tech_control_description?: string;
  status: string;
  command?: string;
  evidence_data?: string;
}

export interface AssessmentExecutionViewAsset {
  asset_uuid: string;
  asset_hostname: string;
  ip_address?: string;
  component_name: string;
  scanned_at?: string;
  controls: AssessmentExecutionViewTechControl[];
}

export interface AssessmentExecutionViewEvidenceFile {
  supporting_evidence_id: string;
  title: string;
  file_name: string;
}

/** One round of executor save + reviewer action on a statement.
 *  Backend returns iterations[] oldest → newest; the latest entry mirrors
 *  the top-level statement fields. */
export interface AssessmentExecutionViewIteration {
  iteration_number: number;
  statement_execution_id?: string;
  execution_method?: "opa" | "declarative" | "declarative_evidence" | string | null;
  state?: string;
  justification?: string | null;
  saved_at?: string | null;
  review_status?: string | null;
  reviewer_note?: string | null;
  needs_evidence?: boolean;
  reviewed_at?: string | null;
}

export interface AssessmentExecutionViewStatement {
  statement_id: string;
  statement_label: string;
  statement_description: string;
  assessment_methods: string[];
  needs_evidence: boolean;
  verdict: string;
  execution_method?: "opa" | "declarative" | "declarative_evidence" | null;
  state?: "NEW" | "AWAITING_REVIEW" | "FOLLOWUP" | "FOLLOWUP_RESOLVED" | "REVIEWED" | string;
  iteration?: number;
  statement_execution_id?: string;
  justification?: string | null;
  evidence_files?: AssessmentExecutionViewEvidenceFile[];
  assets?: AssessmentExecutionViewAsset[];
  review_status?: string | null;
  note?: string | null;
  /** Reviewer's follow-up message (preferred name; some responses use `note`). */
  reviewer_note?: string | null;
  /** Full per-round history (oldest → newest). Latest entry mirrors the
   *  top-level state/justification/review_status/reviewer_note fields. */
  iterations?: AssessmentExecutionViewIteration[];
  reviewed_by?: string | null;
  reviewed_at?: string | null;
}

export interface AssessmentExecutionViewControl {
  plan_control_id: string;
  control_id: string;
  control_description: string;
  component_names: string[];
  mode: "AUTOMATED" | "MANUAL" | string;
  statements: AssessmentExecutionViewStatement[];
}

export interface AssessmentExecutionViewResponse {
  plan_id: string;
  ssp_id: string;
  review_complete?: boolean;
  controls: AssessmentExecutionViewControl[];
}

export interface ControlExecutionStatusResponse {
  planControlId: string;
  controlId: string;
  controlDescription: string | null;
  statements: PendingControlStatement[];
  sortOrder: number;
  implementationStatus: string | null;
  controlType: "automated" | "manual";
  executable: boolean;
  executed: boolean;
  followUpPending: boolean;
  followUpQuestion: string | null;
  opaOverallStatus: "passed" | "failed" | "pending" | null;
  opaResults: MappedOpaControlResult[];
  determination: boolean | null;
  justification: string | null;
  executedAt: string | null;
  executionMode?: string;
  executionViewAssets?: AssessmentExecutionViewAsset[];
  executionViewStatements?: AssessmentExecutionViewStatement[];
}

/** Get execution view for an assessment plan */
export function getAssessmentExecutionView(
  planId: string,
): Promise<ApiEnvelope<AssessmentExecutionViewResponse>> {
  return apiFetch<ApiEnvelope<AssessmentExecutionViewResponse>>(
    `/assessment-plans/${planId}/execution-view`,
  );
}

/** Get unified control execution statuses (automated + manual) for a plan */
export function getControlExecutionStatuses(
  planId: string,
  moduleName?: string,
): Promise<ApiEnvelope<ControlExecutionStatusResponse[]>> {
  const url = moduleName
    ? `/assessment-execution/${planId}/control-statuses?moduleName=${moduleName}`
    : `/assessment-execution/${planId}/control-statuses`;
  return apiFetch<ApiEnvelope<ControlExecutionStatusResponse[]>>(url);
}

/* ── Assessment Result types ── */

export interface ResultViolation {
  summary: string;
  role?: string;
  project?: string;
  user_name?: string;
  [key: string]: string | undefined;
}

export interface OpaResult {
  opaControlId: string;
  title: string;
  component: string;
  status: string;
  passed: boolean;
  hasData: boolean;
  controlStatement: string;
  evidenceCounts: Record<string, number>;
  violations: ResultViolation[];
  passingEvidence: Record<string, unknown>[];
}

export interface ResultControlCheck {
  controlId: string;
  controlTitle: string;
  statementId?: string;
  statementDescription?: string;
  status: string;
  passed: boolean;
  opaResults: OpaResult[];
}

export interface AssessmentResultResponse {
  source: string;
  collectedAt: string;
  requested: string[];
  foundCount: number;
  controls: ResultControlCheck[];
  notFound: string[];
}

/** Execute assessment controls and generate results */
export function executeAssessmentControls(
  controlIds: string[],
  source = "openstack",
): Promise<AssessmentResultResponse> {
  return apiFetch<AssessmentResultResponse>(
    `/assessment-results/controls?source=${encodeURIComponent(source)}`,
    { method: "POST", body: JSON.stringify(controlIds) },
  );
}

/** Get assessment results controls (GET — reads existing results) */
export function getAssessmentResultControls(
  source = "openstack",
): Promise<AssessmentResultResponse> {
  return apiFetch<AssessmentResultResponse>(
    `/assessment-results/controls?source=${encodeURIComponent(source)}`,
  );
}

/* ── Declarative Assessment types ── */

export interface DeclarativeStatementPayload {
  planControlId: string;
  statementId: string;
  determination: boolean;
  justification: string;
}

export interface DeclarativePayload {
  statements: DeclarativeStatementPayload[];
}

/** Submit declarative assessment for a plan */
export function submitDeclarativeAssessment(
  planId: string,
  payload: DeclarativePayload,
): Promise<ApiEnvelope<unknown>> {
  return apiFetch<ApiEnvelope<unknown>>(
    `/assessment-execution/${planId}/declarative`,
    { method: "POST", body: JSON.stringify(payload) },
  );
}

/* ── Assessment Execution — View with Review Status ── */

export type ReviewStatus =
  | "pending"
  | "approved"
  | "accepted"
  | "rejected"
  | "follow_up_raised"
  | "follow_up_resolved"
  | null;

export interface ExecutionResponse {
  id: string;
  planControlId: string;
  controlId: string;
  controlDescription: string;
  statementId: string;
  /** Human-readable statement label (e.g. "ac-1_smt") for display; statementId stays the UUID for API calls. */
  statementLabel?: string;
  statementDescription: string;
  executionMethod: string;
  determination: boolean;
  justification: string;
  executedBy: string;
  executedAt: string;
  reviewStatus: ReviewStatus;
  editable: boolean;
  followUpQuestion: string | null;
  createdAt: string;
  updatedAt: string;
}

/** Get all executions for a plan (System Owner view) */
export function getExecutions(
  planId: string,
): Promise<ApiEnvelope<ExecutionResponse[]>> {
  return apiFetch<ApiEnvelope<ExecutionResponse[]>>(
    `/assessment-execution/${planId}`,
  );
}

/* ── Assessment Review (Assessor) ── */

export interface ReviewHistoryEntry {
  id: string;
  reviewStatus: string;
  followUpQuestion: string | null;
  followUpResponse: string | null;
  followUpRespondedBy: string | null;
  followUpRespondedAt: string | null;
  followUpEvidenceIds?: string[];
  reviewedBy: string;
  reviewedAt: string;
}

export interface ExecutionReviewItem {
  execution: ExecutionResponse;
  currentReviewStatus: string;
  reviewHistory: ReviewHistoryEntry[];
}

/** Get all executions for review (Assessor view) */
export function getExecutionReviews(
  planId: string,
  moduleName?: string,
): Promise<ApiEnvelope<ExecutionReviewItem[]>> {
  const url = moduleName
    ? `/assessment-execution/${planId}/review?moduleName=${moduleName}`
    : `/assessment-execution/${planId}/review`;
  return apiFetch<ApiEnvelope<ExecutionReviewItem[]>>(url);
}

export interface ReviewSubmitItem {
  statementExecutionId: string;
  status: "accepted" | "approved" | "follow_up_raised" | "rejected";
  reviewStatus?: "pending" | "approved" | "follow_up_raised";
  reviewComments?: string;
  followUpQuestion?: string;
  evidenceIds?: string[];
  needsEvidence?: boolean;
}

export interface ReviewSubmitPayload {
  reviews: ReviewSubmitItem[];
}

/** Submit reviews (Assessor) */
export function submitExecutionReviews(
  planId: string,
  payload: ReviewSubmitPayload,
): Promise<ApiEnvelope<unknown>> {
  return apiFetch<ApiEnvelope<unknown>>(
    `/assessment-execution/${planId}/review`,
    { method: "POST", body: JSON.stringify(payload) },
  );
}

export interface StatementReviewPayload {
  action: "accept" | "reject" | "followup";
  needs_evidence?: boolean;
  note?: string;
}

export interface StatementReviewResponse {
  review_id: string;
  statement_execution_id: string;
  plan_id: string;
  statement_id: string;
  iteration: number;
  state: "REVIEWED" | "FOLLOWUP" | string;
  review_status: "accepted" | "rejected" | "follow_up_raised" | string;
  needs_evidence: boolean;
  note: string | null;
  reviewed_by: string;
  reviewed_at: string;
}

/** Review a single statement from the execution-view workflow */
export function reviewAssessmentPlanStatement(
  planId: string,
  statementId: string,
  payload: StatementReviewPayload,
): Promise<ApiEnvelope<StatementReviewResponse>> {
  return apiFetch<ApiEnvelope<StatementReviewResponse>>(
    `/assessment-plans/${planId}/statements/${statementId}/review`,
    { method: "POST", body: JSON.stringify(payload) },
  );
}

/** Finalize all reviews for a plan */
export interface FinalizeReviewsResponse {
  assessmentResultsId: string | null;
  planId: string;
  reviewsFinalized: number;
}

export function finalizeReviews(
  planId: string,
): Promise<ApiEnvelope<FinalizeReviewsResponse>> {
  return apiFetch<ApiEnvelope<FinalizeReviewsResponse>>(
    `/assessment-execution/${planId}/finalize-reviews`,
    { method: "POST" },
  );
}

/* ── LLM Validation ── */

export interface LlmControlValidationResult {
  controlId: string;
  status: "pass" | "fail" | "partial";
  confidence: number;
  reasoning: string;
  recommendations: string[];
}

export interface LlmValidationResponse {
  results: LlmControlValidationResult[];
}

export function validateControlsWithLlm(
  planId: string,
  controlIds: string[],
  executionId?: string,
): Promise<ApiEnvelope<LlmValidationResponse>> {
  return apiFetch<ApiEnvelope<LlmValidationResponse>>(
    `/assessment-execution/${planId}/controls/validate-llm`,
    {
      method: "POST",
      body: JSON.stringify({ controlIds, ...(executionId ? { executionId } : {}) }),
    },
  );
}

/* ── Follow-Ups (System Owner) ── */

export interface FollowUpItem {
  id: string;
  statementExecutionId: string;
  reviewStatus: string;
  needsEvidence: boolean;
  followUpQuestion: string;
  followUpResponse: string | null;
  reviewedBy: string;
  reviewedAt: string;
  createdAt?: string;
}

/** Get pending follow-ups for a plan */
export function getFollowUps(
  planId: string,
): Promise<ApiEnvelope<FollowUpItem[]>> {
  return apiFetch<ApiEnvelope<FollowUpItem[]>>(
    `/assessment-execution/${planId}/follow-ups`,
  );
}

/** Respond to a follow-up */
export function respondToFollowUp(
  planId: string,
  executionId: string,
  followUpResponse: string,
  evidenceIds?: string[],
  determination?: boolean,
  justification?: string,
): Promise<ApiEnvelope<unknown>> {
  return apiFetch<ApiEnvelope<unknown>>(
    `/assessment-execution/${planId}/follow-ups/${executionId}`,
    {
      method: "PUT",
      body: JSON.stringify({
        followUpResponse: followUpResponse,
        ...(determination !== undefined ? { determination } : {}),
        ...(justification ? { justification } : {}),
        ...(evidenceIds?.length ? { evidenceIds } : {}),
      }),
    },
  );
}

/** Get declarative results for a plan */
export function getDeclarativeResults(
  planId: string,
  moduleName?: string,
): Promise<ApiEnvelope<ExecutionReviewItem[]>> {
  const url = moduleName
    ? `/assessment-results/plans/${planId}/declarative-results?moduleName=${moduleName}`
    : `/assessment-results/plans/${planId}/declarative-results`;
  return apiFetch<ApiEnvelope<ExecutionReviewItem[]>>(url);
}

/** Get reviewed controls for a plan */
export function getReviewedControls(
  planId: string,
): Promise<ApiEnvelope<PendingControl[]>> {
  return apiFetch<ApiEnvelope<PendingControl[]>>(
    `/assessment-plans/${planId}/reviewed-controls`,
  );
}

/* ═══════════════════════════════════════════════════════════════════
   Supporting Evidence APIs
   ═══════════════════════════════════════════════════════════════════ */

export interface SupportingEvidence {
  id: string;
  title: string;
  tags: string[];
  hasFile: boolean;
  fileName: string | null;
  fileSize: number | null;
  contentType: string | null;
  createdAt: string;
  updatedAt: string;
  createdBy: string;
}

export interface EvidenceLink {
  id: string;
  assessmentPlanId: string;
  statementId: string;
  supportingEvidenceId: string;
  evidenceName?: string;
  linkedBy: string;
  createdAt: string;
}

/** Create a new supporting evidence entry */
export function createSupportingEvidence(payload: {
  title: string;
  tags?: string[];
  description?: string;
  owningOrgUnitId?: string;
}): Promise<ApiEnvelope<SupportingEvidence>> {
  return apiFetch(`/content-registry/supporting-evidence`, {
    method: "POST",
    body: JSON.stringify(payload),
  });
}

/** Upload file to an existing evidence entry */
export async function uploadEvidenceFile(
  evidenceId: string,
  file: File,
): Promise<ApiEnvelope<SupportingEvidence>> {
  const formData = new FormData();
  formData.append("file", file);
  const res = await fetch(
    `${window.BE_URL || ""}/api/v1/content-registry/supporting-evidence/${evidenceId}/upload`,
    {
      method: "POST",
      headers: { ...getAuthHeaders() },
      body: formData,
    },
  );
  if (!res.ok) {
    const body = await res.text().catch(() => "");
    // Try to surface a JSON API error; otherwise show a brief, clean message.
    let detail = briefHttpError(res.status, body, res.statusText);
    try {
      const parsed = JSON.parse(body);
      const first = parsed?.errors?.[0];
      if (first?.detail || first?.message) {
        detail = (first.detail || first.message).split(/[.(]/)[0].trim();
      }
    } catch {
      /* not JSON — keep brief message */
    }
    throw new Error(detail);
  }
  return res.json();
}

export interface EvidencePreview {
  /** Blob URL for iframe/img (empty string when not previewable). */
  url: string;
  /** False for binary files the browser can't render inline (offer download). */
  previewable: boolean;
  /** File name resolved from the Content-Disposition header, if present. */
  fileName: string;
}

/** File extensions the browser can render inline (images render as-is). */
const IMAGE_EXTS = ["png", "jpg", "jpeg", "gif", "webp", "svg", "bmp", "ico"];
/** Text-like extensions — re-wrapped as text/plain so the iframe shows them. */
const TEXT_EXTS = [
  "yaml", "yml", "json", "xml", "csv", "txt", "md", "log",
  "html", "htm", "ini", "conf", "sh", "js", "ts", "css",
];
/** Binary formats browsers can't preview inline — offer download instead. */
const BINARY_EXTS = [
  "xlsx", "xls", "docx", "doc", "pptx", "ppt",
  "zip", "rar", "7z", "tar", "gz", "bin", "exe",
];

/** Preview evidence file — classifies the response so callers can render
 *  PDFs/images natively, text files as text, and offer a download fallback
 *  for binary formats the browser can't show inline. */
export async function previewEvidenceFile(
  evidenceId: string,
): Promise<EvidencePreview> {
  const res = await fetch(
    `${window.BE_URL || ""}/api/v1/content-registry/supporting-evidence/${evidenceId}/preview`,
    {
      headers: { ...getAuthHeaders() },
    },
  );
  if (!res.ok) {
    throw new Error(`Preview failed: ${res.status} ${res.statusText}`);
  }
  const blob = await res.blob();
  const type = (blob.type || "").toLowerCase();

  // Resolve the file name/extension from Content-Disposition when available.
  // Filenames may be malformed or percent-encoded; never let a bad header
  // throw and take down the whole preview — fall back to the raw match.
  const disposition = res.headers.get("content-disposition") || "";
  let fileName = "";
  const nameMatch = /filename\*?=(?:UTF-8'')?"?([^";]+)"?/i.exec(disposition);
  if (nameMatch) {
    const raw = nameMatch[1].trim();
    try {
      fileName = decodeURIComponent(raw);
    } catch {
      fileName = raw;
    }
  }
  const ext = (fileName.split(".").pop() || "").toLowerCase();

  // A 0-byte response can't be previewed meaningfully — send it to download.
  if (blob.size === 0) {
    return { url: URL.createObjectURL(blob), previewable: false, fileName };
  }

  // PDFs and images render natively in an iframe/img — keep them as-is.
  if (type === "application/pdf" || ext === "pdf") {
    return { url: URL.createObjectURL(blob), previewable: true, fileName };
  }
  if (type.startsWith("image/") || IMAGE_EXTS.includes(ext)) {
    return { url: URL.createObjectURL(blob), previewable: true, fileName };
  }

  // Known binary formats can't preview inline — signal a download fallback.
  // A blob URL is still returned so simple callers can fall back to it.
  // Also catch Office/archive MIME types (xlsx/docx/zip) that arrive without
  // a Content-Disposition, so the extension check alone wouldn't flag them.
  const looksBinaryMime =
    type.includes("officedocument") ||
    type.includes("ms-excel") ||
    type.includes("ms-powerpoint") ||
    type.includes("msword") ||
    type.includes("zip") ||
    type.includes("compressed");
  if (BINARY_EXTS.includes(ext) || looksBinaryMime) {
    return { url: URL.createObjectURL(blob), previewable: false, fileName };
  }

  // Text-like files (YAML, JSON, XML, CSV, TXT) often arrive as
  // application/octet-stream or a MIME the browser won't display inline, so
  // the iframe downloads them instead of previewing. Re-wrap as text/plain
  // so they render as text in the preview modal.
  const looksText =
    type.startsWith("text/") ||
    type.includes("json") ||
    type.includes("xml") ||
    type.includes("yaml") ||
    type === "application/octet-stream" ||
    type === "" ||
    TEXT_EXTS.includes(ext);
  if (looksText) {
    // Reading the bytes can fail for a detached/huge blob; if it does, fall
    // back to the raw blob URL rather than failing the whole preview.
    try {
      const textBlob = new Blob([await blob.arrayBuffer()], {
        type: "text/plain",
      });
      return {
        url: URL.createObjectURL(textBlob),
        previewable: true,
        fileName,
      };
    } catch {
      return { url: URL.createObjectURL(blob), previewable: true, fileName };
    }
  }

  // Unknown type — hand back the raw blob URL and let the browser try.
  return { url: URL.createObjectURL(blob), previewable: true, fileName };
}

/** Delete a supporting evidence entry */
export function deleteSupportingEvidence(
  evidenceId: string,
): Promise<ApiEnvelope<null>> {
  return apiFetch(`/content-registry/supporting-evidence/${evidenceId}`, {
    method: "DELETE",
  });
}

/** List all supporting evidence */
export function listSupportingEvidence(): Promise<
  ApiEnvelope<SupportingEvidence[]>
> {
  return apiFetch(`/content-registry/supporting-evidence`);
}

/** Search supporting evidence by title */
export function searchSupportingEvidence(
  q: string,
): Promise<ApiEnvelope<SupportingEvidence[]>> {
  return apiFetch(
    `/content-registry/supporting-evidence/search?q=${encodeURIComponent(q)}`,
  );
}

/** Link evidence to a statement in a plan */
export function linkEvidenceToStatement(
  planId: string,
  payload: { statementId: string; evidenceIds: string[] },
): Promise<ApiEnvelope<EvidenceLink[]>> {
  return apiFetch(`/assessment-execution/${planId}/evidence`, {
    method: "POST",
    body: JSON.stringify(payload),
  });
}

/** Get all evidence linked to a plan */
export function getLinkedEvidence(
  planId: string,
  moduleName?: string,
): Promise<ApiEnvelope<EvidenceLink[]>> {
  const url = moduleName
    ? `/assessment-execution/${planId}/evidence?moduleName=${moduleName}`
    : `/assessment-execution/${planId}/evidence`;
  return apiFetch(url);
}

/** Unlink evidence from a statement */
export function unlinkEvidence(
  planId: string,
  evidenceId: string,
  statementId: string,
): Promise<ApiEnvelope<null>> {
  return apiFetch(
    `/assessment-execution/${planId}/evidence/${evidenceId}?statementId=${encodeURIComponent(statementId)}`,
    { method: "DELETE" },
  );
}

/** Get a single supporting evidence by ID */
export function getSupportingEvidence(
  id: string,
): Promise<ApiEnvelope<SupportingEvidenceResponse>> {
  return apiFetch(`/content-registry/supporting-evidence/${id}`);
}

/** Update supporting evidence */
export function updateSupportingEvidence(
  id: string,
  payload: { title?: string; description?: string; tags?: string[] },
): Promise<ApiEnvelope<SupportingEvidenceResponse>> {
  return apiFetch(`/content-registry/supporting-evidence/${id}`, {
    method: "PATCH",
    body: JSON.stringify(payload),
  });
}

/** Upload file to supporting evidence (using apiFetch) */
export function uploadSupportingEvidenceFile(
  id: string,
  file: File,
): Promise<ApiEnvelope<SupportingEvidenceResponse>> {
  const formData = new FormData();
  formData.append("file", file);
  return apiFetch(`/content-registry/supporting-evidence/${id}/upload`, {
    method: "POST",
    body: formData,
    headers: {},
  });
}

/** Download supporting evidence file URL */
export function downloadSupportingEvidenceFile(id: string): string {
  return `${BASE_URL}/content-registry/supporting-evidence/${id}/download`;
}

/** Preview supporting evidence file URL.
 *  No token in the query string — callers fetch this URL with the
 *  Authorization header (see PreviewModal / DiagramPreviewModal). */
export function previewSupportingEvidenceFile(id: string): string {
  return `${BASE_URL}/content-registry/supporting-evidence/${id}/preview`;
}

/** Search tags by prefix */
export function searchEvidenceTags(
  prefix: string,
): Promise<ApiEnvelope<string[]>> {
  return apiFetch(
    `/content-registry/supporting-evidence/tags/search?prefix=${encodeURIComponent(prefix)}`,
  );
}

/** Submit declarative + evidence execution */
export interface DeclarativeEvidenceStatement {
  planControlId: string;
  statementId: string;
  determination: boolean;
  evidenceIds: string[];
  // justification: string;
}

export interface DeclarativeEvidenceExecution {
  id: string;
  planControlId: string;
  statementId: string;
  executionMethod: string;
  determination: boolean;
  justification: string;
  executedBy: string;
  executedAt: string;
  reviewStatus: string | null;
  followUpQuestion: string | null;
  createdAt: string;
  updatedAt: string;
}

export function submitDeclarativeEvidence(
  planId: string,
  payload: { statements: DeclarativeEvidenceStatement[] },
): Promise<ApiEnvelope<DeclarativeEvidenceExecution[]>> {
  return apiFetch(`/assessment-execution/${planId}/declarative-evidence`, {
    method: "POST",
    body: JSON.stringify(payload),
  });
}

/** Execute a single statement — supports three payload types:
 *  1. Automated (OPA): empty body or {"execution_method": "opa"}
 *  2. Manual declarative: {"execution_method": "declarative", "justification": "..."}
 *  3. Manual declarative+evidence: {"execution_method": "declarative_evidence", "justification": "..."} (requires evidence to be linked first)
 */
export interface StatementExecutionPayload {
  execution_method?: "opa" | "declarative" | "declarative_evidence";
  justification?: string;
}

export interface StatementExecutionResponse {
  statement_execution_id: string;
  iteration: number;
  state: string;
  verdict: string;
  assets?: Array<{
    component_name: string;
    asset_uuid: string;
    controls?: Array<{
      tech_control_id: string;
      status: string;
      evidence_data?: unknown;
    }>;
  }>;
}

export function executeStatement(
  planId: string,
  statementId: string,
  payload?: StatementExecutionPayload,
): Promise<ApiEnvelope<StatementExecutionResponse>> {
  return apiFetch<ApiEnvelope<StatementExecutionResponse>>(
    `/assessment-plans/${planId}/statements/${statementId}/execution`,
    { method: "POST", body: JSON.stringify(payload || {}) },
  );
}

/** Finalize execution — auto-create execution records from OPA results */
export function finalizeExecution(
  planId: string,
  source = "openstack",
): Promise<ApiEnvelope<unknown>> {
  return apiFetch<ApiEnvelope<unknown>>(
    `/assessment-execution/${planId}/finalize-execution?source=${encodeURIComponent(source)}`,
    { method: "POST" },
  );
}

/** Submit all executions for review — transitions drafts to pending, creates OPA execution records */
export function submitForReview(
  planId: string,
  source = "openstack",
): Promise<ApiEnvelope<unknown>> {
  return apiFetch<ApiEnvelope<unknown>>(
    `/assessment-execution/${planId}/submit-for-review?source=${encodeURIComponent(source)}`,
    { method: "POST" },
  );
}

/** Read whether every execution + review for a plan is finalised. Drives the
 *  "All Controls Completed" overlay on the Execution and Review pages. */
export interface ReviewReadinessResponse {
  allReviewed: boolean;
  totalExecutions: number;
  reviewedExecutions: number;
  pendingExecutions: number;
  followUpPending: number;
  blockers: string[];
}

export function getReviewReadiness(
  planId: string,
): Promise<ApiEnvelope<ReviewReadinessResponse>> {
  return apiFetch<ApiEnvelope<ReviewReadinessResponse>>(
    `/assessment-execution/${planId}/review-readiness`,
  );
}

/* ─────────────────────────────────────────────────────────
   Content Registry - Governance Documents & Supporting Evidence
───────────────────────────────────────────────────────── */

export type GovernanceDocumentType =
  | "POLICY"
  | "STANDARD"
  | "GUIDELINE"
  | "PROCEDURE";

export type DocumentLifecycleStatus =
  | "DRAFT"
  | "IN_REVIEW"
  | "APPROVED"
  | "PUBLISHED"
  | "REVIEW_OVERDUE"
  | "RETIRED";

export type AllowedAction =
  | "UPDATE"
  | "UPLOAD"
  | "DELETE_FILE"
  | "DELETE"
  | "SUBMIT"
  | "APPROVE"
  | "REJECT"
  | "NEW_VERSION"
  | "RETIRE";

export type ContentCategory = "governance" | "evidence";

// Version info for governance documents
export interface VersionInfo {
  versionNumber: number;
  fileName: string | null;
  contentType: string | null;
  fileSize: number | null;
  hasFile: boolean;
  createdAt: string;
  createdBy: string;
  createdByName?: string;
}

// Full governance document response
export interface GovernanceDocumentResponse {
  id: string;
  documentType: GovernanceDocumentType;
  title: string;
  description: string | null;
  owningOrgUnitId: string | null;
  owningOrgUnitName?: string | null;
  status: DocumentLifecycleStatus;
  currentVersion: number;
  requiresReview: boolean;
  reviewCycleDays: number | null;
  reviewAlertDays?: number | null;
  reviewDate: string | null;
  daysUntilReview: number | null;
  reviewDueSoon?: boolean;
  rejectionReason: string | null;
  rejectedAt: string | null;
  rejectedBy?: string | null;
  rejectedByName?: string | null;
  publishedAt: string | null;
  publishedBy: string | null;
  publishedByName?: string | null;
  retiredAt?: string | null;
  retiredBy?: string | null;
  retiredByName?: string | null;
  retirementReason?: string | null;
  currentVersionInfo: VersionInfo | null;
  versions: VersionInfo[];
  createdAt: string;
  createdBy?: string;
  createdByName?: string;
  updatedAt: string;
  updatedBy?: string;
  updatedByName?: string;
  allowedActions?: AllowedAction[];
}

export interface ReviewSummaryResponse {
  total: number;
  pending: number;
  approved: number;
  rejected: number;
  byType?: Record<string, { total: number; pending: number; approved: number; rejected: number }>;
}

// Summary response for list views
export interface GovernanceDocumentSummaryResponse {
  id: string;
  documentType: GovernanceDocumentType;
  title: string;
  owningOrgUnitId: string | null;
  status: DocumentLifecycleStatus;
  currentVersion: number;
  versionCount: number;
  requiresReview: boolean;
  reviewDate: string | null;
  daysUntilReview: number | null;
  reviewDueSoon?: boolean;
  hasFile: boolean;
  createdAt: string;
  createdBy?: string;
  createdByName?: string;
  updatedAt: string;
  updatedBy?: string;
  updatedByName?: string;
}

// Supporting Evidence response
export interface SupportingEvidenceResponse {
  id: string;
  title: string;
  description: string | null;
  tags: string[];
  fileName: string | null;
  contentType: string | null;
  fileSize: number | null;
  hasFile: boolean;
  createdAt: string;
  updatedAt: string;
}

// Content item (union type for search results)
export interface ContentItemResponse {
  category: ContentCategory;
  id: string;
  title: string;
  description: string | null;
  hasFile: boolean;
  createdAt: string;
  updatedAt: string;
  governanceDocument: GovernanceDocumentResponse | null;
  supportingEvidence: SupportingEvidenceResponse | null;
}

// Dashboard response
export interface DashboardResponse {
  draft: number;
  inReview: number;
  published: number;
  reviewDueSoon: number;
  reviewOverdue: number;
  retired: number;
  total: number;
}

/* ── Governance Documents API ── */

/** List governance documents */
export function listGovernanceDocuments(params?: {
  type?: GovernanceDocumentType;
  status?: DocumentLifecycleStatus;
}): Promise<ApiEnvelope<GovernanceDocumentSummaryResponse[]>> {
  const qs = new URLSearchParams();
  if (params?.type) qs.set("type", params.type);
  if (params?.status) qs.set("status", params.status);
  const q = qs.toString();
  return apiFetch<ApiEnvelope<GovernanceDocumentSummaryResponse[]>>(
    `/content-registry/governance-documents${q ? `?${q}` : ""}`,
  );
}

/** Get review summary dashboard */
export function getGovernanceReviewSummary(
  type?: GovernanceDocumentType,
): Promise<ApiEnvelope<ReviewSummaryResponse>> {
  const path = type
    ? `/content-registry/governance-documents/review-summary?type=${type}`
    : `/content-registry/governance-documents/review-summary`;
  return apiFetch<ApiEnvelope<ReviewSummaryResponse>>(path);
}

/** Get a single governance document */
export function getGovernanceDocument(
  id: string,
): Promise<ApiEnvelope<GovernanceDocumentResponse>> {
  return apiFetch<ApiEnvelope<GovernanceDocumentResponse>>(
    `/content-registry/governance-documents/${id}`,
  );
}

/** Create a new governance document */
export function createGovernanceDocument(payload: {
  documentType: GovernanceDocumentType;
  title: string;
  description?: string;
  owningOrgUnitId?: string;
  requiresReview?: boolean;
  reviewCycleDays?: number;
  reviewAlertDays?: number;
}): Promise<ApiEnvelope<GovernanceDocumentResponse>> {
  return apiFetch<ApiEnvelope<GovernanceDocumentResponse>>(
    `/content-registry/governance-documents`,
    { method: "POST", body: JSON.stringify(payload) },
  );
}

/** Update a governance document (DRAFT only) */
export function updateGovernanceDocument(
  id: string,
  payload: {
    title?: string;
    description?: string;
    owningOrgUnitId?: string;
    requiresReview?: boolean;
    reviewCycleDays?: number;
    reviewAlertDays?: number;
  },
): Promise<ApiEnvelope<GovernanceDocumentResponse>> {
  return apiFetch<ApiEnvelope<GovernanceDocumentResponse>>(
    `/content-registry/governance-documents/${id}`,
    { method: "PATCH", body: JSON.stringify(payload) },
  );
}

/** Delete a governance document (soft delete) */
export function deleteGovernanceDocument(
  id: string,
): Promise<{ success: boolean }> {
  return apiFetch<{ success: boolean }>(
    `/content-registry/governance-documents/${id}`,
    { method: "DELETE" },
  );
}

/**
 * Fire-and-forget DELETE for an orphaned DRAFT (e.g. created in a wizard
 * that the user then cancelled). Uses `keepalive: true` so the request
 * survives tab close / page unload. Errors are intentionally swallowed —
 * the user has already left, there's nothing useful to surface.
 */
export function deleteGovernanceDocumentOrphan(id: string): void {
  try {
    void fetch(
      `${BASE_URL}/content-registry/governance-documents/${id}`,
      {
        method: "DELETE",
        headers: { ...getAuthHeaders() },
        keepalive: true,
      },
    ).catch(() => { /* best effort */ });
  } catch {
    /* best effort */
  }
}

/**
 * Upload file to governance document (DRAFT only).
 *
 * Backend now runs the full validation pipeline server-side. If anything
 * fails it returns 422 with `code: "CR-0303-E"`. The `detail` string is
 * how the FE distinguishes the two failure variants:
 *   - "...The draft document has been discarded..."  → doc soft-deleted
 *   - "...The existing file is retained."            → doc + prior file kept
 * Callers should catch `ApiEnvelopeError` and inspect `code` / `detail`.
 */
export async function uploadGovernanceDocumentFile(
  id: string,
  file: File,
): Promise<ApiEnvelope<GovernanceDocumentResponse>> {
  const formData = new FormData();
  formData.append("file", file);

  const res = await fetch(
    `${BASE_URL}/content-registry/governance-documents/${id}/upload`,
    {
      method: "POST",
      headers: { ...getAuthHeaders() },
      body: formData,
    },
  );

  const text = await res.text();
  let parsed: any = null;
  try { parsed = text ? JSON.parse(text) : null; } catch { /* fall through */ }

  if (!res.ok) {
    const e = parsed?.errors?.[0] ?? {};
    throw new ApiEnvelopeError({
      code: e.code ?? "UNKNOWN",
      title: e.title ?? "Upload failed",
      // Use the API's detail when present; otherwise a brief, sanitized message
      // (avoids dumping nginx HTML error pages like a 413 into the toast).
      detail: e.detail ?? briefHttpError(res.status, text, res.statusText),
      pointer: e.pointer,
      providerMessage: e?.details?.providerMessage,
      status: res.status,
    });
  }

  return parsed as ApiEnvelope<GovernanceDocumentResponse>;
}

/* ─────────────────────────────────────────────────────────
   Governance: validate-before-upload
───────────────────────────────────────────────────────── */

export type ValidateRuleStatus = "PASS" | "FAIL" | "WARN" | "SKIPPED";
export type ValidateRuleSeverity = "ERROR" | "WARN";

export interface ValidateRuleResult {
  ruleId: string;
  name: string;
  severity: ValidateRuleSeverity;
  status: ValidateRuleStatus;
  evidence?: string;
  remediation?: string;
  category: string;
}

export interface ValidateFileSummary {
  pass: number;
  fail: number;
  warn: number;
  skipped: number;
  total: number;
}

export interface ValidateFileResult {
  filename: string;
  docType: string;
  durationMs: number;
  summary: ValidateFileSummary;
  results: ValidateRuleResult[];
  resultsByCategory: Record<string, ValidateRuleResult[]>;
  summaryByCategory: Record<string, ValidateFileSummary>;
}

/** Normalize the raw API response shape into ValidateFileResult */
function normalizeValidateResult(raw: any): ValidateFileResult {
  const rawSummary = raw.summary ?? {};
  const results: ValidateRuleResult[] = (raw.results ?? []).map((r: any) => ({
    ruleId: r.ruleId ?? "",
    name: r.name ?? r.ruleId ?? "",
    severity: r.severity ?? "WARN",
    status: r.status ?? "SKIPPED",
    evidence: r.evidence ?? undefined,
    remediation: r.remediation ?? undefined,
    category: r.category ?? "uncategorized",
  }));

  const summary: ValidateFileSummary = {
    pass:    rawSummary.pass    ?? 0,
    fail:    rawSummary.failed  ?? rawSummary.fail    ?? rawSummary.errors   ?? 0,
    warn:    rawSummary.warnings ?? rawSummary.warn   ?? 0,
    skipped: rawSummary.skipped ?? 0,
    total:   rawSummary.total   ?? results.length,
  };

  // Build resultsByCategory from flat results list
  const resultsByCategory: Record<string, ValidateRuleResult[]> = {};
  for (const rule of results) {
    const cat = rule.category;
    if (!resultsByCategory[cat]) resultsByCategory[cat] = [];
    resultsByCategory[cat].push(rule);
  }

  // Use API-provided summaryByCategory when available, otherwise derive from results
  const rawCatSummary = raw.summaryByCategory ?? {};
  const summaryByCategory: Record<string, ValidateFileSummary> = {};
  const allCats = new Set([...Object.keys(resultsByCategory), ...Object.keys(rawCatSummary)]);
  for (const cat of allCats) {
    const rs = rawCatSummary[cat];
    if (rs) {
      summaryByCategory[cat] = {
        pass:    rs.pass    ?? 0,
        fail:    rs.failed  ?? rs.fail    ?? 0,
        warn:    rs.warnings ?? rs.warn   ?? 0,
        skipped: rs.skipped ?? 0,
        total:   rs.total   ?? 0,
      };
    } else {
      const cats = resultsByCategory[cat] ?? [];
      summaryByCategory[cat] = { pass: 0, fail: 0, warn: 0, skipped: 0, total: cats.length };
      for (const rule of cats) {
        if (rule.status === "PASS")      summaryByCategory[cat].pass++;
        else if (rule.status === "FAIL") summaryByCategory[cat].fail++;
        else if (rule.status === "WARN") summaryByCategory[cat].warn++;
        else                             summaryByCategory[cat].skipped++;
      }
    }
  }

  return {
    filename: raw.filename ?? "",
    docType:  raw.docType  ?? "",
    durationMs: raw.durationMs ?? 0,
    summary,
    results,
    resultsByCategory,
    summaryByCategory,
  };
}

/**
 * Rich error type that preserves the full ApiError envelope so the UI can
 * render `details.providerMessage` and route field errors via `pointer`.
 * The default `apiFetch` collapses errors to a one-line message — this
 * keeps everything.
 */
export class ApiEnvelopeError extends Error {
  code: string;
  title: string;
  detail: string;
  pointer?: string;
  providerMessage?: string;
  status: number;

  constructor(args: {
    code: string;
    title: string;
    detail: string;
    pointer?: string;
    providerMessage?: string;
    status: number;
  }) {
    super(args.detail);
    this.name = args.title || "ApiEnvelopeError";
    this.code = args.code;
    this.title = args.title;
    this.detail = args.detail;
    this.pointer = args.pointer;
    this.providerMessage = args.providerMessage;
    this.status = args.status;
  }
}

/**
 * Pre-flight validate: extracts PDF text and runs the org's active rules
 * through the active LLM. Does not save anything. Caller is expected to
 * separately call `uploadGovernanceDocumentFile` with the same File on confirm.
 */
export async function validateGovernanceDocumentFile(
  id: string,
  file: File,
  signal?: AbortSignal,
): Promise<ValidateFileResult> {
  const formData = new FormData();
  formData.append("file", file);

  const res = await fetch(
    `${BASE_URL}/content-registry/governance-documents/${id}/validate-file`,
    {
      method: "POST",
      headers: { ...getAuthHeaders() },
      body: formData,
      signal,
    },
  );

  const text = await res.text();
  let parsed: any = null;
  try { parsed = text ? JSON.parse(text) : null; } catch { /* fall through */ }

  if (!res.ok) {
    const e = parsed?.errors?.[0] ?? {};
    throw new ApiEnvelopeError({
      code: e.code ?? "UNKNOWN",
      title: e.title ?? "Validation failed",
      detail: e.detail ?? `Server returned ${res.status}.`,
      pointer: e.pointer,
      providerMessage: e?.details?.providerMessage,
      status: res.status,
    });
  }

  return normalizeValidateResult(parsed?.data ?? {});
}

/** Delete file from governance document (DRAFT only) */
export function deleteGovernanceDocumentFile(
  id: string,
): Promise<ApiEnvelope<GovernanceDocumentResponse>> {
  return apiFetch<ApiEnvelope<GovernanceDocumentResponse>>(
    `/content-registry/governance-documents/${id}/file`,
    { method: "DELETE" },
  );
}

/** Submit for review (DRAFT → IN_REVIEW) */
export function submitGovernanceDocumentForReview(
  id: string,
): Promise<ApiEnvelope<GovernanceDocumentResponse>> {
  return apiFetch<ApiEnvelope<GovernanceDocumentResponse>>(
    `/content-registry/governance-documents/${id}/submit`,
    { method: "POST" },
  );
}

/** Review action (approve, reject, or publish) */
export function reviewGovernanceDocument(
  id: string,
  action: "APPROVE" | "REJECT" | "PUBLISH",
  reason?: string,
): Promise<ApiEnvelope<GovernanceDocumentResponse>> {
  const body: { action: string; reason?: string } = { action };
  if (action === "REJECT" && reason) {
    body.reason = reason;
  }
  return apiFetch<ApiEnvelope<GovernanceDocumentResponse>>(
    `/content-registry/governance-documents/${id}/review`,
    { method: "POST", body: JSON.stringify(body) },
  );
}

/** Create new version (PUBLISHED → DRAFT v2) */
export function createNewGovernanceDocumentVersion(
  id: string,
): Promise<ApiEnvelope<GovernanceDocumentResponse>> {
  return apiFetch<ApiEnvelope<GovernanceDocumentResponse>>(
    `/content-registry/governance-documents/${id}/new-version`,
    { method: "POST" },
  );
}

/** Retire a document (PUBLISHED/REVIEW_OVERDUE → RETIRED) */
export function retireGovernanceDocument(
  id: string,
  reason: string,
): Promise<ApiEnvelope<GovernanceDocumentResponse>> {
  return apiFetch<ApiEnvelope<GovernanceDocumentResponse>>(
    `/content-registry/governance-documents/${id}/retire`,
    { method: "POST", body: JSON.stringify({ reason }) },
  );
}

/** Get dashboard counts */
export function getGovernanceDocumentDashboard(): Promise<
  ApiEnvelope<DashboardResponse>
> {
  return apiFetch<ApiEnvelope<DashboardResponse>>(
    `/content-registry/governance-documents/dashboard`,
  );
}

/** History event type */
export interface HistoryEvent {
  id: string;
  documentId: string;
  versionNumber: number;
  eventType: string;
  eventDescription: string;
  fromStatus: string | null;
  toStatus: string | null;
  eventData: Record<string, unknown> | null;
  performedBy: string;
  performedByName: string;
  performedAt: string;
}

/** Get document history */
export function getGovernanceDocumentHistory(
  id: string,
  params?: { eventType?: string },
): Promise<ApiEnvelope<HistoryEvent[]>> {
  const qs = new URLSearchParams();
  if (params?.eventType) qs.set("eventType", params.eventType);
  const q = qs.toString();
  return apiFetch<ApiEnvelope<HistoryEvent[]>>(
    `/content-registry/governance-documents/${id}/history${q ? `?${q}` : ""}`,
  );
}

/** Download file (current version) */
export function downloadGovernanceDocumentFile(id: string): string {
  return `${BASE_URL}/content-registry/governance-documents/${id}/download`;
}

/** Preview file (current version) */
export function previewGovernanceDocumentFile(id: string): string {
  return `${BASE_URL}/content-registry/governance-documents/${id}/preview`;
}

/** List all versions */
export function listGovernanceDocumentVersions(
  id: string,
): Promise<ApiEnvelope<VersionInfo[]>> {
  return apiFetch<ApiEnvelope<VersionInfo[]>>(
    `/content-registry/governance-documents/${id}/versions`,
  );
}

/** Download specific version */
export function downloadGovernanceDocumentVersionFile(
  id: string,
  versionNumber: number,
): string {
  return `${BASE_URL}/content-registry/governance-documents/${id}/versions/${versionNumber}/download`;
}

/* ── Legacy aliases for backward compatibility ── */
// These map old function names to new ones during migration

export type LibraryDocumentType = GovernanceDocumentType;
export type LibraryLifecycleStatus = DocumentLifecycleStatus;
export type LibraryReviewStatus = ReviewStatus;

export interface DocumentLibrarySummaryResponse extends GovernanceDocumentSummaryResponse {
  identifier?: string | null;
  status: DocumentLifecycleStatus;
}

export interface DocumentLibraryVersionResponse extends VersionInfo {
  id: string;
  documentId: string;
  versionLabel: string | null;
  changeSummary: string | null;
  lifecycleStatus: DocumentLifecycleStatus;
  publishedAt: string | null;
  reviewDate: string | null;
  reviewStatus: ReviewStatus;
  daysUntilReview: number | null;
  approvedBy: string | null;
  approvedAt: string | null;
}

export interface DocumentLibraryResponse extends GovernanceDocumentResponse {
  identifier?: string | null;
  ownerUserId?: string | null;
  supersededBy?: string | null;
  parentDocumentId?: string | null;
  status: DocumentLifecycleStatus;
  createdBy?: string;
  updatedBy?: string;
}

/* ── Evidence by Statement (for Review page) ── */

export interface EvidenceByStatementItem {
  linkId: string;
  assessmentPlanId: string;
  statementId: string;
  supportingEvidenceId: string;
  linkedBy: string;
  linkedAt: string;
  title: string;
  tags: string[];
  hasFile: boolean;
  fileName: string | null;
  fileSize: number | null;
  contentType: string | null;
}

/* ── Assessment Results — Compilation ── */

export interface AssessmentResultByPlan {
  id: string;
  documentId: string;
  assessmentPlanId: string;
  title: string;
  lifecycleStatus: string;
  importApHref: string;
  createdAt: string;
  updatedAt: string;
}

export interface CompiledResult {
  id: string;
  controlId: string;
  statementId: string;
  finalDetermination:
    | "satisfied"
    | "not-satisfied"
    | "partially-satisfied"
    | "not-applicable";
  contributingAttestationIds: string[];
  contributingDirectFindingIds: string[];
  contributingDerivedFindingIds: string[];
  reconciliationMethod: string;
  reconciliationNotes: string | null;
  compiledBy: string;
  compiledAt: string;
}

export interface ExecutionSummary {
  totalStatements: number;
  passed: number;
  failed: number;
  declarativePassed: number;
  declarativeFailed: number;
  declarativeEvidencePassed: number;
  declarativeEvidenceFailed: number;
}

/** Get Assessment Results ID for a plan */
export function getAssessmentResultByPlan(
  planId: string,
): Promise<ApiEnvelope<AssessmentResultByPlan>> {
  return apiFetch<ApiEnvelope<AssessmentResultByPlan>>(
    `/assessment-results/by-plan/${planId}`,
  );
}

/** Compile results — reconciles all execution data */
export function compileResults(
  arId: string,
  source?: string,
  owningOrgUnitId?: string,
): Promise<ApiEnvelope<CompiledResult[]>> {
  const params = new URLSearchParams();
  if (source) params.append("source", source);
  if (owningOrgUnitId) params.append("orgId", owningOrgUnitId);
  const q = params.toString() ? `?${params.toString()}` : "";
  return apiFetch<ApiEnvelope<CompiledResult[]>>(
    `/assessment-results/${arId}/compile${q}`,
    { method: "POST" },
  );
}

/** Get compiled results (read back) */
export function getCompiledResults(
  arId: string,
  controlId?: string,
): Promise<ApiEnvelope<CompiledResult[]>> {
  const q = controlId ? `?control_id=${encodeURIComponent(controlId)}` : "";
  return apiFetch<ApiEnvelope<CompiledResult[]>>(
    `/assessment-results/${arId}/compiled-results${q}`,
  );
}

/** Get execution summary for a plan */
export function getExecutionSummary(
  planId: string,
): Promise<ApiEnvelope<ExecutionSummary>> {
  return apiFetch<ApiEnvelope<ExecutionSummary>>(
    `/assessment-results/plans/${planId}/execution-summary`,
  );
}

/** Get evidence linked to a specific statement */
export function getEvidenceByStatement(
  planId: string,
  statementId: string,
): Promise<ApiEnvelope<EvidenceByStatementItem[]>> {
  return apiFetch<ApiEnvelope<EvidenceByStatementItem[]>>(
    `/assessment-execution/${planId}/evidence/by-statement?statementId=${encodeURIComponent(statementId)}`,
  );
}

/** Get the current stored license */
export function getLicense(): Promise<ApiEnvelope<LicenseDetails>> {
  return apiFetch<ApiEnvelope<LicenseDetails>>("/license");
}

/** Validate a license key */
export function validateLicense(
  licenseKey: string,
): Promise<LicenseValidationResponse> {
  return apiFetch<LicenseValidationResponse>("/license/validate", {
    method: "POST",
    body: JSON.stringify({ licenseKey }),
  });
}

/* ── CMDB API Endpoints ── */

export interface AssetApplicationMapping {
  id: string;
  assetId: string;
  applicationId: string;
  applicationName: string;
  applicationShortName: string | null;
  createdAt: string;
}

export interface OrgUnit {
  id: string;
  name: string;
  shortName?: string;
  description?: string;
  parentId?: string;
  /** Dotted path, e.g. "devopslabs.devopslabs_india". */
  path?: string;
  orgType: string;
  isActive: boolean;
  /** Present when fetched as a tree (root_only=true&flat=false). */
  children?: OrgUnit[];
  createdAt?: string;
  updatedAt?: string;
}

/** Full org-unit tree — root nodes (GROUP) with nested children. */
export function getOrgUnitTree(): Promise<ApiEnvelope<OrgUnit[]>> {
  return apiFetch<ApiEnvelope<OrgUnit[]>>(`/org-units?root_only=true&flat=false`);
}

export interface CMDBAsset {
  id: string;
  name: string;
  assetType: "ON_PREMISES" | "CLOUD_ACCOUNT" | "CLOUD_VM";
  ipAddress?: string;
  accountId?: string;
  cloudProvider?: string;
  region?: string;
  description?: string;
  status: "ACTIVE" | "INACTIVE" | "DECOMMISSIONED";
  applications?: AssetApplicationMapping[];
  mappings?: CMDBComponentMapping[];
  createdAt?: string;
  updatedAt?: string;
}

export interface CMDBComponentMapping {
  id: string;
  assetId: string;
  componentId: string;
  component: {
    id: string;
    title: string;
    componentType: string | null;
  };
  port: number | null;
  protocol: string;
  status: "RUNNING" | "STOPPED" | "UNKNOWN";
  createdAt: string;
  updatedAt: string;
}

// List OrgUnits by type (DEPARTMENT, DIVISION, APPLICATION)
export function listOrgUnits(
  orgType?: string,
): Promise<ApiEnvelope<OrgUnit[]>> {
  const qs = orgType ? `?orgType=${orgType}&flat=true` : `?flat=true`;
  return apiFetch<ApiEnvelope<OrgUnit[]>>(`/org-units${qs}`);
}

// Get single OrgUnit
export function getOrgUnit(id: string): Promise<ApiEnvelope<OrgUnit>> {
  return apiFetch<ApiEnvelope<OrgUnit>>(`/org-units/${id}`);
}

// Create OrgUnit (Application, Department, Division)
export function createOrgUnit(payload: {
  name: string;
  shortName?: string;
  description?: string;
  parentId?: string;
  orgType: string;
}): Promise<ApiEnvelope<OrgUnit>> {
  return apiFetch<ApiEnvelope<OrgUnit>>(`/org-units`, {
    method: "POST",
    body: JSON.stringify(payload),
  });
}

// Update OrgUnit
export function updateOrgUnit(
  id: string,
  payload: {
    name?: string;
    shortName?: string;
    description?: string;
    isActive?: boolean;
  },
): Promise<ApiEnvelope<OrgUnit>> {
  return apiFetch<ApiEnvelope<OrgUnit>>(`/org-units/${id}`, {
    method: "PATCH",
    body: JSON.stringify(payload),
  });
}

// Delete OrgUnit (soft delete)
export function deleteOrgUnit(id: string): Promise<void> {
  return apiFetch<void>(`/org-units/${id}`, { method: "DELETE" });
}

// List Assets for an Application
export function listAssets(
  applicationId: string,
): Promise<ApiEnvelope<CMDBAsset[]>> {
  return apiFetch<ApiEnvelope<CMDBAsset[]>>(
    `/cmdb/applications/${applicationId}/assets`,
  );
}

// Get single Asset
export function getAsset(assetId: string): Promise<ApiEnvelope<CMDBAsset>> {
  return apiFetch<ApiEnvelope<CMDBAsset>>(`/cmdb/assets/${assetId}`);
}

// Create Asset
export function createAsset(
  applicationId: string,
  payload: {
    name: string;
    assetType: "ON_PREMISES" | "CLOUD_ACCOUNT" | "CLOUD_VM";
    ipAddress?: string;
    accountId?: string;
    cloudProvider?: string;
    region?: string;
    description?: string;
  },
): Promise<ApiEnvelope<CMDBAsset>> {
  return apiFetch<ApiEnvelope<CMDBAsset>>(
    `/cmdb/applications/${applicationId}/assets`,
    {
      method: "POST",
      body: JSON.stringify(payload),
    },
  );
}

// Update Asset
export function updateAsset(
  assetId: string,
  payload: {
    name?: string;
    description?: string;
    status?: "ACTIVE" | "INACTIVE" | "DECOMMISSIONED";
    ipAddress?: string;
    accountId?: string;
    region?: string;
  },
): Promise<ApiEnvelope<CMDBAsset>> {
  return apiFetch<ApiEnvelope<CMDBAsset>>(`/cmdb/assets/${assetId}`, {
    method: "PATCH",
    body: JSON.stringify(payload),
  });
}

// Delete Asset (soft delete)
export function deleteAsset(assetId: string): Promise<void> {
  return apiFetch<void>(`/cmdb/assets/${assetId}`, { method: "DELETE" });
}

// List Component Mappings for an Asset
export function listComponentMappings(
  assetId: string,
): Promise<ApiEnvelope<CMDBComponentMapping[]>> {
  return apiFetch<ApiEnvelope<CMDBComponentMapping[]>>(
    `/cmdb/assets/${assetId}/components`,
  );
}

// Add Component Mapping to Asset
export function createComponentMapping(
  assetId: string,
  payload: {
    componentId: string;
    port?: number;
    protocol?: string;
    status: "RUNNING" | "STOPPED" | "UNKNOWN";
  },
): Promise<ApiEnvelope<CMDBComponentMapping>> {
  return apiFetch<ApiEnvelope<CMDBComponentMapping>>(
    `/cmdb/assets/${assetId}/components`,
    {
      method: "POST",
      body: JSON.stringify(payload),
    },
  );
}

// Update Component Mapping
export function updateComponentMapping(
  mappingId: string,
  payload: {
    port?: number;
    protocol?: string;
    status?: "RUNNING" | "STOPPED" | "UNKNOWN";
  },
): Promise<ApiEnvelope<CMDBComponentMapping>> {
  return apiFetch<ApiEnvelope<CMDBComponentMapping>>(
    `/cmdb/asset-components/${mappingId}`,
    {
      method: "PATCH",
      body: JSON.stringify(payload),
    },
  );
}

// Delete Component Mapping (soft delete)
export function deleteComponentMapping(mappingId: string): Promise<void> {
  return apiFetch<void>(`/cmdb/asset-components/${mappingId}`, {
    method: "DELETE",
  });
}

// Search for components available for mapping
export function listComponentsForSearch(
  query?: string,
): Promise<ApiEnvelope<Array<{ id: string; title: string; componentType?: string }>>> {
  return searchComponents({ q: query }).then((res) => ({
    ...res,
    data: (res.data || []).map((comp: any) => ({
      id: comp.id || comp.uuid,
      title: comp.title,
      componentType: comp.componentType,
    })),
  }));
}

// ── CMDB Applications API ──

export type ApplicationStatus = "OPERATIONAL" | "UNDER_DEVELOPMENT" | "DISPOSITION" | "OTHER";
export type ApplicationCriticality = "LOW" | "MEDIUM" | "HIGH" | "CRITICAL";

export interface CMDBApplication {
  id: string;
  name: string;
  appCode: string | null;
  description: string | null;
  parentId: string | null;
  path: string | null;
  isActive: boolean;
  divisionId: string | null;
  divisionName: string | null;
  departmentId: string | null;
  departmentName: string | null;
  status: ApplicationStatus;
  criticality: ApplicationCriticality;
  componentCount: number;
  ssps?: { sspId: string; title: string }[];
  createdAt: string;
  updatedAt: string;
}

// List all CMDB Applications
export function listCMDBApplications(): Promise<ApiEnvelope<CMDBApplication[]>> {
  return apiFetch<ApiEnvelope<CMDBApplication[]>>(`/cmdb/applications`);
}

// Get single CMDB Application
export function getCMDBApplication(id: string): Promise<ApiEnvelope<CMDBApplication>> {
  return apiFetch<ApiEnvelope<CMDBApplication>>(`/cmdb/applications/${id}`);
}

// Create CMDB Application
export function createCMDBApplication(payload: {
  name: string;
  appCode?: string | null;
  description?: string | null;
  parentId: string;
  status?: ApplicationStatus;
  criticality?: ApplicationCriticality;
}): Promise<ApiEnvelope<CMDBApplication>> {
  return apiFetch<ApiEnvelope<CMDBApplication>>(`/cmdb/applications`, {
    method: "POST",
    body: JSON.stringify(payload),
  });
}

// Update CMDB Application
export function updateCMDBApplication(
  id: string,
  payload: {
    name?: string;
    appCode?: string | null;
    description?: string | null;
    status?: ApplicationStatus;
    criticality?: ApplicationCriticality;
    active?: boolean;
  }
): Promise<ApiEnvelope<CMDBApplication>> {
  return apiFetch<ApiEnvelope<CMDBApplication>>(`/cmdb/applications/${id}`, {
    method: "PATCH",
    body: JSON.stringify(payload),
  });
}

// Delete CMDB Application
export function deleteCMDBApplication(id: string): Promise<void> {
  return apiFetch<void>(`/cmdb/applications/${id}`, {
    method: "DELETE",
  });
}

// ── CMDB Components Search ──

export interface CMDBComponent {
  id: string;
  title: string;
  componentType?: string;
  layerId?: string;
  layer?: { id: string; name?: string; code?: string } | null;
}

// List CMDB components with optional search and type filter
export function listCMDBComponents(params?: {
  q?: string;
  type?: "SOFTWARE" | "SERVICE" | "HARDWARE" | "POLICY" | "PROCESS";
}): Promise<ApiEnvelope<CMDBComponent[]>> {
  const qs = new URLSearchParams();
  if (params?.q) qs.set("q", params.q);
  if (params?.type) qs.set("type", params.type);
  const query = qs.toString();
  return apiFetch<ApiEnvelope<CMDBComponent[]>>(
    `/cmdb/components${query ? `?${query}` : ""}`,
  );
}

// ── SSP Layers ──

export interface SSPLayer {
  id: string;
  slug: string;
  displayName: string;
  description?: string;
  displayOrder?: number;
  iconKey?: string;
  active?: boolean;
}

// GET /api/v1/cmdb/layers — list SSP layers used to categorize components
export function listSSPLayers(): Promise<ApiEnvelope<SSPLayer[]>> {
  return apiFetch<ApiEnvelope<SSPLayer[]>>(`/cmdb/layers`);
}

// ── Asset-Application Many-to-Many Mapping ──

// List all assets globally (not scoped to an application)
export function listAllAssets(): Promise<ApiEnvelope<CMDBAsset[]>> {
  return apiFetch<ApiEnvelope<CMDBAsset[]>>(`/cmdb/assets`);
}

// Create asset without requiring an application ID (global endpoint)
export function createAssetGlobal(payload: {
  name: string;
  assetType: "ON_PREMISES" | "CLOUD_ACCOUNT" | "CLOUD_VM";
  ipAddress?: string;
  accountId?: string;
  cloudProvider?: string;
  region?: string;
  description?: string;
  status?: "ACTIVE" | "INACTIVE" | "DECOMMISSIONED";
}): Promise<ApiEnvelope<CMDBAsset>> {
  return apiFetch<ApiEnvelope<CMDBAsset>>(`/cmdb/assets`, {
    method: "POST",
    body: JSON.stringify(payload),
  });
}

// List applications an asset is linked to
export function listAssetApplicationMappings(
  assetId: string,
): Promise<ApiEnvelope<AssetApplicationMapping[]>> {
  return apiFetch<ApiEnvelope<AssetApplicationMapping[]>>(
    `/cmdb/assets/${assetId}/applications`,
  );
}

// Link an asset to an application
export function linkAssetToApplication(
  assetId: string,
  applicationId: string,
): Promise<ApiEnvelope<AssetApplicationMapping>> {
  return apiFetch<ApiEnvelope<AssetApplicationMapping>>(
    `/cmdb/assets/${assetId}/applications`,
    {
      method: "POST",
      body: JSON.stringify({ applicationId }),
    },
  );
}

// Unlink an asset from one application
export function unlinkAssetFromApplication(mappingId: string): Promise<void> {
  return apiFetch<void>(`/cmdb/asset-applications/${mappingId}`, {
    method: "DELETE",
  });
}

/* ── New CMDB Architecture: CI + Cloud + Deployments ── */

// ── Devices (formerly /ci/onprem) ──

export type DeviceType = "SERVER" | "VM" | "FIREWALL" | "SWITCH" | "ROUTER" | "LB" | "CONTAINER" | "STORAGE";
export type DeviceStatus = "ACTIVE" | "INACTIVE" | "MAINTENANCE" | "DECOMMISSIONED";

export interface DeviceResponse {
  id: string;
  orgId: string;
  name: string;
  type: DeviceType;
  status: DeviceStatus;
  description: string | null;
  // connectivity
  ipAddress: string | null;
  managementIp: string | null;
  virtualIp: string | null;
  hostname: string | null;
  // os
  osName: string | null;
  osVersion: string | null;
  // hardware
  cpuCores: number | null;
  ramGb: number | null;
  diskGb: number | null;
  // virtualisation
  hypervisor: string | null;
  parentCiId: string | null;
  // container
  imageName: string | null;
  namespace: string | null;
  registryUrl: string | null;
  // storage
  totalCapacityTb: number | null;
  usedCapacityTb: number | null;
  createdAt: string;
  updatedAt: string;
}

// Backward-compat alias
export type CiOnpremResponse = DeviceResponse;

export function listCiDevices(params?: { type?: DeviceType }): Promise<ApiEnvelope<DeviceResponse[]>> {
  const qs = params?.type ? `?type=${params.type}` : "";
  return apiFetch<ApiEnvelope<DeviceResponse[]>>(`/cmdb/devices${qs}`);
}
export const listCiOnprem = listCiDevices; // backward compat

export function getCiDevice(id: string): Promise<ApiEnvelope<DeviceResponse>> {
  return apiFetch<ApiEnvelope<DeviceResponse>>(`/cmdb/devices/${id}`);
}
export const getCiOnprem = getCiDevice;

export function createCiDevice(payload: {
  orgId: string;
  name: string;
  type: DeviceType;
  status?: DeviceStatus;
  description?: string;
  ipAddress?: string;
  managementIp?: string;
  virtualIp?: string;
  hostname?: string;
  osName?: string;
  osVersion?: string;
  cpuCores?: number;
  ramGb?: number;
  diskGb?: number;
  hypervisor?: string;
  parentCiId?: string;
  imageName?: string;
  namespace?: string;
  registryUrl?: string;
  totalCapacityTb?: number;
  usedCapacityTb?: number;
}): Promise<ApiEnvelope<DeviceResponse>> {
  return apiFetch<ApiEnvelope<DeviceResponse>>(`/cmdb/devices`, {
    method: "POST",
    body: JSON.stringify(payload),
  });
}
export const createCiOnprem = createCiDevice;

export function updateCiDevice(
  id: string,
  payload: Partial<Omit<DeviceResponse, "id" | "createdAt" | "updatedAt">>,
): Promise<ApiEnvelope<DeviceResponse>> {
  return apiFetch<ApiEnvelope<DeviceResponse>>(`/cmdb/devices/${id}`, {
    method: "PATCH",
    body: JSON.stringify(payload),
  });
}
export const updateCiOnprem = updateCiDevice;

export function deleteCiDevice(id: string): Promise<void> {
  return apiFetch<void>(`/cmdb/devices/${id}`, { method: "DELETE" });
}
export const deleteCiOnprem = deleteCiDevice;

export interface DeviceImportError {
  row: number;
  field?: string;
  message: string;
}

export interface DeviceImportResult {
  total: number;
  success: number;
  failed: number;
  errors: DeviceImportError[];
}

export function importDevices(file: File, orgId: string): Promise<ApiEnvelope<DeviceImportResult>> {
  const fd = new FormData();
  fd.append("file", file);
  return apiFetch<ApiEnvelope<DeviceImportResult>>(`/cmdb/devices/import?orgId=${encodeURIComponent(orgId)}`, {
    method: "POST",
    headers: {},
    body: fd,
  });
}

export async function downloadDeviceImportTemplate(): Promise<void> {
  const token = useAuthStore.getState().getToken();
  const headers: Record<string, string> = token ? { Authorization: `Bearer ${token}` } : {};
  const res = await fetch(
    `${BASE_URL}/cmdb/devices/import/template`,
    { headers },
  );
  if (!res.ok) throw new Error(`Failed to download template (${res.status})`);
  const blob = await res.blob();
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = "device-import-template.csv";
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  URL.revokeObjectURL(url);
}

// ── Device Relationships ──

export type RelationshipType = "HOSTED_ON" | "CONNECTED_TO" | "BEHIND" | "ATTACHED_TO" | "PROTECTED_BY";

export interface CiRelationshipResponse {
  id: string;
  relationshipType: RelationshipType;
  direction: "OUTGOING" | "INCOMING";
  peerCiId: string;
  peerName: string;
  peerType: DeviceType;
  peerStatus: string;
  peerIpAddress: string | null;
  notes: string | null;
  createdAt: string;
}

export interface DeviceRelationshipGraphResponse {
  deviceId: string;
  deviceName: string;
  deviceType: DeviceType;
  deviceStatus: string;
  // Outgoing
  hostedOn:     CiRelationshipResponse[];
  connectedTo:  CiRelationshipResponse[];
  behind:       CiRelationshipResponse[];
  attachedTo:   CiRelationshipResponse[];
  protectedBy:  CiRelationshipResponse[];
  // Incoming
  hosts:        CiRelationshipResponse[];
  connectedFrom:CiRelationshipResponse[];
  backends:     CiRelationshipResponse[];
  attachedFrom: CiRelationshipResponse[];
  protects:     CiRelationshipResponse[];
}

export function getDeviceRelationships(ciId: string): Promise<ApiEnvelope<DeviceRelationshipGraphResponse>> {
  return apiFetch<ApiEnvelope<DeviceRelationshipGraphResponse>>(`/cmdb/devices/${ciId}/relationships`);
}

export function addDeviceRelationship(
  ciId: string,
  payload: { targetCiId: string; relationshipType: string; notes?: string },
): Promise<ApiEnvelope<CiRelationshipResponse>> {
  return apiFetch<ApiEnvelope<CiRelationshipResponse>>(`/cmdb/devices/${ciId}/relationships`, {
    method: "POST",
    body: JSON.stringify(payload),
  });
}

export function deleteDeviceRelationship(relationshipId: string): Promise<void> {
  return apiFetch<void>(`/cmdb/device-relationships/${relationshipId}`, { method: "DELETE" });
}

// ── Services (Module 3) ──

export type ServiceStatus = "ACTIVE" | "INACTIVE" | "DEGRADED" | "MAINTENANCE";
export type ServiceDeploymentRole = "PRIMARY" | "BACKUP" | "STANDBY" | "READ_REPLICA" | "DR" | "ACTIVE" | "PASSIVE";
export type AppServiceDependencyType = "REQUIRED" | "OPTIONAL";

/** A device (or cloud resource) deployment under one component of a service */
export interface ServiceComponentDeployment {
  assetId: string;
  ciId: string;
  ciType: "ON_PREMISES" | "CLOUD" | null;
  ciName: string;
  ciIpAddress: string | null;
  ciIdentifier: string | null;       // IP for on-prem, resource ID/accountId for cloud
  // Cloud-specific
  ciCloudProviderName: string | null; // cloud account name
  awsResourceName: string | null;     // AWS resource Name tag
  awsResourceIdentifier: string | null; // e.g. "i-0abc1234", "db-prod-mysql"
  cloudResourceName?: string | null;   // generic cloud resource display name
  cloudResourceIdentifier?: string | null; // generic cloud resource id
  awsServiceName: string | null;      // "EC2 Instances", "RDS", etc.
  port: number | null;
  protocol: string;
  role: string;
  status: string;
  endpointUrl: string | null;
}

/** One OSCAL component linked to a service, with its device deployments */
export interface ServiceComponentMapping {
  id: string;        // mappingId — used for DELETE /cmdb/service-components/:mappingId
  serviceId: string;
  componentId: string;
  component: { id: string; title: string; componentType: string | null };
  notes: string | null;
  createdAt: string;
  deployments: ServiceComponentDeployment[];
  sspLayerSlug?: string | null;
  sspLayerName?: string | null;
}

export interface ServiceResponse {
  id: string;
  orgId: string;
  name: string;
  status: ServiceStatus;
  description: string | null;
  createdAt: string;
  updatedAt: string;
  components: ServiceComponentMapping[];
}

export interface AppServiceDependency {
  id: string;
  applicationId: string;
  serviceId: string;
  service: ServiceResponse;
  dependencyType: AppServiceDependencyType;
  notes: string | null;
  createdAt: string;
}

export function listServices(): Promise<ApiEnvelope<ServiceResponse[]>> {
  return apiFetch<ApiEnvelope<ServiceResponse[]>>(`/cmdb/services`);
}

export function createService(payload: {
  orgId: string;
  name: string;
  status?: ServiceStatus;
  description?: string;
}): Promise<ApiEnvelope<ServiceResponse>> {
  return apiFetch<ApiEnvelope<ServiceResponse>>(`/cmdb/services`, {
    method: "POST",
    body: JSON.stringify(payload),
  });
}

export function updateService(
  id: string,
  payload: Partial<Omit<ServiceResponse, "id" | "createdAt" | "updatedAt" | "components">>,
): Promise<ApiEnvelope<ServiceResponse>> {
  return apiFetch<ApiEnvelope<ServiceResponse>>(`/cmdb/services/${id}`, {
    method: "PATCH",
    body: JSON.stringify(payload),
  });
}

export function deleteService(id: string): Promise<void> {
  return apiFetch<void>(`/cmdb/services/${id}`, { method: "DELETE" });
}

export function getServiceById(id: string): Promise<ApiEnvelope<ServiceResponse>> {
  return apiFetch<ApiEnvelope<ServiceResponse>>(`/cmdb/services/${id}`);
}

/** Link an OSCAL component to a service (Step 1 of Add Component wizard) */
export function addServiceComponent(
  serviceId: string,
  payload: {
    componentId: string;
    notes?: string;
    layerSlug?: string;
    layerOverrideReason?: string;
  },
): Promise<ApiEnvelope<ServiceComponentMapping>> {
  return apiFetch<ApiEnvelope<ServiceComponentMapping>>(`/cmdb/services/${serviceId}/components`, {
    method: "POST",
    body: JSON.stringify(payload),
  });
}

/** Remove an OSCAL component from a service */
export function removeServiceComponent(mappingId: string): Promise<void> {
  return apiFetch<void>(`/cmdb/service-components/${mappingId}`, { method: "DELETE" });
}

/** Link an asset (device deployment record) to a service (Step 2 of Add Component wizard) */
export function linkAssetToService(serviceId: string, assetId: string): Promise<void> {
  return apiFetch<void>(`/cmdb/services/${serviceId}/assets`, {
    method: "POST",
    body: JSON.stringify({ assetId }),
  });
}

/** Remove an asset from a service */
export function removeServiceAsset(assetId: string): Promise<void> {
  return apiFetch<void>(`/cmdb/service-assets/${assetId}`, { method: "DELETE" });
}

export function listAppServices(applicationId: string): Promise<ApiEnvelope<AppServiceDependency[]>> {
  return apiFetch<ApiEnvelope<AppServiceDependency[]>>(`/cmdb/applications/${applicationId}/services`);
}

export function linkServiceToApp(
  applicationId: string,
  payload: { serviceId: string; dependencyType: AppServiceDependencyType; notes?: string },
): Promise<ApiEnvelope<AppServiceDependency>> {
  return apiFetch<ApiEnvelope<AppServiceDependency>>(`/cmdb/applications/${applicationId}/services`, {
    method: "POST",
    body: JSON.stringify(payload),
  });
}

export function unlinkServiceFromApp(dependencyId: string): Promise<void> {
  return apiFetch<void>(`/cmdb/app-services/${dependencyId}`, { method: "DELETE" });
}

// ── Service Asset Selection (per-application) ──

export interface ServiceAsset {
  id: string;
  componentId: string;
  componentTitle: string;
  ciType: "ON_PREMISES" | "CLOUD" | null;
  ciId: string;
  ciName: string;
  ciIdentifier: string | null;
  port: number | null;
  role: string;
  status: string;
  createdAt: string;
  updatedAt: string;
}

export interface ServiceAssetWithSelection {
  asset: ServiceAsset;
  isSelected: boolean;
}

export function listServiceAssetsWithSelection(
  applicationId: string,
  serviceId: string,
): Promise<ApiEnvelope<ServiceAssetWithSelection[]>> {
  return apiFetch<ApiEnvelope<ServiceAssetWithSelection[]>>(
    `/cmdb/applications/${applicationId}/services/${serviceId}/assets`,
  );
}

export function saveServiceAssetSelections(
  applicationId: string,
  serviceId: string,
  assetIds: string[],
): Promise<ApiEnvelope<ServiceAssetWithSelection[]>> {
  return apiFetch<ApiEnvelope<ServiceAssetWithSelection[]>>(
    `/cmdb/applications/${applicationId}/services/${serviceId}/asset-selections`,
    { method: "PUT", body: JSON.stringify({ assetIds }) },
  );
}

// ── Cloud Providers (TABLE 6b) ──

export interface CloudProviderResponse {
  id: string;
  orgId: string;
  provider: "AWS" | "AZURE" | "GCP" | "OCI";
  accountName: string;
  accountId: string;
  region: string;
  regions?: string[];
  role: "PRODUCTION" | "DEVELOPMENT" | "STAGING" | "DR" | "UAT" | "SANDBOX";
  description: string | null;
  status: "ACTIVE" | "INACTIVE" | "SUSPENDED";
  createdAt: string;
  updatedAt: string;
}

export interface TestConnectionPayload {
  orgId: string;
  provider: string;
  credentialType: "ACCESS_KEY" | "ASSUME_ROLE";
  accessKey?: string;
  secretKey?: string;
  roleArn?: string;
  externalId?: string;
}

export interface TestConnectionResult {
  success: boolean;
  accountId: string;
  message: string;
}

export interface CreateCloudAccountPayload {
  orgId: string;
  provider: string;
  credentialType: "ACCESS_KEY" | "ASSUME_ROLE";
  accessKey?: string;
  secretKey?: string;
  roleArn?: string;
  externalId?: string;
  accountName: string;
  role?: string;
  description?: string;
  status?: string;
}

export interface DiscoveryRunStatus {
  id: string;
  cloudProviderId: string;
  status: "RUNNING" | "SUCCESS" | "FAILED";
  regionsScanned: string[];
  progressPercent: number;
  regionsCompleted: number;
  regionsTotal: number;
  totalDiscovered: number;
  createdCount: number;
  updatedCount: number;
  staleCount: number;
  errorMessage: string | null;
  startedAt: string;
  finishedAt: string | null;
}

export function testCloudConnection(
  payload: TestConnectionPayload
): Promise<ApiEnvelope<TestConnectionResult>> {
  return apiFetch(`/cmdb/cloud/accounts/test-connection`, {
    method: "POST",
    body: JSON.stringify(payload),
  });
}

export function createCloudAccount(
  payload: CreateCloudAccountPayload
): Promise<ApiEnvelope<CloudProviderResponse>> {
  return apiFetch(`/cmdb/cloud/accounts`, {
    method: "POST",
    body: JSON.stringify(payload),
  });
}

export function discoverCloudAccount(
  cloudProviderId: string
): Promise<ApiEnvelope<{ runId: string; status: string; message: string }>> {
  return apiFetch(`/cmdb/cloud/accounts/${cloudProviderId}/discover`, {
    method: "POST",
  });
}

export function getDiscoveryRun(
  runId: string
): Promise<ApiEnvelope<DiscoveryRunStatus>> {
  return apiFetch(`/cmdb/cloud/discovery-runs/${runId}`);
}

export function listCloudProviders(params?: { applicationId?: string }): Promise<ApiEnvelope<CloudProviderResponse[]>> {
  const qs = params?.applicationId ? `?applicationId=${params.applicationId}` : "";
  return apiFetch<ApiEnvelope<CloudProviderResponse[]>>(`/cmdb/ci/cloud${qs}`);
}

export function getCloudProvider(id: string): Promise<ApiEnvelope<CloudProviderResponse>> {
  return apiFetch<ApiEnvelope<CloudProviderResponse>>(`/cmdb/ci/cloud/${id}`);
}

export function createCloudProvider(payload: {
  orgId: string;
  provider: "AWS" | "AZURE" | "GCP" | "OCI";
  accountName: string;
  accountId: string;
  region: string;
  role?: "PRODUCTION" | "DEVELOPMENT" | "STAGING" | "DR" | "UAT" | "SANDBOX";
  description?: string;
  status?: "ACTIVE" | "INACTIVE" | "SUSPENDED";
}): Promise<ApiEnvelope<CloudProviderResponse>> {
  return apiFetch<ApiEnvelope<CloudProviderResponse>>(`/cmdb/ci/cloud`, {
    method: "POST",
    body: JSON.stringify(payload),
  });
}

export function updateCloudProvider(
  id: string,
  payload: Partial<Omit<CloudProviderResponse, "id" | "createdAt" | "updatedAt">>
): Promise<ApiEnvelope<CloudProviderResponse>> {
  return apiFetch<ApiEnvelope<CloudProviderResponse>>(`/cmdb/ci/cloud/${id}`, {
    method: "PATCH",
    body: JSON.stringify(payload),
  });
}

export function deleteCloudProvider(id: string): Promise<void> {
  return apiFetch<void>(`/cmdb/ci/cloud/${id}`, { method: "DELETE" });
}

// ── Deployment Records (TABLE 4) ──

export type AssetRole = "PRIMARY" | "BACKUP" | "STANDBY" | "READ_REPLICA" | "DR" | "ACTIVE" | "PASSIVE";
export type AssetStatus = "RUNNING" | "STOPPED" | "UNKNOWN" | "STARTING" | "DEGRADED" | "MAINTENANCE";
export type AssetProtocol = "TCP" | "UDP" | "HTTP" | "HTTPS" | "GRPC";

export interface AssetSummaryResponse {
  id: string;
  componentId: string;
  componentTitle: string;
  ciType: "ON_PREMISES" | "CLOUD";
  ciId: string | null;
  ciName: string | null;
  ciIdentifier: string | null;
  port: number | null;
  protocol: AssetProtocol;
  role: AssetRole;
  status: AssetStatus;
  createdAt: string;
  updatedAt: string;
}

export interface CiOnpremDetail {
  id: string;
  orgId: string;
  name: string;
  type: string;
  hostname: string | null;
  ipAddress: string;
  osName: string | null;
  osVersion: string | null;
  description: string | null;
  status: string;
}

export interface CloudProviderDetail {
  id: string;
  orgId: string;
  provider: string;
  accountName: string;
  accountId: string;
  region: string;
  role: string;
  description: string | null;
  status: string;
}

export interface AwsResourceListResponse {
  id: string;
  cloudProviderId: string;
  resourceId: string;
  resourceName: string | null;
  serviceName: string;
  category: string;
  cloudIdentifier: string;
  awsIdentifier?: string;
  iconKey: string;
  region: string;
  availabilityZone: string | null;
  status: string | null;
  isGlobal: boolean;
  vpcId: string | null;
}

export interface AssetResponse {
  id: string;
  componentId: string;
  component: { id: string; title: string; componentType?: string };
  ciType: "ON_PREMISES" | "CLOUD";
  ciOnpremId?: string;
  ciOnprem?: CiOnpremDetail;
  ciCloudProviderId?: string;
  ciCloud?: CloudProviderDetail;
  cloudResourceId?: string | null;
  awsResourceId?: string | null;
  cloudResource?: {
    id: string;
    resourceId: string;
    resourceName: string | null;
    serviceName: string;
    cloudIdentifier: string;
    awsIdentifier?: string;
    region: string;
    availabilityZone: string | null;
    status: string | null;
  } | null;
  awsResource?: {
    id: string;
    resourceId: string;
    resourceName: string | null;
    serviceName: string;
    awsIdentifier: string;
    region: string;
    availabilityZone: string | null;
    status: string | null;
  } | null;
  port: number | null;
  arn: string | null;
  protocol: AssetProtocol;
  role: AssetRole;
  status: AssetStatus;
  endpointUrl: string | null;
  notes: string | null;
  createdAt: string;
  updatedAt: string;
}

export function listAssetsByComponent(componentId?: string): Promise<ApiEnvelope<AssetSummaryResponse[]>> {
  const query = componentId ? `?componentId=${componentId}` : "";
  return apiFetch<ApiEnvelope<AssetSummaryResponse[]>>(`/cmdb/assets${query}`);
}

export function getAssetDetail(assetId: string): Promise<ApiEnvelope<AssetResponse>> {
  return apiFetch<ApiEnvelope<AssetResponse>>(`/cmdb/assets/${assetId}`);
}

export function createAssetDeployment(payload: {
  componentId: string;
  ciType: "ON_PREMISES" | "CLOUD";
  ciOnpremId?: string;
  ciCloudProviderId?: string;
  cloudResourceId?: string;
  awsResourceId?: string;
  port?: number;
  arn?: string;
  protocol?: AssetProtocol;
  role?: AssetRole;
  status?: AssetStatus;
  endpointUrl?: string;
  notes?: string;
  applicationId?: string;
}): Promise<ApiEnvelope<AssetResponse>> {
  return apiFetch<ApiEnvelope<AssetResponse>>(`/cmdb/assets`, {
    method: "POST",
    body: JSON.stringify(payload),
  });
}

export function updateAssetDeployment(
  assetId: string,
  payload: Partial<Omit<AssetResponse, "id" | "createdAt" | "updatedAt" | "component">>
): Promise<ApiEnvelope<AssetResponse>> {
  return apiFetch<ApiEnvelope<AssetResponse>>(`/cmdb/assets/${assetId}`, {
    method: "PATCH",
    body: JSON.stringify(payload),
  });
}

export function deleteAssetDeployment(assetId: string): Promise<void> {
  return apiFetch<void>(`/cmdb/assets/${assetId}`, { method: "DELETE" });
}

// ── Component Mappings (TABLE 3) ──

export type ComponentSource = "ON_PREM" | "CLOUD" | "SERVICE";

export interface AppComponentDeploymentItem {
  assetId: string;
  ciId: string;
  ciType?: "ON_PREMISES" | "CLOUD" | null;
  ciName: string;
  ciIpAddress: string | null;
  ciIdentifier?: string | null;
  cloudProviderId?: string | null;
  cloudAccountName?: string | null;
  cloudAccountId?: string | null;
  cloudResourceId?: string | null;
  cloudIdentifier?: string | null;
  region?: string | null;
  ciCloudProviderName?: string | null;
  cloudResourceName?: string | null;
  awsResourceName?: string | null;
  cloudResourceIdentifier?: string | null;
  awsResourceIdentifier?: string | null;
  cloudServiceName?: string | null;
  awsServiceName?: string | null;
  port: number | null;
  protocol: string;
  role: string;
  status: string;
  endpointUrl: string | null;
}

export interface AppComponentMappingResponse {
  id: string;
  applicationId: string;
  componentId: string;
  component: { id: string; title: string; componentType: string | null };
  source: ComponentSource;
  ciName: string | null;
  ciAccountId: string | null;
  notes: string | null;
  /** Component architecture layer assigned to this mapping. */
  sspLayerName?: string | null;
  sspLayerSlug?: string | null;
  createdAt: string;
  deployments?: AppComponentDeploymentItem[];
}

export interface UpdateComponentMappingPayload {
  notes?: string | null;
  layerSlug?: string | null;
  /** Required when layerSlug is set. */
  layerOverrideReason?: string;
}

export function listAppComponentMappings(
  applicationId: string
): Promise<ApiEnvelope<AppComponentMappingResponse[]>> {
  return apiFetch<ApiEnvelope<AppComponentMappingResponse[]>>(
    `/cmdb/applications/${applicationId}/components`
  );
}

export function createAppComponentMapping(
  applicationId: string,
  payload: { componentId: string; notes?: string; layerId?: string }
): Promise<ApiEnvelope<AppComponentMappingResponse>> {
  return apiFetch<ApiEnvelope<AppComponentMappingResponse>>(
    `/cmdb/applications/${applicationId}/components`,
    {
      method: "POST",
      body: JSON.stringify(payload),
    }
  );
}

export function deleteAppComponentMapping(mappingId: string): Promise<void> {
  return apiFetch<void>(`/cmdb/app-components/${mappingId}`, { method: "DELETE" });
}

/** Update an app→component mapping — PATCH /cmdb/app-components/{mappingId} */
export function updateAppComponentMapping(
  mappingId: string,
  payload: UpdateComponentMappingPayload,
): Promise<ApiEnvelope<AppComponentMappingResponse>> {
  return apiFetch<ApiEnvelope<AppComponentMappingResponse>>(
    `/cmdb/app-components/${mappingId}`,
    { method: "PATCH", body: JSON.stringify(payload) },
  );
}

/** Update a service→component mapping — PATCH /cmdb/service-components/{mappingId} */
export function updateServiceComponentMapping(
  mappingId: string,
  payload: UpdateComponentMappingPayload,
): Promise<ApiEnvelope<AppComponentMappingResponse>> {
  return apiFetch<ApiEnvelope<AppComponentMappingResponse>>(
    `/cmdb/service-components/${mappingId}`,
    { method: "PATCH", body: JSON.stringify(payload) },
  );
}

export function addCloudResourceToAppComponent(
  mappingId: string,
  payload: { cloudResourceId: string },
): Promise<ApiEnvelope<AppComponentDeploymentItem>> {
  return apiFetch<ApiEnvelope<AppComponentDeploymentItem>>(
    `/cmdb/app-components/${mappingId}/cloud-resources`,
    { method: "POST", body: JSON.stringify(payload) },
  );
}

export function deleteCloudResourceDeployment(resourceMappingId: string): Promise<void> {
  return apiFetch<void>(`/cmdb/cloud-resource-deployments/${resourceMappingId}`, {
    method: "DELETE",
  });
}

export function linkCloudAccountToApp(
  applicationId: string,
  providerId: string,
): Promise<void> {
  return apiFetch<void>(
    `/cmdb/applications/${applicationId}/cloud-accounts/${providerId}`,
    { method: "PUT" },
  );
}

export function unlinkCloudAccountFromApp(
  applicationId: string,
  providerId: string,
): Promise<void> {
  return apiFetch<void>(
    `/cmdb/applications/${applicationId}/cloud-accounts/${providerId}`,
    { method: "DELETE" },
  );
}

export function unlinkCiOnpremFromApp(
  applicationId: string,
  ciId: string,
): Promise<void> {
  return apiFetch<void>(
    `/cmdb/applications/${applicationId}/devices/${ciId}`,
    { method: "DELETE" },
  );
}

// ── Asset-Application Mappings (TABLE 5) ──

export interface AssetApplicationMappingNewResponse {
  id: string;
  applicationId: string;
  assetId: string;
  asset: AssetSummaryResponse;
  ciName?: string | null;
  notes: string | null;
  createdAt: string;
}

export function listAppAssetMappings(
  applicationId: string
): Promise<ApiEnvelope<AssetApplicationMappingNewResponse[]>> {
  return apiFetch<ApiEnvelope<AssetApplicationMappingNewResponse[]>>(
    `/cmdb/applications/${applicationId}/assets`
  );
}

export function createAppAssetMapping(
  applicationId: string,
  payload: { assetId: string; notes?: string }
): Promise<ApiEnvelope<AssetApplicationMappingNewResponse>> {
  return apiFetch<ApiEnvelope<AssetApplicationMappingNewResponse>>(
    `/cmdb/applications/${applicationId}/assets`,
    {
      method: "POST",
      body: JSON.stringify(payload),
    }
  );
}

export function deleteAppAssetMapping(mappingId: string): Promise<void> {
  return apiFetch<void>(`/cmdb/app-assets/${mappingId}`, { method: "DELETE" });
}

// ── AWS Resources ──

export interface AwsServiceCatalog {
  id: number;
  serviceName: string;
  category: string;
  description: string | null;
  cloudIdentifier: string | null;
  awsIdentifier?: string | null;
  iconKey: string;
  isGlobal: boolean;
}

export interface AwsResourceSummary {
  id: string;
  cloudProviderId: string;
  cloudServiceId: string;
  serviceName: string | null;
  category: string | null;
  iconKey: string | null;
  cloudIdentifier: string | null;
  vpcId: string | null;
  resourceId: string;
  resourceName: string | null;
  region: string;
  availabilityZone: string | null;
  status: string | null;
  isGlobal: boolean;
  tags: Record<string, string> | null;
  lastSyncedAt: string | null;
  createdAt: string;
}

export interface AwsResource extends AwsResourceSummary {
  accountId: string | null;
  accountName: string | null;
  provider: string | null;
  tags: Record<string, string> | null;
  rawConfig: Record<string, unknown> | null;
  updatedAt: string;
}

export interface AwsServiceGroup {
  cloudServiceId: string;
  serviceName: string | null;
  category: string | null;
  iconKey: string | null;
  cloudIdentifier: string | null;
  resourceCount: number;
  resources: AwsResourceSummary[];
}

export interface AwsVpcGroup {
  vpcId: string | null;
  vpcName: string | null;
  region: string;
  totalResources: number;
  serviceGroups: AwsServiceGroup[];
}

export interface AwsAccountSummary {
  cloudProviderId: string;
  accountId: string;
  accountName: string;
  provider: string;
  totalResources: number;
  vpcs: AwsVpcGroup[];
  globalServices: AwsVpcGroup;
}

export function listAwsServices(): Promise<ApiEnvelope<AwsServiceCatalog[]>> {
  return apiFetch<ApiEnvelope<AwsServiceCatalog[]>>(`/cmdb/cloud/services`);
}

export function getAwsAccountSummary(
  cloudProviderId: string
): Promise<ApiEnvelope<AwsAccountSummary>> {
  return apiFetch<ApiEnvelope<AwsAccountSummary>>(
    `/cmdb/cloud/accounts/${cloudProviderId}/summary`
  );
}

export function listAwsResources(params: {
  cloudProviderId: string;
  vpcId?: string;
  serviceId?: string;
}): Promise<ApiEnvelope<AwsResourceSummary[]>> {
  const qs = new URLSearchParams({ cloudProviderId: params.cloudProviderId });
  if (params.vpcId) qs.set("vpcId", params.vpcId);
  if (params.serviceId) qs.set("serviceId", params.serviceId);
  return apiFetch<ApiEnvelope<AwsResourceSummary[]>>(`/cmdb/cloud/resources?${qs}`);
}

export function getAwsResource(resourceId: string): Promise<ApiEnvelope<AwsResource>> {
  return apiFetch<ApiEnvelope<AwsResource>>(`/cmdb/cloud/resources/${resourceId}`);
}

// ── Cloud Account Topology ────────────────────────────────────────────────────

export interface TopologyResource {
  id: string;
  resourceId: string;
  resourceName: string;
  serviceName: string;
  iconKey: string;
  vpcId: string | null;
  region: string;
  availabilityZone: string | null;
  status: string;
  isGlobal: boolean;
  lastSyncedAt: string;
}

export interface ServiceTopologyGroup {
  cloudServiceId: string;
  serviceName: string;
  category: string;
  iconKey: string;
  resourceCount: number;
  resources: TopologyResource[];
}

export interface VpcTopologyGroup {
  vpcId: string | null;
  vpcName: string;
  region: string;
  totalResources: number;
  serviceGroups: ServiceTopologyGroup[];
}

export interface CloudTopologyResponse {
  cloudProviderId: string;
  accountId: string;
  accountName: string;
  provider: string;
  region: string;
  totalResources: number;
  vpcs: VpcTopologyGroup[];
  globalServices: VpcTopologyGroup;
}

export function getCloudTopology(providerId: string): Promise<ApiEnvelope<CloudTopologyResponse>> {
  return apiFetch<ApiEnvelope<CloudTopologyResponse>>(`/cmdb/ci/cloud/${providerId}/topology`);
}

export interface AwsServiceTypeResponse {
  id: string;
  serviceName: string;
  category: string;
  cloudIdentifier: string;
  awsIdentifier?: string;
  iconKey: string;
}

/** Dynamic service-type dropdown — GET /cmdb/ci/cloud/{providerId}/resource-types */
export function listCloudProviderResourceTypes(
  providerId: string
): Promise<ApiEnvelope<AwsServiceTypeResponse[]>> {
  return apiFetch<ApiEnvelope<AwsServiceTypeResponse[]>>(
    `/cmdb/ci/cloud/${providerId}/resource-types`
  );
}

/** Per-account resource picker — GET /cmdb/ci/cloud/{providerId}/resources */
export function listCloudProviderResources(
  providerId: string,
  params?: { cloudIdentifier?: string; q?: string }
): Promise<ApiEnvelope<AwsResourceListResponse[]>> {
  const qs = new URLSearchParams();
  if (params?.cloudIdentifier) qs.set("cloudIdentifier", params.cloudIdentifier);
  if (params?.q) qs.set("q", params.q);
  const query = qs.toString() ? `?${qs}` : "";
  return apiFetch<ApiEnvelope<AwsResourceListResponse[]>>(`/cmdb/ci/cloud/${providerId}/resources${query}`);
}

export function deleteAwsResource(resourceId: string): Promise<void> {
  return apiFetch<void>(`/cmdb/cloud/resources/${resourceId}`, { method: "DELETE" });
}

// ── AWS App-Service Mappings ──

export type MappingStatus = "UNMAPPED" | "PENDING" | "CONFIRMED" | "IGNORED";
export type MappingType   = "AUTO" | "MANUAL";

export interface AwsServiceMappingOverviewItem {
  cloudServiceId: string;
  awsServiceId?: string;
  serviceName: string;
  category: string;
  iconKey: string | null;
  resourceCount: number;
  mappingId: string | null;
  componentId: string | null;
  componentTitle: string | null;
  mappingType: MappingType | null;
  status: MappingStatus;
}

export interface AwsAppServiceMappingResponse {
  id: string;
  applicationId: string;
  applicationName: string | null;
  cloudProviderId: string;
  accountName: string | null;
  accountId: string | null;
  cloudServiceId: string;
  awsServiceId?: string;
  serviceName: string | null;
  category: string | null;
  iconKey: string | null;
  componentId: string | null;
  componentTitle: string | null;
  mappingType: MappingType;
  status: MappingStatus;
  notes: string | null;
  createdAt: string;
  updatedAt: string;
}

export interface AwsAppServiceMappingScanResult {
  scanned: number;
  autoMatched: number;
  alreadyMapped: number;
  unmatched: number;
}

export interface AwsResourceMappingResponse {
  mappingId: string | null;
  resourceId: string;
  resourceName: string | null;
  resourceIdentifier: string | null;
  region: string | null;
  availabilityZone: string | null;
  status: string | null;
  isGlobal: boolean;
  componentId: string | null;
  componentTitle: string | null;
  isExcluded: boolean;
  source: "AUTO" | "MANUAL" | null;
  createdAt: string | null;
  updatedAt: string | null;
}

export interface AwsResourceMappingUpdateRequest {
  componentId: string | null;
  isExcluded: boolean | null;
  notes: string | null;
  source?: "AUTO" | "MANUAL" | null;
}

export interface AwsServiceHierarchyItem {
  cloudServiceId: string;
  awsServiceId?: string;
  serviceName: string;
  category: string;
  iconKey: string | null;
  isGlobal: boolean;
  mappingId: string | null;
  componentId: string | null;
  componentTitle: string | null;
  mappingType: MappingType | null;
  status: "CONFIRMED" | null;
  notes: string | null;
  resources: AwsResourceMappingResponse[];
}

export function getAwsMappingHierarchy(
  applicationId: string,
  cloudProviderId: string,
): Promise<ApiEnvelope<AwsServiceHierarchyItem[]>> {
  return apiFetch<ApiEnvelope<AwsServiceHierarchyItem[]>>(
    `/cmdb/cloud/app-mappings/hierarchy?applicationId=${applicationId}&cloudProviderId=${cloudProviderId}`,
  );
}

export function getAwsMappingOverview(
  applicationId: string,
  cloudProviderId: string,
): Promise<ApiEnvelope<AwsServiceMappingOverviewItem[]>> {
  return apiFetch<ApiEnvelope<AwsServiceMappingOverviewItem[]>>(
    `/cmdb/cloud/app-mappings/overview?applicationId=${applicationId}&cloudProviderId=${cloudProviderId}`,
  );
}

export function scanAwsMappings(
  applicationId: string,
  cloudProviderId: string,
): Promise<ApiEnvelope<AwsAppServiceMappingScanResult>> {
  return apiFetch<ApiEnvelope<AwsAppServiceMappingScanResult>>(`/cmdb/cloud/app-mappings/scan`, {
    method: "POST",
    body: JSON.stringify({ applicationId, cloudProviderId }),
  });
}

export interface BuildAndMapResult {
  scanned: number;
  autoMatched: number;
  alreadyMapped: number;
  unmatched: number;
}

export function buildAndMapComponents(
  applicationId: string,
  cloudProviderId: string,
): Promise<ApiEnvelope<BuildAndMapResult>> {
  return apiFetch<ApiEnvelope<BuildAndMapResult>>(`/cmdb/cloud/app-mappings/build-and-map`, {
    method: "POST",
    body: JSON.stringify({ applicationId, cloudProviderId }),
  });
}

export function createAwsMapping(payload: {
  applicationId: string;
  cloudProviderId: string;
  cloudServiceId: string;
  componentId: string | null;
  notes: string | null;
  source?: "AUTO" | "MANUAL" | null;
}): Promise<ApiEnvelope<AwsAppServiceMappingResponse>> {
  return apiFetch<ApiEnvelope<AwsAppServiceMappingResponse>>(`/cmdb/cloud/app-mappings`, {
    method: "POST",
    body: JSON.stringify(payload),
  });
}

export function updateAwsMapping(
  mappingId: string,
  payload: { componentId: string | null; notes: string | null; source?: "AUTO" | "MANUAL" | null },
): Promise<ApiEnvelope<AwsAppServiceMappingResponse>> {
  return apiFetch<ApiEnvelope<AwsAppServiceMappingResponse>>(`/cmdb/cloud/app-mappings/${mappingId}`, {
    method: "PUT",
    body: JSON.stringify(payload),
  });
}

export function confirmAwsMapping(mappingId: string): Promise<ApiEnvelope<AwsAppServiceMappingResponse>> {
  return apiFetch<ApiEnvelope<AwsAppServiceMappingResponse>>(`/cmdb/cloud/app-mappings/${mappingId}/confirm`, {
    method: "POST",
  });
}

export function ignoreAwsMapping(mappingId: string): Promise<ApiEnvelope<AwsAppServiceMappingResponse>> {
  return apiFetch<ApiEnvelope<AwsAppServiceMappingResponse>>(`/cmdb/cloud/app-mappings/${mappingId}/ignore`, {
    method: "POST",
  });
}

export function deleteAwsMapping(mappingId: string): Promise<void> {
  return apiFetch<void>(`/cmdb/cloud/app-mappings/${mappingId}`, { method: "DELETE" });
}

export function updateAwsResourceMapping(
  serviceMappingId: string,
  resourceMappingId: string,
  payload: AwsResourceMappingUpdateRequest,
): Promise<ApiEnvelope<AwsResourceMappingResponse>> {
  return apiFetch<ApiEnvelope<AwsResourceMappingResponse>>(
    `/cmdb/cloud/app-mappings/${serviceMappingId}/resources/${resourceMappingId}`,
    { method: "PUT", body: JSON.stringify(payload) },
  );
}

export interface AwsResourceMappingBulkUpdateRequest {
  componentId: string;
  selectedResourceIds: string[];
  unselectedResourceIds?: string[];
  replace?: boolean;
}

export function bulkUpdateAwsResourceMappings(
  serviceMappingId: string,
  payload: AwsResourceMappingBulkUpdateRequest,
): Promise<ApiEnvelope<AwsResourceMappingResponse[]>> {
  return apiFetch<ApiEnvelope<AwsResourceMappingResponse[]>>(
    `/cmdb/cloud/app-mappings/${serviceMappingId}/resources/bulk`,
    { method: "PUT", body: JSON.stringify(payload) },
  );
}

// ── Application Topology ──────────────────────────────────────────────────────

export interface AppTopologyDeployment {
  assetId: string;
  ciType: "ON_PREMISES" | "CLOUD";
  ciName?: string;
  ciIpAddress?: string;
  port?: number;
  role?: string;
  status?: string;
  cloudResourceName?: string;
  cloudIdentifier?: string;
}

export interface AppTopologyComponent {
  mappingId: string;
  componentId: string;
  componentTitle: string;
  componentType: string;
  deployments: AppTopologyDeployment[];
}

export interface AppTopologyServiceDep {
  dependencyId: string;
  serviceId: string;
  serviceName: string;
  serviceStatus: string;
  dependencyType: string;
  components: AppTopologyComponent[];
}

export interface AppTopologyDirectResource {
  mappingId: string;
  cloudResourceId: string;
  resourceId: string;
  resourceName: string;
  cloudIdentifier: string;
  serviceName: string;
  iconKey: string;
  category: string;
  region: string;
  status: string;
  provider: string;
  accountName: string;
  notes: string | null;
}

export interface AppTopologyCloudAccount {
  cloudProviderId: string;
  accountId: string;
  accountName: string;
  provider: string;
  region: string;
  resourceCount: number;
  directResources: AppTopologyDirectResource[];
}

export interface AppTopologyResponse {
  applicationId: string;
  name: string;
  description?: string;
  appStatus: string;
  criticality: string;
  components: AppTopologyComponent[];
  serviceDependencies: AppTopologyServiceDep[];
  cloudAccounts: AppTopologyCloudAccount[];
  totalComponents: number;
  totalDeployments: number;
  totalCloudAccounts: number;
  totalDirectResources?: number;
}

export function getApplicationTopology(appId: string): Promise<ApiEnvelope<AppTopologyResponse>> {
  return apiFetch<ApiEnvelope<AppTopologyResponse>>(`/cmdb/applications/${appId}/topology`);
}

/** App-scoped resource type filter dropdown — only lists types from linked accounts */
export function listAppResourceTypes(appId: string): Promise<ApiEnvelope<AwsServiceTypeResponse[]>> {
  return apiFetch<ApiEnvelope<AwsServiceTypeResponse[]>>(`/cmdb/applications/${appId}/resource-types`);
}

/** App-scoped resource picker — aggregates resources from all linked cloud accounts */
export function listAppResourcePicker(
  appId: string,
  params?: { cloudIdentifier?: string; q?: string },
): Promise<ApiEnvelope<AwsResourceListResponse[]>> {
  const qs = new URLSearchParams();
  if (params?.cloudIdentifier) qs.set("cloudIdentifier", params.cloudIdentifier);
  if (params?.q) qs.set("q", params.q);
  const query = qs.toString() ? `?${qs}` : "";
  return apiFetch<ApiEnvelope<AwsResourceListResponse[]>>(`/cmdb/applications/${appId}/resource-picker${query}`);
}

/* ─────────────────────────────────────────────────────────
   Drift Types & API
───────────────────────────────────────────────────────── */

/**
 * FE-facing OPA detail. `evidence` is kept as a string so the existing Drift UI
 * (which JSON-parses / pretty-prints it) renders unchanged. The wire shape now
 * delivers evidence as an array of {command, data}; `postDrift` normalizes it.
 */
export interface DriftOpaDetail {
  opaControlId: string;
  status: string;
  assetUuid: string;
  assetName: string;
  componentName?: string;
  evidence?: string;
  controlTitle?: string;
  controlStatement?: string;
}

export interface DriftHistoryPoint {
  timestamp: string;
  status: 'passed' | 'failed' | 'unknown';
  opaDetails?: DriftOpaDetail[];
}

export interface DriftApiControl {
  controlId: string;
  controlTitle?: string;
  controlStatement?: string;
  currentStatus: 'passed' | 'failed' | 'unknown';
  driftCount: number;
  longestFailedStreak: number;
  worstStreak: number;
  /** Per-control scan rollup counts. Invariant:
   *  passedScans + failedScans + unknownScans === totalScans === history.length */
  totalScans: number;
  passedScans: number;
  failedScans: number;
  unknownScans: number;
  history: DriftHistoryPoint[];
}

export interface DriftSummary {
  totalControls: number;
  passedControls: number;
  failedControls: number;
  totalDrifts: number;
}

export interface DriftResponse {
  planId: string;
  summary: DriftSummary;
  controls: DriftApiControl[];
}

/* ── Wire shape (raw POST /api/v1/drift response, pre-normalization) ── */

interface DriftWireEvidence {
  command?: string;
  data?: string;
}

interface DriftWireOpaDetail {
  opaControlId: string;
  status: string;
  assetUuid: string;
  assetName: string;
  componentName?: string | null;
  component_name?: string | null;
  componentTitle?: string | null;
  component_title?: string | null;
  // Backend now returns an array of {command, data}; older payloads may still
  // send a plain string. Tolerate both.
  evidence?: DriftWireEvidence[] | string | null;
}

interface DriftWireHistoryPoint {
  timestamp: string;
  status: 'passed' | 'failed' | 'unknown';
  opaDetails?: DriftWireOpaDetail[];
}

interface DriftWireControl {
  controlId: string;
  controlTitle?: string;
  controlStatement?: string;
  currentStatus: 'passed' | 'failed' | 'unknown';
  driftCount: number;
  longestFailedStreak: number;
  worstStreak: number;
  totalScans?: number;
  passedScans?: number;
  failedScans?: number;
  unknownScans?: number;
  history?: DriftWireHistoryPoint[];
}

interface DriftWireResponse {
  planId: string;
  summary: DriftSummary;
  controls?: DriftWireControl[];
}

/** Collapse the wire `evidence` (array of {command,data} | string | null) into the
 *  string the existing Drift UI renders. Empty/absent → undefined so the
 *  "Evidence" affordance hides. */
function normalizeDriftEvidence(
  ev: DriftWireEvidence[] | string | null | undefined,
): string | undefined {
  if (ev == null) return undefined;
  if (typeof ev === 'string') return ev.trim() ? ev : undefined;
  if (Array.isArray(ev)) return ev.length ? JSON.stringify(ev, null, 2) : undefined;
  return undefined;
}

function inferDriftComponentName(controlId?: string): string | undefined {
  const prefix = controlId?.split("-")[0]?.toUpperCase();
  if (prefix === "LIN") return "Linux";
  if (prefix === "OCI") return "OCI";
  return prefix || undefined;
}

/** Scan rollup counts: use the backend-shipped fields when present, else derive
 *  from history[] (older payloads that predate the counts). */
function deriveScanCounts(c: DriftWireControl): {
  totalScans: number;
  passedScans: number;
  failedScans: number;
  unknownScans: number;
} {
  if (typeof c.totalScans === 'number') {
    return {
      totalScans: c.totalScans,
      passedScans: c.passedScans ?? 0,
      failedScans: c.failedScans ?? 0,
      unknownScans: c.unknownScans ?? 0,
    };
  }
  const history = c.history ?? [];
  let passedScans = 0;
  let failedScans = 0;
  let unknownScans = 0;
  for (const h of history) {
    if (h.status === 'passed') passedScans++;
    else if (h.status === 'failed') failedScans++;
    else unknownScans++;
  }
  return { totalScans: history.length, passedScans, failedScans, unknownScans };
}

function normalizeDriftResponse(wire: DriftWireResponse): DriftResponse {
  return {
    planId: wire.planId,
    summary: wire.summary,
    controls: (wire.controls ?? []).map((c) => ({
      controlId: c.controlId,
      controlTitle: c.controlTitle,
      controlStatement: c.controlStatement,
      currentStatus: c.currentStatus,
      driftCount: c.driftCount,
      longestFailedStreak: c.longestFailedStreak,
      worstStreak: c.worstStreak,
      ...deriveScanCounts(c),
      history: (c.history ?? []).map((h) => ({
        timestamp: h.timestamp,
        status: h.status,
        opaDetails: (h.opaDetails ?? []).map((od) => ({
          opaControlId: od.opaControlId,
          status: od.status,
          assetUuid: od.assetUuid,
          assetName: od.assetName,
          componentName:
            od.componentName ??
            od.component_name ??
            od.componentTitle ??
            od.component_title ??
            inferDriftComponentName(od.opaControlId),
          evidence: normalizeDriftEvidence(od.evidence),
        })),
      })),
    })),
  };
}

/**
 * POST /api/v1/drift
 *
 * The backend reads the time window from the plan's own saved schedule;
 * `startDate`/`endDate` in the body are accepted but ignored (the DTO still
 * requires them for backward-compatibility). The raw response delivers OPA
 * evidence as an array of {command, data}; we normalize it to a string here so
 * the Drift UI renders without change.
 */
export async function postDrift(payload: {
  planId: string;
  startDate: string;
  endDate: string;
}): Promise<ApiEnvelope<DriftResponse>> {
  const res = await apiFetch<ApiEnvelope<DriftWireResponse> | DriftWireResponse>(
    '/drift',
    {
      method: 'POST',
      body: JSON.stringify(payload),
    },
  );
  // Endpoint may return the payload bare or wrapped in an ApiEnvelope.
  const wire = (res as ApiEnvelope<DriftWireResponse>)?.data ?? (res as DriftWireResponse);
  const data = normalizeDriftResponse(wire);
  return { ...(res as ApiEnvelope<DriftWireResponse>), data } as ApiEnvelope<DriftResponse>;
}

/** GET /api/v1/assessment-plans/by-ssp/<sspId> — list plans linked to an SSP */
export async function fetchAssessmentPlansBySsp(
  sspId: string,
): Promise<ApiAssessmentPlan[]> {
  const res = await apiFetch<{ status: string; data: ApiAssessmentPlan[] }>(
    `/assessment-plans/by-ssp/${sspId}`,
  );
  return res?.data ?? [];
}

/** POST /api/v1/drift/plan — drift data scoped to a specific plan */
export async function postDriftByPlan(payload: {
  planId: string;
  startDate: string;
  endDate: string;
}): Promise<ApiEnvelope<DriftResponse>> {
  const res = await apiFetch<ApiEnvelope<DriftWireResponse> | DriftWireResponse>(
    '/drift/plan',
    {
      method: 'POST',
      body: JSON.stringify(payload),
    },
  );
  const wire = (res as ApiEnvelope<DriftWireResponse>)?.data ?? (res as DriftWireResponse);
  const data = normalizeDriftResponse(wire);
  return { ...(res as ApiEnvelope<DriftWireResponse>), data } as ApiEnvelope<DriftResponse>;
}

export async function postDriftBySsp(
  sspId: string,
  dateParams?: { startDate?: string; endDate?: string },
): Promise<ApiEnvelope<DriftResponse>> {
  const body = dateParams && (dateParams.startDate || dateParams.endDate)
    ? JSON.stringify(dateParams)
    : undefined;
  const res = await apiFetch<ApiEnvelope<DriftWireResponse> | DriftWireResponse>(
    `/drift/ssp/${sspId}`,
    { method: 'POST', ...(body ? { body } : {}) },
  );
  const wire = (res as ApiEnvelope<DriftWireResponse>)?.data ?? (res as DriftWireResponse);
  const data = normalizeDriftResponse(wire);
  return { ...(res as ApiEnvelope<DriftWireResponse>), data } as ApiEnvelope<DriftResponse>;
}

export interface DriftEvidenceQuery {
  /** cmdb_assets.id — the asset (OpaDetail.assetUuid) whose evidence to load. */
  assetId: string;
  /**
   * The scan timestamp (one history point) the user drilled into. Send EXACTLY
   * the drift response's history[].timestamp — no reformatting, no microsecond
   * truncation, no timezone conversion (backend does a byte-exact match).
   */
  timestamp: string;
  /**
   * Tech-control ids (opaDetails[].opaControlId, e.g. "LIN-001") for this asset
   * at this timestamp — NOT the framework id (ac-1). Non-empty.
   */
  controlIds: string[];
}

/* Wire shapes for POST /api/v1/drift/evidence */
interface DriftEvidenceWireRow {
  controlId: string;
  status?: string;
  componentName?: string | null;
  component_name?: string | null;
  componentTitle?: string | null;
  component_title?: string | null;
  evidence?: { command?: string; data?: string } | null;
  controlTitle?: string;
  controlStatement?: string;
}
interface DriftEvidenceWireResponse {
  assetId: string;
  timestamp: string;
  evidences?: DriftEvidenceWireRow[];
}

/** Historical OPA output never changes — cache forever by asset+timestamp+ids. */
const driftEvidenceCache = new Map<string, DriftOpaDetail[]>();

/**
 * POST /api/v1/drift/evidence
 *
 * Lazily fetches the per-(asset, tech-control) evidence for ONE asset at a single
 * scan timestamp. Batches every tech-control the caller wants for that asset into
 * a single request. Returns the rows mapped to the UI's DriftOpaDetail shape
 * (evidence normalized to a string). Results are cached in-memory.
 *
 * `assetName` is for display only (the response doesn't echo it); pass the
 * asset's hostname from the drift response's opaDetails.
 */
export async function getDriftEvidence(
  q: DriftEvidenceQuery,
  assetName?: string,
): Promise<DriftOpaDetail[]> {
  const cacheKey = `${q.assetId}|${q.timestamp}|${[...q.controlIds]
    .sort()
    .join(',')}`;
  const cached = driftEvidenceCache.get(cacheKey);
  if (cached) return cached;

  const res = await apiFetch<
    ApiEnvelope<DriftEvidenceWireResponse> | DriftEvidenceWireResponse
  >('/drift/evidence', {
    method: 'POST',
    body: JSON.stringify({
      assetId: q.assetId,
      timestamp: q.timestamp,
      controlIds: q.controlIds,
    }),
  });

  const body =
    (res as ApiEnvelope<DriftEvidenceWireResponse>)?.data ??
    (res as DriftEvidenceWireResponse);
  const rows = body?.evidences ?? [];

  const mapped: DriftOpaDetail[] = rows.map((r) => ({
    opaControlId: r.controlId,
    status: r.status ?? '',
    assetUuid: body?.assetId ?? q.assetId,
    assetName: assetName || body?.assetId || q.assetId,
    componentName:
      r.componentName ??
      r.component_name ??
      r.componentTitle ??
      r.component_title ??
      inferDriftComponentName(r.controlId) ??
      inferDriftComponentName(q.controlIds[0]),
    // Wrap the single {command,data} into the array-string the UI parses; null
    // evidence → undefined ("no evidence captured").
    evidence: r.evidence
      ? JSON.stringify([
          { command: r.evidence.command ?? '', data: r.evidence.data ?? '' },
        ])
      : undefined,
    controlTitle: r.controlTitle,
    controlStatement: r.controlStatement,
  }));

  driftEvidenceCache.set(cacheKey, mapped);
  return mapped;
}

/* ─────────────────────────────────────────────────────────
   Permissions API
───────────────────────────────────────────────────────── */

export interface Permission {
  code: string;
  module: string;
  action: string;
  displayName: string;
}

/** GET /api/v1/users/current/permissions */
export function fetchUserPermissions(): Promise<ApiEnvelope<Permission[]>> {
  return apiFetch<ApiEnvelope<Permission[]>>('/users/current/permissions', {
    method: 'GET',
  });
}

/* ─────────────────────────────────────────────────────────
   Document Compliance Validator
───────────────────────────────────────────────────────── */

export type RuleSeverity = "ERROR" | "WARN";

export interface ValidationRule {
  id: string;
  name: string;
  validator: string;
  target: string;
  params: Record<string, unknown>;
  severity: RuleSeverity;
  applicable_doc_types: string[];
  remediation: string;
  intent: string | null;
  expected_evidence: string | null;
  enabled: boolean;
}

export type ValidationStatus = "PASS" | "FAIL" | "WARN" | "SKIPPED";
export type SkipReason = "NO_INPUT" | "NOT_APPLICABLE" | "NO_DATA" | null;

export interface DocValidationResult {
  ruleId: string;
  name: string;
  validator: string;
  severity: RuleSeverity;
  status: ValidationStatus;
  evidence: string;
  remediation: string;
  skipReason: SkipReason;
}

export interface DocValidationResponse {
  filename: string;
  tenant_id: string;
  doc_type: string;
  results: DocValidationResult[];
  summary: {
    pass: number;
    fail: number;
    warn: number;
    skipped: number;
    total: number;
  };
  duration_ms: number;
}

export interface DocValidationMeta {
  brand: string;
  title: string;
  version: string;
  owner_role?: string;
  approver?: string;
  section_number?: string;
  classification?: string;
  doc_type: string;
}

/** GET /api/v1/content-registry/validation/rules */
export function getValidationRules(): Promise<ValidationRule[]> {
  return apiFetch<ValidationRule[]>('/content-registry/validation/rules');
}

/** POST /api/v1/content-registry/validation/validate (multipart) */
export function validateDocument(
  file: File,
  meta: DocValidationMeta,
): Promise<DocValidationResponse> {
  const formData = new FormData();
  formData.append('file', file);
  Object.entries(meta).forEach(([k, v]) => {
    if (v !== undefined && v !== '') formData.append(k, v);
  });
  return apiFetch<DocValidationResponse>('/content-registry/validation/validate', {
    method: 'POST',
    headers: {},
    body: formData,
  });
}

/* ── DocEngine v2: validate documents for a control ── */

export interface ValidateDocControlPayload {
  orgId: string;
  documentIds: string[];
  control: {
    controlId: string;
    controlTitle: string;
    controlStatement: string;
  };
}

export interface ValidatedDocChunk {
  chunkId: string;
  sectionTitle: string;
  pageRange: string;
}

export interface ValidatedSupportingDocument {
  documentId: string;
  documentName: string;
  validationStatus: string; // e.g. "PARTIALLY_SATISFIED", "SATISFIED", "NOT_SATISFIED"
  confidence: {
    score: number;
    level: string;
  };
  found: string[];
  chunks: ValidatedDocChunk[];
  schedulingPresent: boolean;
}

export interface ValidateDocumentsResult {
  controlId: string;
  controlTitle: string;
  controlStatement: string;
  supportingDocuments: ValidatedSupportingDocument[];
  recommendation: {
    priority: string;
    summary: string;
    actions: string[];
  };
}

/** POST /api/v1/content-registry/docengine/v2/validate/documents */
export function validateDocumentsForControl(
  payload: ValidateDocControlPayload,
): Promise<ApiEnvelope<ValidateDocumentsResult>> {
  return apiFetch('/content-registry/docengine/v2/validate/documents', {
    method: 'POST',
    body: JSON.stringify(payload),
  });
}

/* ═══════════════════════════════════════════════════════════════════
   Axios-based API Service (consolidated from apiService.ts)
   ═══════════════════════════════════════════════════════════════════ */

axios.interceptors.response.use(
  (response) => response,
  (error) => {
    const status = error.response?.status;
    const errorMessage = error.response?.data?.message || error.message;

    console.error(`[API Error] Status: ${status}, Message: ${errorMessage}`);

    if (status === 401) {
      // A 401 from the login request means invalid credentials, not an expired
      // session. Let the Login page surface the real API error instead of
      // showing the generic "Session Ended" toast.
      const requestUrl: string = error.config?.url || "";
      if (requestUrl.includes(API_URL.AUTH)) {
        return Promise.reject(error);
      }
      console.warn("[401] Unauthorized - logging out user");
      handleSessionExpiration();
      return Promise.reject(error);
    }

    if (status === 403) {
      console.warn("[403] Forbidden - access denied");
      // Backend envelope: { errors: [{ code, title, detail }] }. The .data.message
      // path used to be checked here, but the real permission text lives on
      // errors[0].detail — e.g. "No permission for action 'create' on resource
      // 'role mapping'". Surface that verbatim so the user knows WHAT was denied.
      const firstErr = extractApiErrors(error)[0];
      const title = firstErr?.title || "Access Denied";
      const detail =
        firstErr?.detail ||
        firstErr?.title ||
        error.response?.data?.message ||
        "You don't have permission to perform this action.";
      const message = /contact/i.test(detail)
        ? detail
        : `${detail}. Please contact your administrator.`.replace(/\.\./g, ".");
      useToastStore.getState().showError(title, message);
      window.dispatchEvent(new CustomEvent("access-denied-error", {
        detail: { message }
      }));
      return Promise.reject(error);
    }

    if (status === 502) {
      console.error("[502] Bad Gateway - backend is unavailable");
      sessionStorage.setItem("sessionExpired", "true");
      sessionStorage.setItem("logoutReason", "Server error. Please try again later.");
      setTimeout(() => {
        window.location.href = "/login";
      }, 100);
      return Promise.reject(error);
    }

    if (
      (status && status >= 500) ||
      error.code === "ERR_CONNECTION_REFUSED" ||
      error.message?.includes("Network Error")
    ) {
      console.error("[Server Error] Server appears to be down");
      useToastStore.getState().showCritical(
        "Server Error",
        "The server is currently unavailable. Please try again later."
      );
      window.dispatchEvent(new CustomEvent("server-down-error", {
        detail: { status, message: errorMessage }
      }));
      return Promise.reject(error);
    }

    if (error.code === "ECONNABORTED") {
      console.error("[Timeout] Request took too long");
      useToastStore.getState().showError(
        "Request Timeout",
        "The request took too long. Please try again."
      );
      return Promise.reject(new Error("Request timeout - please try again"));
    }

    return Promise.reject(error);
  }
);

axios.interceptors.request.use(
  (config) => {
    if (!navigator.onLine) {
      console.warn("[Request] Device is offline");
      return Promise.reject({ isOffline: true });
    }

    const token = useAuthStore.getState().getToken();
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    } else {
      console.warn("[Request] No auth token available");
    }

    // Default 30s, but honour a per-call timeout when the caller set one
    // (e.g. long-running report/download endpoints override this to 60s).
    config.timeout = config.timeout || 30000;

    return config;
  },
  (error) => {
    console.error("[Request] Error preparing request:", error.message);
    return Promise.reject(error);
  }
);

export const authentication = async (
  username: string,
  password: string,
  rememberMe = false
): Promise<any> => {
  const config = {
    headers: { "Content-Type": "application/json" },
  };

  try {
    const response = await axios.post(
      API_URL.AUTH,
      { username, password },
      config
    );
    const userData = response.data?.data;
    if (userData?.accessToken) {
      useAuthStore.getState().login(userData, rememberMe);
      await new Promise(resolve => setTimeout(resolve, 0));
    } else {
      console.error("Login response missing accessToken:", response.data);
      throw new Error("Login failed: No access token received");
    }
    return response.data;
  } catch (error: any) {
    // Re-throw the original error so the caller keeps the backend error
    // envelope ({ errors: [{ code, title, detail }] }) and can surface the
    // specific title + detail (handled in Login via getErrorTitleAndMessage).
    throw error;
  }
};

export const logoutUser = async (): Promise<void> => {
  useAuthStore.getState().logout();
};

export const getAPI = async (apiUrl: string, timeoutMs?: number) => {
  try {
    const response = await axios.get(apiUrl, timeoutMs ? { timeout: timeoutMs } : undefined);
    return response.data;
  } catch (error: any) {
    if (error?.isOffline) {
      throw new Error("OFFLINE");
    }
    throw error;
  }
};

export const downloadFile = async (apiUrl: string, timeoutMs?: number) => {
  try {
    return await axios.get(apiUrl, {
      responseType: "blob",
      ...(timeoutMs ? { timeout: timeoutMs } : {}),
    });
  } catch (error: any) {
    if (error?.isOffline) {
      throw new Error("OFFLINE");
    }
    throw error;
  }
};

export const postAPI = async (apiUrl: string, data: any) => {
  try {
    const response = await axios.post(apiUrl, data, {
      headers: { "Content-Type": "application/json" },
    });
    return response.data;
  } catch (error: any) {
    if (error?.isOffline) {
      throw new Error("OFFLINE");
    }
    throw error;
  }
};

export const postMultipartAPI = async (apiUrl: string, data: any) => {
  try {
    const response = await axios.post(apiUrl, data, {
      headers: { "Content-Type": "multipart/form-data" },
    });
    return response.data;
  } catch (error: any) {
    if (error?.isOffline) {
      throw new Error("OFFLINE");
    }
    throw error;
  }
};

export const putAPI = async (apiUrl: string, data: any) => {
  try {
    const response = await axios.put(apiUrl, data, {
      headers: { "Content-Type": "application/json" },
    });
    return response.data;
  } catch (error: any) {
    if (error?.isOffline) {
      throw new Error("OFFLINE");
    }
    throw error;
  }
};

export const patchAPI = async (apiUrl: string, data: any) => {
  try {
    const response = await axios.patch(apiUrl, data, {
      headers: { "Content-Type": "application/json" },
    });
    return response.data;
  } catch (error: any) {
    if (error?.isOffline) {
      throw new Error("OFFLINE");
    }
    throw error;
  }
};

export const deleteAPI = async (apiUrl: string) => {
  try {
    const response = await axios.delete(apiUrl);
    return response.data;
  } catch (error: any) {
    if (error?.isOffline) {
      throw new Error("OFFLINE");
    }
    throw error;
  }
};

export const fetchQuadrantRiskMap = async (parentId?: string) => {
  try {
    const url = parentId
      ? `${API_URL.QUADRANT_RISK_MAP}?parentId=${parentId}`
      : API_URL.QUADRANT_RISK_MAP;
    const response = await axios.get(url);
    return response.data?.data || response.data;
  } catch (error: any) {
    if (error?.isOffline) {
      throw new Error("OFFLINE");
    }
    throw error;
  }
};

export const fetchQuadrantEntityDetails = async (
  applicationId: string,
  options: { sourceType?: string; range?: string } = {},
) => {
  const sourceType = options.sourceType ?? "APPLICATION";
  const range = options.range ?? "week";
  try {
    const url = `${API_URL.QUADRANT_ENTITY_DETAILS}/${applicationId}/drill-down?source_type=${sourceType}&range=${range}`;
    const response = await axios.get(url);
    return response.data?.data || response.data;
  } catch (error: any) {
    if (error?.isOffline) {
      throw new Error("OFFLINE");
    }
    throw error;
  }
};

/* ═══════════════════════════════════════════════════════════════════
   SSP Inheritance API (consolidated from sspInheritanceApi.ts)
   ═══════════════════════════════════════════════════════════════════ */

export interface InheritedDevice {
  id: string;
  orgId?: string;
  name: string;
  type: string;
  status: string;
  ipAddress?: string;
  hostname?: string;
  osName?: string;
  osVersion?: string;
  cpuCores?: number;
  ramGb?: number;
  diskGb?: number;
  createdAt?: string;
  updatedAt?: string;
  [key: string]: unknown;
}

export interface InheritedComponent {
  componentId: string;
  title: string;
  componentType: string;
  devices: InheritedDevice[];
  cloudAccounts?: unknown[];
  awsResources?: unknown[];
  services?: InheritedService[];
}

export interface InheritedSspRef {
  sspId: string;
  title: string;
}

export interface InheritedService {
  serviceId: string;
  orgId?: string;
  name: string;
  serviceType: string;
  status: string;
  description?: string;
  components: InheritedComponent[];
  isUsed?: boolean;
  usedBySsps?: InheritedSspRef[];
}

export interface InheritedApplication {
  applicationId: string;
  applicationName: string;
  appCode: string;
  components: InheritedComponent[];
  services: InheritedService[];
}

export interface InheritanceEnvelope {
  status: string;
  apiVersion: string;
  requestId: string;
  timestamp: string;
  data: InheritedApplication[];
}

export type InheritanceMode = "application" | "service";

export type InheritanceOrigination = "inherited" | "implemented";

export interface InheritedComponentRow {
  id: string;
  title: string;
  componentType: string;
  status: string;
  description: string;
  provider: string;
  origination: InheritanceOrigination;
}

export interface InheritedInventoryRow {
  id: string;
  description: string;
  component: string;
  ip: string;
  status: string;
  provider: string;
  origination: InheritanceOrigination;
}

export interface InheritedControlRow {
  id: string;
  controlId: string;
  title: string;
  statement: string;
  component: string;
  provider: string;
  status: string;
  origination: InheritanceOrigination;
}

export interface SspInheritance {
  applications: InheritedApplication[];
  components: InheritedComponentRow[];
  inventory: InheritedInventoryRow[];
  controls: InheritedControlRow[];
}

async function fetchRawEnvelope<T>(path: string): Promise<T[]> {
  const token = useAuthStore.getState().getToken();
  const res = await fetch(`${BASE_URL}${path}`, {
    headers: {
      "Content-Type": "application/json",
      ...(token ? { Authorization: `Bearer ${token}` } : {}),
    },
  });
  if (!res.ok) {
    const body = await res.text().catch(() => "");
    const err = new Error(
      `Inheritance fetch failed: ${res.status} ${body || res.statusText}`,
    );
    (err as Error & { status?: number }).status = res.status;
    throw err;
  }
  const envelope = (await res.json()) as { data?: T[] };
  return envelope.data ?? [];
}

export function fetchApplicationsWithAssets(): Promise<InheritedApplication[]> {
  return fetchRawEnvelope<InheritedApplication>(
    "/ssps/applications/components-with-assets",
  );
}

export async function fetchServicesWithAssets(): Promise<InheritedApplication[]> {
  const services = await fetchRawEnvelope<InheritedService>(
    "/ssps/services/components-with-assets",
  );
  return [
    {
      applicationId: "services-root",
      applicationName: "Services",
      appCode: "SERVICES",
      components: [],
      services,
    },
  ];
}

export function fetchInheritanceByMode(
  mode: InheritanceMode,
): Promise<InheritedApplication[]> {
  return mode === "application"
    ? fetchApplicationsWithAssets()
    : fetchServicesWithAssets();
}

// ── Architecture Compliance v2 APIs ───────────────────────────────────────────

export interface TopologyAsset {
  assetId: string;
  ciType: string;
  status: string | null;
  role: string | null;
  arn: string | null;
  resourceId: string;
  resourceName: string;
  region: string;
}

export interface TopologyComponent {
  componentType: string;
  componentName: string;
  assets: TopologyAsset[];
}

export interface TopologyCloudAccount {
  cloudProviderId: string;
  provider: string;
  accountId: string;
  accountName: string;
  role: string;
  status: string;
  regions: string[];
  components: TopologyComponent[];
}

export interface TopologyData {
  applicationId: string;
  applicationName: string;
  cloudAccounts: TopologyCloudAccount[];
}

export interface ArchApplicationListItem {
  applicationId: string;
  applicationName: string;
  description: string | null;
  cloudAccountCount: number;
  providers: string[];
}

// Entry-point list — applications that have at least one cloud account linked.
export function fetchArchApplications(): Promise<{ status: string; data: ArchApplicationListItem[] }> {
  return apiFetch(`/dashboard/architecture-compliance/applications`);
}

export function fetchTopologyByApplication(
  applicationId: string,
): Promise<{ status: string; data: TopologyData }> {
  return apiFetch(`/dashboard/architecture-compliance/topology?application_id=${applicationId}`);
}

// NOTE: response shape not yet modeled — typed as unknown so callers narrow.
export function fetchControlsByAccount(
  accountId: string,
): Promise<{ status: string; data: unknown }> {
  return apiFetch(`/dashboard/architecture-compliance/assets/${accountId}/controls`);
}

export function flattenInheritance(
  apps: InheritedApplication[],
): SspInheritance {
  const components: InheritedComponentRow[] = [];
  const inventory: InheritedInventoryRow[] = [];

  const seenComponentIds = new Set<string>();

  const walkComponent = (
    comp: InheritedComponent,
    provider: string,
  ): void => {
    if (!seenComponentIds.has(comp.componentId)) {
      seenComponentIds.add(comp.componentId);
      components.push({
        id: comp.componentId,
        title: comp.title,
        componentType: comp.componentType?.toLowerCase() || "software",
        status: "operational",
        description: deriveComponentDescription(comp, provider),
        provider,
        origination: "inherited",
      });
    }

    for (const dev of comp.devices ?? []) {
      inventory.push({
        id: dev.id,
        description: dev.name,
        component: comp.title,
        ip: dev.ipAddress || "—",
        status: _titleCase(dev.status || "Active"),
        provider,
        origination: "inherited",
      });
    }

    for (const svc of comp.services ?? []) {
      walkService(svc, provider);
    }
  };

  const walkService = (
    svc: InheritedService,
    parentProvider: string,
  ): void => {
    const providerLabel = svc.name ? `${svc.name} SSP` : parentProvider;
    for (const comp of svc.components ?? []) {
      walkComponent(comp, providerLabel);
    }
  };

  for (const app of apps) {
    const appProvider = `${app.applicationName} SSP`;
    for (const comp of app.components ?? []) {
      walkComponent(comp, appProvider);
    }
    for (const svc of app.services ?? []) {
      walkService(svc, appProvider);
    }
  }

  return { applications: apps, components, inventory, controls: [] };
}

function deriveComponentDescription(
  comp: InheritedComponent,
  provider: string,
): string {
  const deviceCount = (comp.devices ?? []).length;
  if (deviceCount > 0) {
    return `Inherited from ${provider}. Backed by ${deviceCount} device${
      deviceCount === 1 ? "" : "s"
    }.`;
  }
  return `Inherited from ${provider}.`;
}

function _titleCase(s: string): string {
  if (!s) return s;
  return s.charAt(0).toUpperCase() + s.slice(1).toLowerCase();
}

/* ── Audit Log API ── */

export interface AuditLogItem {
  id: string;
  userId: string | null;
  displayName: string | null;
  module: string;
  actionType: string;
  changeSourceDetail: string;
  createdAt: string;
}

export interface AuditLogResponse {
  success: boolean;
  data?: {
    content: AuditLogItem[];
    totalElements: number;
    totalPages: number;
    number: number;
    size: number;
    numberOfElements: number;
    first: boolean;
    last: boolean;
    empty: boolean;
    pageable: {
      offset: number;
      pageNumber: number;
      pageSize: number;
    };
  };
  error?: {
    code: string;
    message: string;
    details: string | null;
  };
}

/* ─────────────────────────────────────────────────────────
   AI Companion (Compliance Assistant chatbot)
───────────────────────────────────────────────────────── */

export interface AssistantConversation {
  id: string;
  title: string;
  status: string;
  createdAt: string;
}

export interface AssistantMessageDTO {
  id: string;
  role: "USER" | "ASSISTANT";
  content: string;
  sequenceNumber: number;
  createdAt: string;
}

export interface AssistantUsage {
  inputTokens: number;
  outputTokens: number;
  cacheReadTokens: number;
}

export interface AssistantIterationEvent {
  iteration: number;
}

export interface AssistantToolEvent {
  toolName: string;
  input: unknown;
  isError: boolean;
  durationMs: number;
  resultSizeBytes: number;
}

export interface AssistantTerminalEvent {
  conversationId: string;
  message: AssistantMessageDTO;
  stopReason: "END_TURN" | "MAX_TOKENS" | "STOP_SEQUENCE" | "UNKNOWN" | string;
  usage?: AssistantUsage;
  /** Follow-up prompts the backend suggests rendering as clickable chips. */
  suggestedQuestions?: string[];
  /** Tools the model invoked during this turn. Already streamed individually
   *  via tool_dispatched, but echoed here for completeness. */
  toolCalls?: AssistantToolEvent[];
}

export interface AssistantErrorEvent {
  code: string;
  message: string;
}

export interface AssistantDoneEvent {
  conversationId: string;
}

export interface AssistantStreamHandlers {
  onIterationStart?: (e: AssistantIterationEvent) => void;
  onToolDispatched?: (e: AssistantToolEvent) => void;
  onAssistant?: (e: AssistantTerminalEvent) => void;
  onError?: (e: AssistantErrorEvent) => void;
  onDone?: (e: AssistantDoneEvent) => void;
}

/** Create a new assistant conversation. Returns the conversation id + metadata. */
export const createAssistantConversation = async (
  title: string,
): Promise<AssistantConversation> => {
  try {
    const response = await axios.post<ApiEnvelope<AssistantConversation>>(
      API_URL.AI_COMPANION_CONVERSATIONS,
      { title },
      { headers: { "Content-Type": "application/json" } },
    );
    return response.data.data;
  } catch (error: any) {
    if (error?.isOffline) {
      throw new Error("OFFLINE");
    }
    throw error;
  }
};

/** Dispatch one parsed SSE event block to the correct handler. */
function dispatchAssistantEvent(block: string, h: AssistantStreamHandlers) {
  let eventName = "message";
  const dataLines: string[] = [];
  for (const line of block.split("\n")) {
    if (!line || line.startsWith(":")) continue;
    if (line.startsWith("event:")) {
      eventName = line.slice(6).trim();
    } else if (line.startsWith("data:")) {
      // Strip a single leading space if present (per SSE spec).
      const v = line.slice(5);
      dataLines.push(v.startsWith(" ") ? v.slice(1) : v);
    }
  }
  if (!dataLines.length) return;
  let payload: unknown;
  try {
    payload = JSON.parse(dataLines.join("\n"));
  } catch {
    return;
  }
  switch (eventName) {
    case "iteration_start":
      h.onIterationStart?.(payload as AssistantIterationEvent);
      break;
    case "tool_dispatched":
      h.onToolDispatched?.(payload as AssistantToolEvent);
      break;
    case "assistant":
      h.onAssistant?.(payload as AssistantTerminalEvent);
      break;
    case "error":
      h.onError?.(payload as AssistantErrorEvent);
      break;
    case "done":
      h.onDone?.(payload as AssistantDoneEvent);
      break;
  }
}

/**
 * Stream a user message into an existing conversation over SSE.
 *
 * Resolves when the server closes the stream (after `done`). Rejects on
 * network / transport failure, including AbortError when `signal` is fired.
 * The terminal `assistant` or `error` event is delivered via handlers — this
 * function does not throw for AIC-* errors.
 */
export const streamAssistantMessage = async (
  conversationId: string,
  content: string,
  handlers: AssistantStreamHandlers,
  signal?: AbortSignal,
): Promise<void> => {
  const url = `${API_URL.AI_COMPANION_CONVERSATIONS}/${conversationId}/messages:stream`;
  const token = useAuthStore.getState().getToken();
  const res = await fetch(url, {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
      Accept: "text/event-stream",
      ...(token ? { Authorization: `Bearer ${token}` } : {}),
    },
    body: JSON.stringify({ content }),
    signal,
  });

  if (res.status === 401) {
    handleSessionExpiration();
    const err = new Error("Session expired. Please login again.");
    err.name = "Session Expired";
    throw err;
  }
  if (!res.ok || !res.body) {
    const body = await res.text().catch(() => "");
    throw new Error(briefHttpError(res.status, body, res.statusText));
  }

  const reader = res.body.getReader();
  const decoder = new TextDecoder("utf-8");
  let buffer = "";

  try {
    while (true) {
      const { value, done } = await reader.read();
      if (done) break;
      buffer += decoder.decode(value, { stream: true });
      // Normalize CRLF then split on event terminators.
      buffer = buffer.replace(/\r\n/g, "\n");
      let idx: number;
      while ((idx = buffer.indexOf("\n\n")) !== -1) {
        const block = buffer.slice(0, idx);
        buffer = buffer.slice(idx + 2);
        if (block.trim()) dispatchAssistantEvent(block, handlers);
      }
    }
    // Flush any trailing buffered event (server should terminate with \n\n,
    // but be defensive in case it doesn't).
    if (buffer.trim()) dispatchAssistantEvent(buffer, handlers);
  } finally {
    try {
      reader.releaseLock();
    } catch {
      /* ignore */
    }
  }
};
